import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  SelectionMode,
  useNodesState,
  useReactFlow,
  useStore,
  useStoreApi,
  ViewportPortal,
  type Node,
  type OnNodeDrag,
  type NodeChange,
  type NodeTypes,
  type XYPosition,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { useAreaDrawing } from './hooks/useAreaDrawing'
import { useCanvasZoomVars } from './hooks/useCanvasZoomVars'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { CanvasNote, UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import { NoteNode } from './nodes/NoteNode'
import type { NoteNodeData } from './nodes/NoteNode.types'
import { MemoNode } from './nodes/MemoNode'
import { AreaNode } from './nodes/AreaNode'
import { TextNode } from './nodes/TextNode'
import { useTextDrawing } from './hooks/useTextDrawing'
import {
  CANVAS_TEXT_LINE_HEIGHT,
  CANVAS_TEXT_PLACEHOLDER_ESTIMATE,
  DEFAULT_CANVAS_TEXT_SIZE,
  MIN_CANVAS_TEXT_SIZE,
  MIN_CANVAS_TEXT_SWAP_HEIGHT,
  canvasTextSizeFromSide,
  canvasTextSizeOf,
  estimateCanvasTextSize,
  getCanvasTextPosition,
  scaleCanvasTextSize,
} from './utils/canvasText'
import { isCanvasTextEmpty } from './utils/canvasTextContent'
import { clearedCanvasTextStyle, scaleCanvasTextSizes } from './utils/canvasTextFormat'
import { AdoptionMarks, type AdoptionMark } from './AdoptionMarks'
import { areaFor, fitAround, getAreaColor, MIN_AREA_SIZE } from './utils/canvasArea'
import { compareZ, planZOrder, type ZOrderDirection } from './utils/canvasZOrder'
import { useCanvasZOrderShortcut } from './hooks/useCanvasZOrderShortcut'
import { CanvasElementMenuProvider, withCanvasElementMenu, type CanvasElementMenuApi } from './withCanvasElementMenu'
import {
  DEFAULT_CANVAS_NOTE_COLOR,
  DEFAULT_CANVAS_NOTE_WIDTH,
  nextStickyWidth,
  DEFAULT_CANVAS_NOTE_HEIGHT,
  MIN_CANVAS_NOTE_SIZE,
  MAX_CANVAS_NOTE_SIZE,
  bulkColorFor,
  getCanvasNoteRingColor,
} from './utils/canvasNoteStyle'
import {
  DEFAULT_MEMO_MIN_WIDTH,
  DEFAULT_MEMO_MAX_WIDTH,
} from './utils/canvasMemoStyle'
import type { CanvasPlacementMode, CanvasTool } from './ProjectCanvas.types'
import type { CanvasRevealRequest } from './ProjectCanvasPanel.types'
import { useCanvasZoomShortcut } from './hooks/useCanvasZoomShortcut'
import { useCanvasCursorAnchor } from './hooks/useCanvasCursorAnchor'
import { useSpaceKeyHeld } from './hooks/useSpaceKeyHeld'
import { useOptionPreview } from './hooks/useOptionPreview'
import type { CanvasColorPreview, CanvasFontPreview } from './optionsBar/CanvasOptionsBar.types'
import { useCanvasViewport } from './hooks/useCanvasViewport'
import { useCanvasDuplicateShortcut } from './hooks/useCanvasDuplicateShortcut'
import { useCanvasEscape } from './hooks/useCanvasEscape'
import { CanvasTappedProvider } from './hooks/useCanvasTapped'
import { drawCanvasTape, readCanvasTape, retapeCanvasTape, writeCanvasTape, CANVAS_TAPE_PEEL_MS } from './tapes/canvasTapes'
import { useCanvasTabShortcut } from './hooks/useCanvasTabShortcut'
import { useCanvasDeleteShortcut } from './hooks/useCanvasDeleteShortcut'
import { useCanvasPlacementDrag } from './hooks/useCanvasPlacementDrag'
import { CanvasPlacementPreview } from './CanvasPlacementPreview'
import { CanvasSpawnPreview } from './CanvasSpawnPreview'
import { getInsertPlan, SPAWN_ANIM_MS, type InsertMove, type SpawnSide } from './utils/canvasSpawn'
import { getPlacementPosition, getPlacementSize } from './utils/canvasPlacement'
import { useGroupDrag } from './hooks/useGroupDrag'
import { SelectionOverlay } from './SelectionOverlay'
import type { SwapSeam } from './SelectionOverlay'
import { readCanvasNoteDefaults, writeCanvasNoteDefaults } from './utils/canvasNoteDefaults'

/** Beide Sets auf einmal — für das Vorschaubild, das je nach Art fragt. */
const readAllNoteDefaults = (projectId: string) => ({
  note: readCanvasNoteDefaults(projectId, 'note'),
  memo: readCanvasNoteDefaults(projectId, 'memo'),
  // Der **Bereich** merkt sich seit 2026-09-06 seine Farbe — je Projekt und
  // für sich, wie die beiden anderen. Eine Schrift hat er nicht.
  area: readCanvasNoteDefaults(projectId, 'area'),
  // Der **Text** merkt sich dazu seine Schriftgröße (CONTEXT.md, Text).
  text: readCanvasNoteDefaults(projectId, 'text'),
})
import { useSwapDrag } from './hooks/useSwapDrag'
import { useCanvasHistoryShortcut } from './hooks/useCanvasHistoryShortcut'
import {
  EMPTY_HISTORY,
  StepRecorder,
  forgetNote,
  pushStep,
  redo as redoHistory,
  undo as undoHistory,
} from './utils/canvasHistory'
import type { CanvasHistoryState } from './utils/canvasHistory'
import type { SwapPreview } from './hooks/useSwapDrag'
import type { SwapZone } from './utils/canvasSwapZones'
import { SWAP_LIFTED_Z } from './utils/canvasSwap'
import { getDuplicateOffset, DUPLICATE_DRAG_THRESHOLD } from './utils/canvasDuplicate'
import { buildTabOrder, stepTabOrder } from './utils/canvasTabOrder'
import type { LedAxes, MovingRect, ResizeMode } from './utils/canvasAlignment'
import {
  CANVAS_MIN_ZOOM,
  CANVAS_MAX_ZOOM,
  isZoomedIn,
  firstGlanceViewport,
} from './utils/canvasViewport'
import { HelperLines } from './HelperLines'
import { MeasureLines } from './MeasureLines'
import {
  getAlignment,
  getBoundingBox,
  getGroupAlignment,
  getFreeResizeAlignment,
  getResizeAlignment,
  getWidthResizeAlignment,
  movingEdgesFor,
  toBounds,
  isVisible,
  isFullyVisible,
  ALIGNMENT_THRESHOLD,
  type AlignmentLines,
  type Bounds,
  type Guide,
  type Measure,
  type MovingEdges,
  type ResizeCorner } from './utils/canvasAlignment'
import './CanvasFlow.scss'

// Jede Art trägt das Menü am Rechtsklick (siehe `withCanvasElementMenu`).
const NODE_TYPES: NodeTypes = {
  note: withCanvasElementMenu(NoteNode),
  memo: withCanvasElementMenu(MemoNode),
  area: withCanvasElementMenu(AreaNode),
  text: withCanvasElementMenu(TextNode),
}

/**
 * Rasterweite des Canvas. Dient doppelt: als Abstand der Hintergrundpunkte
 * und als Fangraster in `handleNodesChange` — beide MÜSSEN denselben Wert
 * nutzen, sonst rasten die Notizen sichtbar neben den Punkten ein.
 */
const GRID = 24
/**
 * Radius eines Rasterpunkts bei Zoom 1, in px.
 *
 * React Flows `<Background size={1}>` zeichnete 0,5px — als weich gerasterter
 * SVG-Kreis wirkte das etwa wie 1,5px. Der `radial-gradient` zieht eine harte
 * Kante und lag damit sichtbar kleiner und blasser; 0,75 plus die weiche
 * Kante in der SCSS holen das alte Bild zurück.
 */
const GRID_DOT_RADIUS = 0.75

/** Keine Hilfslinien. Als Konstante, damit „aus" nicht bei jedem Frame eine neue Identität bekommt. */
const NO_GUIDES: { vertical: Guide[]; horizontal: Guide[] } = {
  vertical: [],
  horizontal: [],
}

/** Keine Maßlinien. Aus demselben Grund eine Konstante wie `NO_GUIDES`. */
const NO_MEASURES: Measure[] = []

/** Die Grenzen, in denen die Größe einer Notiz bleiben muss. */
const SIZE_LIMITS = { min: MIN_CANVAS_NOTE_SIZE, max: MAX_CANVAS_NOTE_SIZE }

/**
 * Die Grenzen, in denen die BREITE einer Notiz bleiben muss — eigene Werte,
 * nicht die der Notiz (`SIZE_LIMITS`): Ein Memo ist von Haus aus breiter
 * angelegt (siehe `DEFAULT_MEMO_WIDTH`) und ihre Höhe zählt hier gar
 * nicht mit, sie zieht niemand.
 */
const MEMO_SIZE_LIMITS = { min: DEFAULT_MEMO_MIN_WIDTH, max: DEFAULT_MEMO_MAX_WIDTH }

/**
 * Die Grenzen der Box eines **Texts** an den Ecken: unten eine Zeile in der
 * kleinsten Schrift, oben so gut wie keine — die Schrift selbst wird beim
 * Loslassen auf 8–400 begrenzt (`scaleCanvasTextSize`).
 */
const TEXT_SIZE_LIMITS = { min: Math.floor(MIN_CANVAS_TEXT_SIZE * CANVAS_TEXT_LINE_HEIGHT), max: 100_000 }


/**
 * Ab wie vielen Pixeln aus einem Klick ein Ziehen wird.
 *
 * EIN Wert für zwei Props von React Flow, die dieselbe Frage beantworten —
 * siehe die Tabelle an `<ReactFlow>`. Stehen sie auseinander, verschwinden
 * Klicks in der Lücke dazwischen.
 *
 * Die 1 ist React Flows eigene Vorgabe für `nodeDragThreshold` und bleibt es:
 * Sie hält ein Zittern beim Klicken aus, ohne dass sich das Ziehen träge
 * anfühlt.
 */
const CLICK_DRAG_THRESHOLD = 1

/** Ob zwei Linien-Listen dasselbe zeigen. */
function sameGuides(a: Guide[], b: Guide[]): boolean {
  return a.length === b.length &&
    a.every((line, i) => line.position === b[i]!.position &&
      line.start === b[i]!.start && line.end === b[i]!.end)
}

/** Dasselbe für die Klammern. */
function sameMeasures(a: Measure[], b: Measure[]): boolean {
  return a.length === b.length &&
    a.every((m, i) => m.axis === b[i]!.axis && m.from === b[i]!.from &&
      m.to === b[i]!.to && m.at === b[i]!.at)
}

const isDev = process.env.NODE_ENV !== 'production'

/**
 * Stapelreihenfolge nach Alter: zuletzt angelegt liegt zuoberst. Ohne das
 * verschwindet eine frische Notiz unter einer älteren, die zufällig an
 * derselben Stelle liegt — die Abfrage liefert die Notizen in keiner
 * bestimmten Reihenfolge.
 */
/**
 * Die **Canvas-Elemente** hinter den ausgewählten Nodes.
 *
 * Mit Prüfung auf `data.note`: React Flow trägt auch Nodes ohne unsere Daten
 * (Tests stellen schlichte Rechtecke), und ein `undefined` mitten in der Liste
 * risse die ganze Fläche mit.
 */
function selectedNotesOf(nodes: readonly Node<NoteNodeData>[]): CanvasNote[] {
  return nodes.filter(n => n.selected && n.data?.note).map(n => n.data.note)
}

/**
 * Der Vorgabename eines frisch abgelegten **Bereichs**: schlicht „Bereich".
 *
 * **Kehrt die Entscheidung aus dem Grillen um** (2026-09-04: „kein
 * Vorgabename — solo ist er Text, den man löscht") — auf Ansage am
 * 2026-09-05: Ein Bereich soll benannt dastehen und nicht erst durch einen
 * gedämpften Platzhalter erklären, dass er einen Namen tragen könnte.
 *
 * Ohne Nummer, und das bleibt: Ein Zähler müsste über alle Bereiche des
 * Projekts laufen, überstünde kein Löschen in der Mitte, und zwei Bereiche
 * „Bereich 3" nach einem ⌘Z wären schlimmer als zwei gleiche Namen.
 *
 * Gespeichert wird der WORTLAUT, nicht der Schlüssel — wie bei jedem Namen,
 * den ein Mensch eintippt: Ein Wechsel der Sprache benennt vorhandene Bereiche
 * nicht um. Derselbe Text wie der Platzhalter des leeren Reiters: Wer den
 * Namen löscht, sieht dasselbe Wort, nur gedämpft.
 */
const AREA_TITLE_KEY = 'projectCanvas.area.namePlaceholder'

/**
 * Der Ton eines **Aufnahme-Umrisses**, der auf einem ZETTEL sitzt: seine
 * eigene Farbe, satt und dunkler — derselbe Ton, den auch sein **Tauschpunkt**
 * trägt (CONTEXT.md, Aufnahme-Umriss; dieselbe Regel wie beim **Umriss** des
 * **Platz tauschens**).
 *
 * **Eine Marke trägt die Farbe dessen, worauf sie sitzt** (gewünscht
 * 2026-09-08): auf dem Bereich seine, auf dem Zettel dessen. Vorher liehen
 * sich die Zettel-Marken den Ton des Bereichs — vier Zettel trugen dann
 * dieselbe fremde Farbe, und auf einem gelben Zettel las sich die Kontur eines
 * grünen Bereichs als Zustand des Zettels statt als Angebot.
 */
const toneOfNote = (color: string | null | undefined) => getCanvasNoteRingColor(color ?? null)

/** Der Bereich, der beim **Aufziehen** noch keiner ist — nur für die Rechnung. */
const DRAFT_AREA_ID = '__draft-area__'

/** Keine Gruppe in der Hand (siehe `groupLiftIds`). */
const NO_LIFT: ReadonlySet<string> = new Set()

/**
 * Die Klasse, mit der ein verdrängter Zettel GLEITET statt zu springen (siehe
 * `CanvasFlow.scss`) — nur so lange gesetzt, wie er sich bewegt.
 *
 * Dauerhaft an den Nodes wäre sie ein anderes Canvas: Eine stehende
 * Überblendung auf `transform` zöge auch beim Ziehen, beim Einrasten, beim
 * Platztausch und beim Rückgängig hinterher.
 *
 * Der NEUE Zettel bekommt nichts dergleichen: Sein Aufwachsen hat die Vorschau
 * an genau seiner Stelle schon vorgemacht (`CanvasSpawnPreview.scss`).
 */
const SHIFT_CLASS = 'canvas-note--shifting'

/**
 * Der **Umriss** um den Partner eines **Platz tauschens** (CONTEXT.md): die
 * Marke, die den Tausch vom **Dazwischen schieben** unterscheidet — dort ist
 * es die **Fuge**, ein Strich in der Lücke.
 *
 * Am Zettel selbst, nicht als Fläche im Overlay: Der Partner GLEITET auf den
 * Platz des Getragenen, und eine Marke daneben spränge, während er gleitet.
 */
const SWAP_PARTNER_CLASS = 'canvas-note--swap-partner'

/**
 * Der Getragene wird durchscheinend, **solange die Fuge steht** (CONTEXT.md,
 * Dazwischen schieben): Er hängt am Zeiger, der Zeiger steht auf dem **Rand**,
 * sein Papier deckt damit genau den Spalt zu, um den es geht. Durchscheinend
 * sieht man hindurch, worauf man zielt.
 *
 * **Nicht beim Tausch:** Dort gibt es nichts, was er verdeckte — der Partner
 * ist weggegangen, und was geschieht, sagt dessen **Umriss**.
 */
const SWAP_FADE_CLASS = 'canvas-note--slotting'

/**
 * Wie lange die **Fuge** zum Abblenden braucht — dieselbe Zahl wie in der
 * SCSS (`canvas-swap-mark`). Sie steht so lange noch da; danach räumt der
 * Wecker sie weg (siehe `showSeam`).
 */
const SEAM_FADE_MS = 140

/**
 * Der Zettel **wächst**, sobald der Zeiger auf seinem **Tauschpunkt** steht,
 * und bleibt gewachsen, solange er getragen wird (CONTEXT.md, Tauschpunkt).
 *
 * Nur die Papierfläche wächst, nicht der Node — die Begründung steht in
 * `NoteNode.scss` bei `--note-swell`.
 */
const RING_HOVER_CLASS = 'canvas-note--at-ring'

/**
 * **Andocken trägt kein Blau** (CONTEXT.md, Tauschpunkt): Solange die Geste
 * eine **Ableg-Fläche** trifft, nimmt der Getragene seinen Auswahl-Rahmen ab.
 * Beim **Verschieben** behält er ihn — dort ist es dieselbe Bewegung wie ein
 * Zug am Zettel selbst, und Blau heißt „ausgewählt".
 *
 * **Die Auswahl selbst bleibt**, nur ihr Strich geht: An ihr hängen ⌘Z, die
 * **Options-Leiste** und Escape. Zurückgenommen wird die Regel in
 * `NoteNode.scss` und `MemoNode.scss`.
 */
const DOCKING_CLASS = 'canvas-note--docking'

/**
 * **Wer ausweicht, zeigt es** (CONTEXT.md, Platz tauschen): der stille
 * **Umriss** an jedem Zettel, den das Loslassen bewegt — dieselbe Kontur wie
 * am Tausch-Partner, nur ohne das Atmen. Das gehört dem, auf den man ZEIGT;
 * wer nur Platz macht, steht still.
 *
 * Mit ⌘/Strg trägt niemand sie: Dann bleibt der Ursprung offen, und betroffen
 * ist allein der Getragene.
 */
const YIELDING_CLASS = 'canvas-note--yielding'

/** Alle Klassen, die der Ring gesetzt hat — siehe `applySwapPositions`. */
const RING_CLASSES = new Set([SHIFT_CLASS, SWAP_PARTNER_CLASS, SWAP_FADE_CLASS, RING_HOVER_CLASS, DOCKING_CLASS, YIELDING_CLASS])

/**
 * Ob eine Klassenbelegung ganz dem Ring gehört — dann darf sie neu geschrieben
 * werden. Ein Zettel mitten im **Federstoß** (`POP_CLASS`) behält damit seinen:
 * Er gehört einer anderen Bewegung.
 */
const ringOwns = (className: string | undefined) =>
  className !== undefined && className.split(' ').every(name => RING_CLASSES.has(name))

/** Ob eine Klassenbelegung diesen Namen führt. */
const carries = (className: string | undefined, name: string) =>
  className !== undefined && className.split(' ').includes(name)

/**
 * Der kurze Federstoß, mit dem der echte Zettel das Bild ablöst: erst ein
 * Stück über die Zielgröße hinaus, dann zurück.
 *
 * Er beginnt und endet bei voller Größe — deshalb gibt es an der Ablösung
 * keinen Sprung. (Ein Aufwachsen aus dem Kleinen hatte genau den: Das Bild
 * stand in voller Größe, der Zettel fing bei einem Drittel an.)
 */
const POP_CLASS = 'canvas-note--popping'

/**
 * Wie lange ein übernommenes Vorschau-Bild höchstens auf seinen Zettel wartet.
 *
 * Es hält den Platz zwischen Klick und Live-Query — normalerweise ein paar
 * Frames. Bliebe der Zettel aus (ein fehlgeschlagener Schreibvorgang), stünde
 * dort sonst für immer ein Platzhalter, der aussieht wie ein Zettel und keiner
 * ist. Großzügig bemessen: Er soll nie im gesunden Fall zuschlagen.
 */
const SPAWN_COMMIT_TIMEOUT = 2000

/**
 * **Solange ein Werkzeug scharf ist, nimmt ein Bereich keine Zeiger an**
 * (gemeldet 2026-09-05): Er ist eine Fläche, kein Hindernis — er liegt unter
 * allen Zetteln (CONTEXT.md), und das Absetzen hängt am Klick auf die PANE.
 * Sonst fing er den Klick ab, wurde ausgewählt, und der Zettel hing weiter am
 * Zeiger; ablegen ließ er sich nur auf freier Fläche.
 *
 * Als Stil am Node und nicht im Stylesheet: React Flow setzt `pointerEvents`
 * INLINE (`NodeWrapper`, @xyflow/react) — dagegen kommt keine Regel an, egal
 * wie spezifisch. Sein eigener Wert steht VOR `...node.style`, unserer
 * gewinnt also. Ein fester Wert, damit der Abgleich unten ihn vergleichen kann.
 *
 * Nur der Bereich: Ein Zettel bleibt ein Zettel, auf dem man nicht ungewollt
 * einen zweiten ablegt.
 */
const AREA_PLACING_STYLE = { pointerEvents: 'none' } as const

/**
 * Ein **geklebter** Zettel nimmt keine Pointer-Events an: Der Zeiger fällt
 * durch ihn hindurch auf die Fläche darunter. Auf ihm lässt sich also ein
 * **Auswahlrechteck** beginnen und die Ansicht schieben, als läge er nicht dort
 * — sonst hätte ein Board voller geklebter Zettel überall tote Flecken.
 *
 * Zwei Ausnahmen holen sich ihre Ereignisse in der SCSS zurück: der
 * **Klebestreifen** (er ist der Griff) und das **Eselsohr** (es führt zur
 * Aufgabe). Was ihn nicht ändert, bleibt erlaubt.
 *
 * **Über `style` und nicht über `className`:** Die Klassen des Nodes gehören
 * der Ring-Maschinerie (`RING_CLASSES`), die sie beim Tauschen setzt und
 * wieder auf `undefined` räumt — eine Klebe-Klasse geriete dort unter die
 * Räder. Ein Modul-Konstantenobjekt, damit der Node-Abgleich unten
 * (`existing.style === rebuilt.style`) es wiedererkennt.
 */
const TAPED_STYLE = { pointerEvents: 'none' } as const

function noteToFlowNode(
  note: CanvasNote,
  autoEditNoteId: string | null,
  editingEnabled: boolean,
  zIndex: number,
  handlers: Pick<NoteNodeData, 'onResizeStart' | 'onEdit' | 'onDelete' | 'onOpenTask' | 'onSpawnHover' | 'onSpawn' | 'onApply' | 'onEditingChange' | 'onFitToContent' | 'onFrameGrab' | 'onEmptied'>,
  editRequest: EditRequest | null,
  placing: boolean,
): Node<NoteNodeData> {
  /*
    Der **Klebestreifen** nimmt dem Node zwei Fähigkeiten, und beide fallen hier
    weg statt an zwanzig einzelnen Stellen:

    `draggable: false` — er rührt sich nicht.

    `selectable: false` — er gerät in KEINE Auswahl. Damit fallen der
    **Auswahlrahmen**, die Ziehpunkte und das **Auswahlrechteck** von selbst
    weg, und keine einzige Sammelaktion (verschieben, löschen, färben,
    duplizieren) muss ihn eigens ausnehmen: Er steht nie in der Liste. Was er
    an Bedienung behält, hängt am **Angetippt**-Zustand (`tappedId`).

    `undefined` und nicht `true` für den ungeklebten Fall: Ohne eigene Angabe
    entscheidet React Flows `nodesDraggable`/`elementsSelectable` am
    `<ReactFlow>`, und das hängt am **Werkzeug**. Ein hart gesetztes `true`
    machte Zettel im Hand-Werkzeug wieder ziehbar.
  */
  const taped = Boolean(note.tape)
  return {
    id: note.id,
    draggable: taped ? false : undefined,
    selectable: taped ? false : undefined,
    // Siehe `AREA_PLACING_STYLE` und `TAPED_STYLE`.
    style: taped ? TAPED_STYLE : placing && note.kind === 'area' ? AREA_PLACING_STYLE : undefined,
    // Die Art bestimmt den Node-Typ. Beide Datenformen tragen dieselben
    // Rückruf-Namen (siehe `MemoNode.types.ts`), deshalb genügt EIN Satz
    // Handler für beide.
    type: note.kind === 'area' ? 'area' : note.kind === 'memo' ? 'memo' : note.kind === 'text' ? 'text' : 'note',
    position: { x: note.x, y: note.y },
    // Maße mitgeben, statt React Flow messen zu lassen: einen noch
    // ungemessenen Node rendert es mit `visibility: hidden` (index.js:2353),
    // und ein unsichtbares Element nimmt keinen Fokus an — die frisch
    // abgesetzte Notiz bliebe also stumm, obwohl ihr Bearbeiten-Modus an ist.
    // Die Werte stehen ohnehin in der Datenbank — der Fallback greift nur bei
    // einer Zeile ohne gespeicherte Maße. `getPlacementSize(note.kind)`, nicht
    // `DEFAULT_CANVAS_NOTE_WIDTH`/`_HEIGHT` fest verdrahtet: Dieselbe Frage
    // („wie groß ist ein Element dieser Art ohne eigene Maße?") stellte sich
    // in diesem Zweig schon dreimal mit drei verschiedenen Antworten (hier,
    // in `MemoNode`, in `getPlacementSize` selbst) — `kind` entschied
    // nirgends mit, obwohl ein Memo breiter ist als eine Notiz.
    width: note.width || getPlacementSize(note.kind).width,
    height: note.height || getPlacementSize(note.kind).height,
    zIndex,
    data: {
      note,
      startEditing: note.id === autoEditNoteId,
      editingEnabled,
      // Die Bitte um Bearbeitung gilt nur dem einen Zettel; alle anderen
      // tragen 0, damit der Abgleich unten sie nicht als geändert sieht.
      editRequest: editRequest?.id === note.id ? editRequest.seq : 0,
      ...handlers,
    },
  }
}

/**
 * Eine Bitte um Bearbeitung an EINEN Zettel — vom Doppelklick auf seinen
 * **Tauschpunkt**, der mitten auf ihm liegt und ihm den Doppelklick sonst
 * stähle. `seq` zählt hoch, damit jede Bitte aufs Neue gilt (siehe
 * `editRequest` in `NoteNode.types.ts`).
 */
interface EditRequest {
  id: string
  seq: number
}

/**
 * Was die **Übernahme** vom Canvas braucht. Sie läuft in der Seite daneben —
 * nur dort sind `lifeSpace`, die Abschnitte und der Weg zur Aufgabenliste
 * bekannt —, aber zwei Dinge kann nur der Canvas beisteuern.
 */
export interface CanvasApplyApi {
  /**
   * Wartet ab, was ein offener **Bearbeiten-Modus** gerade schreibt.
   *
   * Der Text eines Zettels wird erst beim Verlassen des Feldes festgeschrieben,
   * und zwar asynchron. Ein Klick auf den Übernehmen-Knopf nimmt dem Feld zwar
   * den Fokus, aber der Schreibvorgang ist beim Klick noch unterwegs — ohne
   * dieses Warten bekäme die Aufgabe den ALTEN Text, und der Zettel zeigte
   * sofort „bearbeitet".
   */
  flushPendingWrites: () => Promise<void>
  /**
   * Legt die Übernahme als EINEN Schritt auf den Rückgängig-Stapel — samt der
   * Aufgaben, die sie angelegt hat (ADR 0011).
   */
  recordApply: (
    changes: { id: string; before: UpdateCanvasNote; after: UpdateCanvasNote }[],
    createdTaskIds: string[],
  ) => void
}

interface CanvasFlowProps {
  projectId: string
  notes: CanvasNote[]
  tool: CanvasTool
  placementMode: CanvasPlacementMode
  onPlacementConsumed: () => void
  /**
   * Ob gerade vom Werkzeug-Knopf GEZOGEN wird (Zeiger gedrückt). Dann setzt das
   * Loslassen ab; ohne das bleibt es beim heutigen Weg, wo der nächste Klick
   * auf die Fläche absetzt (`handlePaneClick`).
   */
  placementDrag: boolean
  /** Ziehen außerhalb der Fläche losgelassen — nichts anlegen, Werkzeug entschärfen. */
  onPlacementCancelled: () => void
  /**
   * Der Zug endete auf der Werkzeug-Pille: nichts anlegen, aber das Werkzeug
   * BLEIBT scharf. Nur der Zug ist zu Ende — sonst wartete der nächste Klick
   * auf die Fläche auf ein Loslassen, das nicht mehr kommt.
   */
  onPlacementDragEnd: () => void
  /** Nur der sichtbare Tab darf Tastenkürzel abfangen (TabStack-Falle). */
  isActiveTab: boolean
  /**
   * Zähler, der die 1:1-Ansicht von außen anfordert (Doppelklick auf das
   * Hand-Werkzeug). Ein Zähler und kein Boolean, weil dieselbe Anforderung
   * beliebig oft hintereinander kommen darf — ein `true`, das `true` bleibt,
   * löste nur einmal aus.
   */
  resetZoomSignal: number
  /**
   * Wer gerade ausgewählt ist — als Kennungen, nicht als Rechtecke.
   *
   * Die Lage ändert sich bei jedem Ziehen, die Auswahl nicht; eine Meldung
   * über Rechtecke feuerte pro Pixel. Gemeldet wird nur, wenn sich die Menge
   * WIRKLICH geändert hat.
   */
  onSelectionChange?: (ids: string[]) => void
  /**
   * Klick auf das **Eselsohr** eines übernommenen Notiz — führt zur
   * Aufgabe. Der Canvas weiß nicht, wie das geht; die Seite daneben schon.
   */
  onOpenTask?: (taskId: string) => void
  /**
   * Ein Treffer der **Suche** (#233): das Element auswählen und den Ausschnitt in 200 ms hinfahren,
   * wie die **Tab-Reihenfolge**. Steht es schon ganz im Bild, fährt nichts. Jede neue `seq` einmal.
   */
  revealRequest?: CanvasRevealRequest | null
  /**
   * Wird von innen befüllt: Der Canvas legt hier ab, was die **Übernahme**
   * von ihm braucht (siehe {@link CanvasApplyApi}).
   *
   * Ein Ref und keine Props-Rückgabe, weil beides Funktionen sind, die die
   * Seite RUFT und nicht beobachtet — als State erzwängen sie ein
   * Neuzeichnen des Canvas bei jedem Aufbau.
   */
  applyApiRef?: React.MutableRefObject<CanvasApplyApi | null>
  /**
   * Die **Übernehmen-Pille** an der Leiste EINES Zettels: Dieser eine wird
   * eine Aufgabe. Fehlt der Rückruf, zeigt keine Leiste die Pille.
   */
  onApplyNote?: (noteId: string) => void
  /** Dieselbe Pille an der Leiste der **Mehrfachauswahl**: alle übernahmefähigen der Auswahl. */
  onApplyApplicable?: () => void
  /**
   * Die schwebenden Bedienelemente des Canvas — die **Werkzeug-Pille** und die
   * Aktionsgruppe, jeweils samt ihrer Platte. Sie werden unverändert an
   * `<ReactFlow>` durchgereicht und NICHT gedeutet.
   *
   * Sie gehören hier hinein und nicht ins Panel daneben (ADR 0008): Nur als
   * Kind von `.react-flow` landet ihre Fläche im selben Stacking-Kontext wie
   * Punktraster (−1) und Notizen (2) und kann sich dazwischenschieben. Von
   * außen ist die Canvas-Ebene ein geschlossener Block — dort verdeckt die
   * Fläche jeden Notizen.
   */
  children?: React.ReactNode
}

/**
 * The mounted `@xyflow/react` canvas — only ever rendered while the
 * Projekt-Canvas panel is `split`/`maximized` (never kept alive collapsed;
 * see the CLAUDE.md TabStack `display:none` measurement gotcha). Must live
 * inside a `<ReactFlowProvider>` (provided by `ProjectCanvasPanel`).
 */
export const CanvasFlow: React.FC<CanvasFlowProps> = ({ projectId, notes, tool, placementMode, onPlacementConsumed, placementDrag, onPlacementCancelled, onPlacementDragEnd, isActiveTab, resetZoomSignal, onSelectionChange, onOpenTask, revealRequest = null, applyApiRef, onApplyNote, onApplyApplicable, children }) => {
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NoteNodeData>>([])
  // Der zuletzt verlassene Ausschnitt dieses Projekts. Der Canvas wird beim
  // Schließen abgebaut, der gemerkte Wert ist also der Startwert des nächsten
  // Aufbaus — mehr ist zum Wiederherstellen nicht nötig.
  const { initialViewport, rememberViewport } = useCanvasViewport(projectId)
  // Die Kennungen der Kopien, die noch nicht aus der Live-Query zurück sind.
  // Solange hier etwas steht, nimmt ⌘D nichts an — die Auswahl liegt bis dahin
  // noch auf den ORIGINALEN, ein zweiter Anschlag legte also dieselben Kopien
  // ein zweites Mal an, deckungsgleich und nicht zu unterscheiden.
  const pendingDuplicatesRef = useRef<string[] | null>(null)
  /**
   * Was beim Anfassen galt: welche Canvas-Elemente die Geste bewegt, wo sie
   * lagen, ob Alt lag, und — sobald die Schwelle gefallen ist — welche Kopie
   * zu welchem gehört.
   *
   * Die Ausgangspositionen dienen doppelt: Die Kopie beim Alt-Ziehen gehört
   * dorthin, wo angefasst wurde (angelegt wird erst ab
   * `DUPLICATE_DRAG_THRESHOLD`, da sind die Nodes schon weiter), und die
   * Mehrfachauswahl rechnet ihren Versatz daraus — über die GESAMTE Geste,
   * nicht je Frame.
   */
  const dragRef = useRef<{
    alt: boolean
    /** Die Kennungen, die diese Geste bewegt (siehe `handleNodeDragStart`). */
    moving: Set<string>
    origins: Map<string, XYPosition>
    done: boolean
    copies: Map<string, string>
    /**
     * Wen ein gezogener **Bereich** mitträgt: Mitglied → Bereich. Beim
     * Anfassen eingefroren, wie die Ausgangslagen; die Mitglieder rücken um
     * denselben Weg wie ihr Bereich — und nie zweimal: Ist eines zugleich
     * selbst ausgewählt, steht es in `moving`, nicht hier.
     */
    carried?: Map<string, string>
  } | null>(null)
  /**
   * Läuft gerade eine Zeiger-Geste? ⌘D nimmt dann nichts an: Die Kopie entstünde
   * an der laufenden statt an der gemeinten Stelle, und der Nachlauf-Effekt
   * risse Auswahl und Ausschnitt (`setCenter`, 200 ms) mitten in der Geste weg.
   */
  const gestureRef = useRef(false)
  /**
   * Die Kennungen, deren Lage und Maße eine eigene Zeiger-Geste gerade führt — **Platz tauschen** und die Gesten am
   * Rahmen einer Auswahl. Sie schreiben die Geometrie direkt in die Nodes, ohne React Flows `dragging`; der
   * Node-Abgleich lässt sie ihnen, bis die Geste endet (siehe dort). `null` außerhalb einer Geste.
   */
  const gestureHeldRef = useRef<ReadonlySet<string> | null>(null)
  /*
    Was der Rahmen einer Mehrfachauswahl gerade trägt — es liegt während der
    Geste obenauf (siehe `flowNodes`). Zustand und kein Ref wie
    `gestureHeldRef`: Anfang und Ende müssen neu zeichnen, damit die Höhe
    wechselt; dazwischen zeichnet die Bewegung ohnehin.
  */
  const [groupLiftIds, setGroupLiftIds] = useState<ReadonlySet<string>>(NO_LIFT)
  /** Beim Alt-Ziehen am Gruppenrahmen: gezogene Kennung → Kennung der Kopie. */
  const groupCopiesRef = useRef<Map<string, string> | null>(null)
  // Das Canvas-Element, an dem das Duplizieren-Kürzel horcht. Über `useStore`
  // und nicht über `useStoreApi`, damit der Hook es bekommt, sobald React Flow
  // seine Pane aufgebaut hat — sonst hinge es an der Reihenfolge der Effekte.
  const canvasElement = useStore(state => state.domNode)
  // Die zuletzt abgesetzte Notiz — sie startet im Bearbeiten-Modus, weil sie
  // leer ist und beschriftet werden soll.
  const [autoEditNoteId, setAutoEditNoteId] = useState<string | null>(null)
  // Die Bitte um Bearbeitung vom Tauschpunkt aus (siehe `EditRequest`).
  const [editRequest, setEditRequest] = useState<EditRequest | null>(null)
  // Der Zettel, der gerade bearbeitet wird — sein Tauschpunkt weicht derweil.
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  /*
    Der Zettel unter dem Zeiger — er trägt den **Tauschpunkt** (CONTEXT.md):
    ob ausgewählt oder nicht, und ohne Zeiger darauf gibt es ihn nicht.
  */
  const [hoveredNoteId, setHoveredNoteId] = useState<string | null>(null)
  /*
    **Angetippt** — der geklebte Zettel, dessen Leiste offen steht (siehe
    `useCanvasTapped`). Genau EINER, so wie es genau eine Auswahl gibt; er kann
    nicht ausgewählt sein, denn ein geklebter Zettel gerät in keine Auswahl.
  */
  const [tappedId, setTappedId] = useState<string | null>(null)
  const tappedRef = useRef<string | null>(null)
  tappedRef.current = tappedId
  /**
   * Wessen **Tauschpunkt** der Zeiger gerade berührt — sein Zettel wächst
   * (`RING_HOVER_CLASS`). In einer Referenz UND als Klasse am Node: Die Klasse
   * zeigt es, die Referenz überlebt eine Geste, damit der Zettel nach dem
   * Loslassen gewachsen bleibt, solange die Hand auf dem Ring liegt.
   *
   * Eigener Zustand, nicht `hoveredNoteId`: Der meint „Zeiger auf dem Zettel
   * ODER seinem Ring" und entscheidet, wer überhaupt einen Ring bekommt.
   */
  const ringHoverRef = useRef<string | null>(null)
  const markRingHover = useCallback((id: string | null) => {
    if (ringHoverRef.current === id) return
    ringHoverRef.current = id
    setNodes(ns => ns.map(node => {
      const wants = node.id === id
      const has = node.className?.split(' ').includes(RING_HOVER_CLASS) ?? false
      if (wants === has) return node
      const rest = (node.className ?? '').split(' ').filter(name => name && name !== RING_HOVER_CLASS)
      return { ...node, className: [...rest, ...(wants ? [RING_HOVER_CLASS] : [])].join(' ') || undefined }
    }))
  }, [setNodes])
  const handleNodeMouseEnter = useCallback((_event: React.MouseEvent, node: { id: string }) => {
    setHoveredNoteId(node.id)
  }, [])
  const handleNodeMouseLeave = useCallback((event: React.MouseEvent) => {
    // Der Ring liegt ÜBER dem Zettel, im Portal: Fährt der Zeiger auf ihn,
    // meldet der Zettel ein Verlassen — das darf den Ring nicht wegnehmen,
    // sonst wäre er nie zu greifen.
    const to = event.relatedTarget
    if (to instanceof Element && to.closest('.canvas-swap-point')) return
    setHoveredNoteId(null)
  }, [])
  // Die Gegenrichtung: vom Ring weg. Zurück auf den eigenen Zettel ist kein
  // Verlassen; alles andere (ins Leere, auf einen anderen Zettel) ist eines —
  // ein anderer Zettel meldet sein Betreten dann selbst.
  const handleSwapPointLeave = useCallback((id: string, event: React.PointerEvent) => {
    markRingHover(null)
    const to = event.relatedTarget
    if (to instanceof Element && to.closest('.react-flow__node')?.getAttribute('data-id') === id) return
    setHoveredNoteId(current => (current === id ? null : current))
  }, [markRingHover])
  const handleEditingChange = useCallback((noteId: string, editing: boolean) => {
    setEditingNoteId(current => (editing ? noteId : current === noteId ? null : current))
  }, [])
  /**
   * Ein Bereich übergibt einen Zug an den **Auswahlrahmen** (siehe
   * `AreaNodeData.onFrameGrab`). Über eine Ref, weil `startMove` erst weiter
   * unten aus `useGroupDrag` kommt — und stabil, weil er in die `data` der
   * Nodes wandert.
   */
  const startMoveRef = useRef<(event: React.PointerEvent) => void>(() => undefined)
  const handleFrameGrab = useCallback((event: React.PointerEvent) => { startMoveRef.current(event) }, [])
  const { screenToFlowPosition, flowToScreenPosition, zoomTo, setCenter, setViewport, getZoom, deleteElements } = useReactFlow()
  // Für den sichtbaren Ausschnitt beim Ausrichten. `useStoreApi` statt
  // `useStore`, weil der Wert nur im Handler gebraucht wird — ein Hook auf
  // die Maße ließe den Canvas bei jeder Fenstergröße neu rendern.
  const storeApi = useStoreApi()

  /*
    Der Zoom-Gegenfaktor als CSS-Variable — ausgelagert nach
    `hooks/useCanvasZoomVars` (2026-09-05).
  */
  const { gridRef, applyZoomVars } = useCanvasZoomVars(canvasElement, storeApi, GRID, GRID_DOT_RADIUS)

  // Die Hilfslinien liegen in eigenem State: er rendert nur `<HelperLines>`
  // neu, nicht den Canvas.
  const [guides, setGuides] = useState(NO_GUIDES)
  // Die Ausrichtungspartner, einmal je Ziehvorgang gesammelt (siehe
  // `handleNodeDragStart`). Als Ref, weil sich daran während des Ziehens
  // nichts ändert und ein State-Update je Frame nur Arbeit machen würde.
  const alignTargetsRef = useRef<Bounds[]>([])
  // Die zuletzt AUSGEGEBENE Position je Node. React Flow reicht an
  // `onNodeDragStop` seine eigene Zeigerrechnung durch
  // (`getEventHandlerParams` in @xyflow/system) — die kennt unsere Korrektur
  // nicht. Ohne dieses Ref landete die nicht ausgerichtete Position in der
  // Datenbank und die Notiz spränge beim Loslassen zurück.
  const lastPositionRef = useRef(new Map<string, XYPosition>())
  // Die Klammern beim Ziehen an einer Ecke — eigener State aus demselben
  // Grund wie die Hilfslinien.
  const [measures, setMeasures] = useState<Measure[]>(NO_MEASURES)
  // Der laufende Ziehvorgang an einer Ecke: welcher Notiz, welche Kanten der
  // Zeiger führt, und welche ART Canvas-Element es ist. Die Angabe kommt vom
  // Node (siehe `NoteNodeData`); aus den Änderungen allein ließe sie
  // sich nicht herleiten. `kind` entscheidet unten in `handleNodesChange`
  // zwischen `getResizeAlignment` (seitenverhältnistreuer Notiz) und
  // `getWidthResizeAlignment` (Notiz, nur Breite) — aus `notesRef` und nicht
  // aus dem React-Flow-Node selbst (Grund an `handleResizeStart` unten, wo
  // dieses Feld gesetzt wird).
  const resizeRef = useRef<
    {
      id: string
      kind: CanvasNote['kind']
      /**
       * Woran gezogen wird. Die Rechnung scheidet sich HIERAN, nicht an der
       * Art: Der **Notiz** hat beides — Ecken zum Skalieren und Kanten für
       * seine zweite Breite. Nach der Art zu verzweigen schickte einen
       * Kantenzug am Notizen durch die seitenverhältnistreue Rechnung, und
       * die schriebe aus der neuen Breite eine erfundene Höhe und ein daraus
       * verschobenes `y` — derselbe Fehler, den die **Notiz** schon hatte.
       */
      mode: ResizeMode
      moving: MovingEdges
      /** Welche Achsen der Zeiger führt — beim freien Skalieren an einer Kante nur eine. */
      axes: LedAxes
      start: MovingRect | null
      /** Beim **Text**: die Schriftgröße beim Anfassen — sie skaliert mit der Höhe. */
      fontSize?: number
      /** Beim **Text**: der Inhalt beim Anfassen — eigene Größen darin skalieren mit (ADR 0029). */
      content?: string
    } | null
  >(null)
  // Die zuletzt AUSGEGEBENE Größe und Lage. Dasselbe Spiel wie bei
  // `lastPositionRef`: React Flow schickt beim Loslassen noch einmal seine
  // eigene Rechnung (`prevValues` in `XYResizer`), die unsere Korrektur nicht
  // kennt.
  const lastResizeRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null)
  const getCursorAnchor = useCanvasCursorAnchor(isActiveTab)

  /* ── Rückgängig und Wiederherstellen ─────────────────────────────────────
     Siehe `utils/canvasHistory`. Der Stapel gehört DIESEM Canvas und lebt so
     lange wie das geöffnete Panel: Ein gemeinsamer Stapel über mehrere
     Projekte nähme mit ⌘Z etwas zurück, das man gerade gar nicht sieht.

     Als Ref und nicht als State: Nichts im Bild hängt daran — es gibt bewusst
     keine Meldung und keinen ausgegrauten Menüpunkt. Ein State erzwänge ein
     Neuzeichnen des Canvas bei jedem Schritt.
  */
  const historyRef = useRef<CanvasHistoryState>(EMPTY_HISTORY)

  /**
   * Sammelt die Feldänderungen EINES Schritts und legt ihn oben auf.
   *
   * Der Aufrufer schreibt selbst in die Datenbank und meldet dem Rekorder
   * dabei, was er vorfand. Das ist der Grund, warum die Aufzeichnung nicht
   * in `indexeddb.ts` sitzt: Die alten Werte liegen an der Aufrufstelle
   * ohnehin schon vor — die Ausgangslage einer Geste, die Farbe vor dem
   * Klick — und ein Nachlesen aus der Datenbank wäre ein zusätzlicher
   * Zugriff für eine Auskunft, die man hat.
   *
   * @example
   * recordStep(step => {
   *   for (const moved of nodes) {
   *     step.update(moved.id, { x: origin.x }, { x: next.x })
   *     void dataSource.updateCanvasNote(moved.id, { x: next.x })
   *   }
   * })
   */
  const recordStep = useCallback((collect: (step: StepRecorder) => void) => {
    const step = new StepRecorder()
    collect(step)
    historyRef.current = pushStep(historyRef.current, step.finish())
  }, [])

  /*
    Die Kette der laufenden Notiz-Schreibvorgänge.

    Sie trägt genau einen Zweck: Die **Übernahme** muss wissen, wann ein
    offener **Bearbeiten-Modus** seinen Text draußen hat. Ein Klick auf den
    Knopf nimmt dem Textfeld den Fokus, das schreibt — aber der Klick läuft
    weiter, während der Schreibvorgang noch unterwegs ist.

    Eine Kette und kein Zähler: Sie lässt sich schlicht abwarten, und ein
    fehlgeschlagener Schreibvorgang darf sie nicht für immer blockieren
    (deshalb das `catch`).
  */
  const noteWritesRef = useRef<Promise<unknown>>(Promise.resolve())
  const trackNoteWrite = useCallback((write: Promise<unknown>) => {
    noteWritesRef.current = noteWritesRef.current.then(() => write).catch(() => undefined)
  }, [])

  /*
    Was die **Übernahme** vom Canvas braucht, in den Ref der Seite gelegt.

    Sie selbst läuft dort — nur dort sind `lifeSpace`, die Abschnitte und der
    Weg zur Aufgabenliste bekannt. Der Canvas steuert zwei Dinge bei, die
    niemand sonst hat: den Stand der offenen Schreibvorgänge und den
    Rückgängig-Stapel.
  */
  useEffect(() => {
    if (!applyApiRef) return
    applyApiRef.current = {
      flushPendingWrites: () => noteWritesRef.current.then(() => undefined),
      recordApply: (changes, createdTaskIds) => {
        recordStep(step => {
          for (const change of changes) step.update(change.id, change.before, change.after)
          step.createdTasks(createdTaskIds)
        })
      },
    }
    return () => { applyApiRef.current = null }
  }, [applyApiRef, recordStep])

  /**
   * Was der nächste abgesetzte Zettel tragen wird — **zuletzt gewählt gilt
   * weiter** (CONTEXT.md).
   *
   * Als Zustand und nicht nur im Speicher: Das **Vorschaubild** zeigt diese
   * Farbe, und es muss sich neu zeichnen, sobald sie sich ändert.
   */
  // Je Art ein eigenes Set — Notiz und Memo merken sich Farbe und Schrift
  // getrennt (CONTEXT.md, „Zuletzt gewählt gilt weiter").
  const [noteDefaults, setNoteDefaults] = useState(() => readAllNoteDefaults(projectId))
  useEffect(() => {
    setNoteDefaults(readAllNoteDefaults(projectId))
  }, [projectId])
  /*
    Derselbe Stand als Ref — für `useAreaDrawing`, das ihn beim **Aufziehen**
    liest. Über den Zustand hinge dort ein Rückruf daran, und der Hook bände
    seine Zeigerlauscher bei jedem Farbwechsel neu ab und wieder an. Dasselbe
    Muster wie bei `nodesRef` und `notesRef` weiter unten.
  */
  const noteDefaultsRef = useRef(noteDefaults)
  noteDefaultsRef.current = noteDefaults

  /**
   * Merkt sich Farbe und Schrift für den nächsten Zettel — sofern die Änderung
   * überhaupt eine davon betraf.
   *
   * An dieser Stelle und nicht in der Leiste: Sie weiß nichts vom Projekt, und
   * beide Wege (einzeln wie in der Mehrfachauswahl) laufen hier zusammen.
   */
  const rememberNoteDefaults = useCallback((kind: CanvasNote['kind'], after: UpdateCanvasNote) => {
    const change: { color?: string | null; font?: string | null; fontSize?: number } = {}
    if (typeof after.color === 'string') change.color = after.color
    // Beim **Text** ist `null` eine Wahl: die Standard-Tinte.
    if (kind === 'text' && after.color === null) change.color = null
    // Und er merkt sich JEDE Größenänderung — Menü, Aufziehen, Ecken (CONTEXT.md).
    if (kind === 'text' && typeof after.fontSize === 'number') change.fontSize = after.fontSize
    /*
      **Die Schrift nur, wo es sie gibt.** Der **Bereich** hat keinen
      Schrift-Knopf (`buttons={['color', 'title', 'delete']}` in `AreaNode`);
      ein gemerktes `font` von ihm wäre ein Wert, den niemand eingestellt hat
      und den er auch nie zeigte.
    */
    if (kind !== 'area' && 'font' in after) change.font = after.font ?? null
    if (Object.keys(change).length === 0) return
    writeCanvasNoteDefaults(projectId, kind, change)
    setNoteDefaults(current => ({ ...current, [kind]: { ...current[kind], ...change } }))
  }, [projectId])

  /**
   * Eine Änderung, die von der Notiz selbst ausgeht — Farbe, Emoji, Text.
   *
   * Die alten Werte kommen von dort: Sie stehen in `note`, die Notiz weiß
   * also, was sie vorfand. Hier wird daraus ein Schritt.
   *
   * MUSS stabil bleiben: Die Funktion wandert in die `data` der Nodes, und
   * der Abgleich vergleicht sie nicht mit (siehe `onResizeStart`).
   */
  const handleNoteEdit = useCallback((
    id: string,
    before: UpdateCanvasNote,
    after: UpdateCanvasNote,
  ) => {
    recordStep(step => step.update(id, before, after))
    /*
      Was geschrieben ist, aber noch nicht zurückkam: Die Live-Query meldet die
      Zeile erst eine Runde später, bis dahin trägt `data.note` den alten
      Stand. Wer im Text sitzt und ⌘D drückt, bekäme sonst eine Kopie von
      VOR dem Tippen — bei einem frischen Notizen also eine leere.
      Aufgeräumt wird, sobald die Prop den Wert zeigt (siehe unten).
    */
    pendingEditsRef.current.set(id, { ...pendingEditsRef.current.get(id), ...after })
    trackNoteWrite(dataSource.updateCanvasNote(id, after))
    // Die Art kommt vom Datensatz — die Notiz weiß sie, meldet sie aber nicht mit.
    const kind = notesRef.current.find(n => n.id === id)?.kind
    if (kind) rememberNoteDefaults(kind, after)
  }, [recordStep, trackNoteWrite, rememberNoteDefaults, dataSource])

  /**
   * Feldänderungen, die geschrieben, aber noch nicht über die Live-Query
   * zurückgekommen sind (siehe `handleNoteEdit`). Nur das **Duplizieren**
   * liest sie — es ist die einzige Stelle, die aus `data.note` einen NEUEN
   * Datensatz baut und deshalb den frischesten Stand braucht.
   */
  const pendingEditsRef = useRef(new Map<string, UpdateCanvasNote>())

  /** Löschen aus der Options-Leiste einer einzelnen Notiz. */

  /** Maße eines Nodes. `measured` gewinnt, weil es die tatsächlich gerenderte
   *  Größe ist; die mitgegebenen Werte sind nur der Startwert. */
  // `||` statt `??`, und das ist kein Stilfrage: Ein noch ungemessener Node
  // meldet `measured: { width: 0, height: 0 }` — also NICHT `undefined`, womit
  // `??` weiterreichen würde. Ein Rechteck ohne Ausdehnung überlappt nichts und
  // fluchtet mit nichts; eine frisch eingetroffene Kopie wäre für die
  // Kollisionsprüfung beim Duplizieren unsichtbar, und die nächste Kopie
  // landete auf ihr. `NoteNode` sitzt derselben Falle auf und löst sie
  // ebenso.
  const sizeOf = useCallback((node: Node<NoteNodeData>) => ({
    width: node.measured?.width || node.width || DEFAULT_CANVAS_NOTE_WIDTH,
    height: node.measured?.height || node.height || DEFAULT_CANVAS_NOTE_HEIGHT,
  }), [])

  /**
   * Die Zugehörigkeit zu **Bereichen** neu entscheiden (ADR 0012): Jeder
   * Zettel (oder nur die in `only`) bekommt den Bereich, der ihn zu mehr als
   * der Hälfte bedeckt — an den Lagen, die die Geste gerade festgelegt hat
   * (`lastPositionRef`, sonst die Node-Lage). Geschrieben wird nur, was sich
   * ändert, in den mitgegebenen Schritt für **Rückgängig**. Die Bereiche
   * kommen in Node-Reihenfolge, also nach `zIndex`: Der spätere liegt oben
   * und gewinnt bei Gleichstand (siehe `areaFor`).
   */
  const settleAreas = useCallback((
    step: { update: (id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => void },
    only: Set<string> | null,
  ) => {
    const nodes = nodesRef.current
    const at = (n: Node<NoteNodeData>) => lastPositionRef.current.get(n.id) ?? n.position
    const areas = nodes
      .filter(n => n.data?.note?.kind === 'area')
      .map(n => ({ id: n.id, rect: { ...at(n), ...sizeOf(n) } }))
    for (const n of nodes) {
      const note = n.data?.note
      if (!note || note.kind === 'area') continue
      if (only && !only.has(n.id)) continue
      const next = areaFor({ ...at(n), ...sizeOf(n) }, areas)?.id ?? null
      // `?? null`: Eine Zeile von vor der Migration kennt das Feld nicht, und
      // „kein Bereich" soll keinen Schreibvorgang auslösen.
      const current = note.areaId ?? null
      if (next === current) continue
      step.update(n.id, { areaId: current }, { areaId: next })
      void dataSource.updateCanvasNote(n.id, { areaId: next })
    }
  }, [sizeOf, dataSource])

  /** Doppelklick auf den Rand eines **Bereichs**: eng um seine Mitglieder, ein Takt Luft. */
  const handleFitToContent = useCallback((id: string) => {
    const area = nodesRef.current.find(n => n.id === id)
    if (!area) return
    const fit = fitAround(
      nodesRef.current.filter(n => n.data?.note?.areaId === id).map(n => ({ ...n.position, ...sizeOf(n) })),
    )
    if (!fit) return
    recordStep(step => step.update(id, { x: area.position.x, y: area.position.y, ...sizeOf(area) }, fit))
    void dataSource.updateCanvasNote(id, fit)
  }, [recordStep, sizeOf, dataSource])

  const handleNoteDelete = useCallback((id: string) => {
    recordStep(step => {
      step.deleted(id, new Date())
      // Ein gelöschter **Bereich** lässt seine Zettel liegen — und frei.
      for (const n of nodesRef.current) {
        if (n.data?.note?.areaId !== id) continue
        step.update(n.id, { areaId: id }, { areaId: null })
        void dataSource.updateCanvasNote(n.id, { areaId: null })
      }
    })
    void dataSource.deleteCanvasNote(id)
  }, [recordStep, dataSource])

  /**
   * Ein **Text** wurde leer verlassen (CONTEXT.md, Text) — es gibt ihn danach
   * nicht mehr.
   *
   * | vorher | danach |
   * |---|---|
   * | nie etwas darin (frisch abgesetzt) | spurlos weg — auch aus dem Stapel (`forgetNote`) |
   * | ein Text | gelöscht, EIN Schritt; ⌘Z bringt ihn mit seinem Text zurück |
   *
   * Der alte Text steht dabei noch in der Zeile: Gelöscht wird nur weich, das
   * Leeren selbst wird nie geschrieben — das Rückgängig muss also nur das
   * Löschen zurücknehmen.
   */
  const handleTextEmptied = useCallback((id: string, before: string) => {
    if (isCanvasTextEmpty(before)) {
      historyRef.current = forgetNote(historyRef.current, id)
      void dataSource.deleteCanvasNote(id)
      return
    }
    recordStep(step => step.deleted(id, new Date()))
    void dataSource.deleteCanvasNote(id)
  }, [recordStep, dataSource])

  /*
    Der **Aufnahme-Umriss** (ADR 0012): Während man zieht, steht die Marke an
    dem, was STEHT — am Bereich, der den getragenen Zettel bekäme, oder an
    den Zetteln, die ein getragener Bereich bekäme. Weil das Stehende sich
    nicht rührt, ändert sich je Frame höchstens, WER gemeint ist; nur dann
    wird gesetzt, sonst liefe die Vorschau bei jedem Zeigerimpuls durch
    React.
  */
  const [adoption, setAdoptionState] = useState<{ area: AdoptionMark | null; notes: AdoptionMark[] }>({ area: null, notes: [] })
  const adoptionKeyRef = useRef('')
  const setAdoption = useCallback((next: { area: AdoptionMark | null; notes: AdoptionMark[] }) => {
    const key = `${next.area?.id ?? ''}|${next.notes.map(n => n.id).join(',')}`
    if (key === adoptionKeyRef.current) return
    adoptionKeyRef.current = key
    setAdoptionState(next)
  }, [])
  const clearAdoption = useCallback(() => setAdoption({ area: null, notes: [] }), [setAdoption])

  /**
   * Was beim Loslassen an dieser Lage geschähe — als Marke. `moved` sind die
   * Elemente in der Hand, mit ihrer Lage JETZT; alles andere steht.
   *
   * Nur, was sich ÄNDERTE, wird gezeigt: Ein Zettel, der in seinem eigenen
   * Bereich umherzieht, sieht keine Marke — ein Angebot, das nichts tut, ist
   * keines (dieselbe Regel wie `idle` bei den Ableg-Flächen). Bei mehreren
   * getragenen Zetteln keine Marke: Jeder landete für sich, und vier Konturen
   * an einem Bereich sagten nichts.
   */
  const previewAdoption = useCallback((moved: { id: string; rect: MovingRect }[]) => {
    const nodes = nodesRef.current
    const movedIds = new Set(moved.map(m => m.id))
    const rectOf = (n: Node<NoteNodeData>) => moved.find(m => m.id === n.id)?.rect ?? { ...n.position, ...sizeOf(n) }
    const areas = nodes
      .filter(n => n.data?.note?.kind === 'area')
      .map(n => ({ id: n.id, rect: rectOf(n), color: n.data.note.color }))
    const movedAreas = areas.filter(a => movedIds.has(a.id))
    const toneOf = (id: string) => getCanvasNoteRingColor(areas.find(a => a.id === id)?.color ?? null)
    let area: AdoptionMark | null = null
    const notes: AdoptionMark[] = []
    if (movedAreas.length === 0) {
      const singles = moved.filter(m => nodes.find(n => n.id === m.id)?.data?.note?.kind !== 'area')
      if (singles.length === 1) {
        const single = singles[0]!
        const target = areaFor(single.rect, areas)
        const current = nodes.find(n => n.id === single.id)?.data?.note?.areaId ?? null
        if (target && target.id !== current) area = { id: target.id, rect: target.rect, accent: toneOf(target.id) }
      }
    } else {
      const carried = dragRef.current?.carried
      for (const n of nodes) {
        const note = n.data?.note
        if (!note || note.kind === 'area' || movedIds.has(n.id) || carried?.has(n.id)) continue
        const rect = { ...n.position, ...sizeOf(n) }
        const target = areaFor(rect, areas)
        if (target && movedIds.has(target.id) && target.id !== (note.areaId ?? null)) {
          // Der Ton des ZETTELS, auf dem die Marke sitzt (siehe `toneOfNote`).
          notes.push({ id: n.id, rect, accent: toneOfNote(note.color) })
        }
      }
    }
    setAdoption({ area, notes })
  }, [sizeOf, setAdoption])

  /**
   * Ein NEUER Bereich nimmt auf, was unter ihm liegt — beim Klick wie beim
   * Aufziehen (CONTEXT.md: „wie ein Bereich, den man um sie herum aufzieht").
   * Er liegt oben und gewinnt darum jeden Gleichstand; wer schon zu einem
   * anderen gehört und von ihm nun mehr bedeckt wird, wechselt.
   */
  const adoptUnder = useCallback((
    step: { update: (id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => void },
    areaId: string,
    rect: MovingRect,
  ) => {
    const nodes = nodesRef.current
    const areas = [
      ...nodes.filter(n => n.data?.note?.kind === 'area').map(n => ({ id: n.id, rect: { ...n.position, ...sizeOf(n) } })),
      { id: areaId, rect },
    ]
    for (const n of nodes) {
      const note = n.data?.note
      if (!note || note.kind === 'area') continue
      const target = areaFor({ ...n.position, ...sizeOf(n) }, areas)?.id ?? null
      const current = note.areaId ?? null
      if (target !== areaId || target === current) continue
      step.update(n.id, { areaId: current }, { areaId: target })
      void dataSource.updateCanvasNote(n.id, { areaId: target })
    }
  }, [sizeOf, dataSource])

  /**
   * Der **Aufnahme-Umriss** schon beim **Aufziehen**: Welche Zettel das
   * Rechteck einfinge, wenn man jetzt losließe (gewünscht 2026-09-08).
   *
   * `previewAdoption` kann das nicht — es rechnet über NODES, und den Bereich
   * gibt es hier noch nicht; er entsteht erst beim Loslassen. Gerechnet wird
   * darum derselbe Plan wie in `adoptUnder`, das die Zugehörigkeit dann
   * wirklich schreibt: dieselbe Mehrheitsregel (`areaFor`), derselbe Vorrang
   * des Frischen bei Gleichstand (er steht als letzter in der Liste). So
   * verspricht die Marke nie etwas, was das Loslassen bricht.
   *
   * Den Ton bringt jeder Zettel selbst mit (`toneOfNote`) — eine Marke trägt
   * die Farbe dessen, worauf sie sitzt.
   */
  const previewDraftArea = useCallback((draft: MovingRect | null) => {
    if (!draft) {
      clearAdoption()
      return
    }
    const nodes = nodesRef.current
    const areas = [
      ...nodes.filter(n => n.data?.note?.kind === 'area').map(n => ({ id: n.id, rect: { ...n.position, ...sizeOf(n) } })),
      { id: DRAFT_AREA_ID, rect: draft },
    ]
    const notes: AdoptionMark[] = []
    for (const n of nodes) {
      const note = n.data?.note
      if (!note || note.kind === 'area') continue
      const rect = { ...n.position, ...sizeOf(n) }
      if (areaFor(rect, areas)?.id !== DRAFT_AREA_ID) continue
      notes.push({ id: n.id, rect, accent: toneOfNote(note.color) })
    }
    setAdoption({ area: null, notes })
  }, [sizeOf, setAdoption, clearAdoption])

  /*
    **Aufziehen** — die eine Geste, die es nur beim Bereich gibt; ausgelagert
    nach `hooks/useAreaDrawing` (2026-09-05). `drewRef` schluckt den Klick, den
    der Browser nach dem Loslassen noch meldet (siehe `handlePaneClick`).
  */
  const { drawing, drewRef } = useAreaDrawing({
    placementMode, canvasElement, projectId, screenToFlowPosition,
    // **Aufziehen und Absetzen liefern dasselbe** — beide nehmen die zuletzt
    // gemerkte Farbe. Als Funktion, damit ein Farbwechsel während der Sitzung
    // ankommt, ohne den Hook neu zu binden.
    // `?? DEFAULT_CANVAS_NOTE_COLOR`: Nur der Text kennt `null` als Farbe; beim Bereich steht dort immer ein Schlüssel.
    areaColor: useCallback(() => noteDefaultsRef.current.area.color ?? DEFAULT_CANVAS_NOTE_COLOR, []),
    setNodes, recordStep, adoptUnder, onPlacementConsumed,
    areaTitle: useCallback(() => t(AREA_TITLE_KEY), [t]),
    grid: GRID,
    clickThreshold: CLICK_DRAG_THRESHOLD,
    selectWhenArrived: useCallback((id: string) => { pendingDuplicatesRef.current = [id] }, []),
  })

  /*
    Die Marken folgen dem Rechteck. Am `drawing`-Zustand und nicht im Hook:
    Der ändert sich ohnehin je Bewegung, und `setAdoption` rendert nur, wenn
    sich ÄNDERT, wer gemeint ist. Beim Loslassen wird `drawing` null — die
    Marken gehen, und `adoptUnder` schreibt dieselbe Menge fest.
  */
  useEffect(() => { previewDraftArea(drawing) }, [drawing, previewDraftArea])

  /**
   * ⇧0 und ⌘/⌃0 → **1:1-Ansicht**. Die 200 ms bauen die Brücke über einen
   * Sprung, der bis zu Faktor 3 groß sein kann.
   *
   * Worauf zentriert wird, entscheidet eine Rangfolge:
   *
   * | | |
   * |---|---|
   * | 1. Auswahl | die Mitte der ausgewählten **Canvas-Elemente** |
   * | 2. Zeiger | die Stelle unter dem Zeiger |
   * | 3. sonst | der Ausschnitt bleibt, wo er ist |
   *
   * **Die Auswahl schlägt den Zeiger**, und das ist keine Willkür: Wer ein
   * Element angeklickt hat, meint dieses. Die Maus liegt danach irgendwo —
   * oft genau dort, wo zuletzt geklickt wurde, aber ebenso oft schon
   * weitergezogen. Eine ausdrückliche Auswahl ist die klarere Auskunft.
   *
   * Bei einer Mehrfachauswahl zählt das umschließende Rechteck; sonst zöge
   * ein einzelnes Element die Ansicht aus einer Gruppe heraus, die man gerade
   * als Ganzes im Blick hat.
   */
  const handleResetZoom = useCallback(() => {
    const selected = nodesRef.current.filter(n => n.selected)
    if (selected.length > 0) {
      const box = getBoundingBox(selected.map(n => ({ ...n.position, ...sizeOf(n) })))
      if (box) {
        void setCenter(box.centerX, box.centerY, { zoom: 1, duration: 200 })
        return
      }
    }

    const anchor = getCursorAnchor()
    if (!anchor) {
      // Weder Auswahl noch Zeiger über dem Canvas: Mitte des Ausschnitts
      // behalten.
      void zoomTo(1, { duration: 200 })
      return
    }

    // Sonst rückt die Stelle unter dem Zeiger zugleich in die Mitte des
    // Canvas — man zeigt auf etwas und bekommt es groß und mittig vorgelegt.
    // `setCenter` misst die Pane selbst (React Flow hält ihre Maße im Store),
    // deshalb stimmt die Mitte auch im geteilten Zustand, wo der Canvas nur
    // die rechte Hälfte belegt.
    const flow = screenToFlowPosition(anchor)
    void setCenter(flow.x, flow.y, { zoom: 1, duration: 200 })
  }, [getCursorAnchor, zoomTo, setCenter, screenToFlowPosition, sizeOf])

  useCanvasZoomShortcut({ enabled: isActiveTab, onResetZoom: handleResetZoom })

  // Dieselbe Ansicht, von der Werkzeug-Pille angefordert. Der erste Wert (0)
  // ist der Ruhezustand und darf nichts auslösen — sonst zoomte der Canvas
  // schon beim Öffnen.
  const isFirstSignal = useRef(true)
  useEffect(() => {
    if (isFirstSignal.current) {
      isFirstSignal.current = false
      return
    }
    handleResetZoom()
  }, [resetZoomSignal, handleResetZoom])

  // Source of truth is Dexie (via useCanvasNotes → LiveQuery) — rebuild the
  // RF node list whenever the notes change. Position drags and text edits
  // commit to Dexie at natural "settle" points (dragStop / blur), not per
  // frame/keystroke, so this doesn't fight with in-progress interaction.
  //
  // Abgeglichen statt neu gebaut: Jede Live-Query meldet die GANZE Liste, auch
  // wenn sich eine einzige Notiz geändert hat. Würden hier jedes Mal alle Nodes
  // neu erzeugt, zeichnete React Flow sämtliche Notizen neu — in der laufenden
  // App als 52–88 ms lange Tab-Renders messbar, und zwar zweimal pro angelegter
  // Notiz (das Anlegen schreibt zusätzlich die Position).
  //
  // Nebeneffekt, der genauso zählt: React Flow hält Auswahl und Zwischenstände
  // AM Node-Objekt (über `onNodesChange`). Ein Rundum-Neubau würfe sie weg.
  // Die Nodes als Ref gespiegelt. Die Ecken-Handler unten wandern in die
  // `data` der Nodes und MÜSSEN dort stabil bleiben (siehe
  // `NoteNodeData.onResizeStart`) — sie dürfen also nicht an `nodes`
  // hängen.
  const nodesRef = useRef(nodes)
  nodesRef.current = nodes

  /*
    **Was man in der Hand hat, liegt obenauf** — und nur das.

    Die Auswahl hebt nicht mehr an (`elevateNodesOnSelect={false}`), sonst
    sähe man „Ganz nach hinten" erst nach dem Abwählen. Gehoben wird allein,
    was eine Geste gerade trägt oder was bearbeitet wird: der von React Flow
    gezogene Zettel (`dragging`), die Gruppe am Rahmen (`groupLiftIds`) und
    der Zettel im Bearbeiten-Modus. Das **Platz tauschen** hebt schon selbst
    (`SWAP_LIFTED_Z`). Bereiche nie — sie liegen unter allen Zetteln, auch in
    der Hand („beim Bewegen des Bereichs verschwinden die Zettel", 2026-09-05).

    Nur für die Anzeige: Der gespeicherte Stapelplatz bleibt, wie er ist, und
    beim Loslassen fällt das Element dorthin zurück.
  */
  /*
    Die **Vorschau** des Farbwählers der Gruppen-Leiste: je Element die Farbe,
    die der Klick ihm gäbe (CONTEXT.md, Farbwähler). Nur für die Anzeige —
    `flowNodes` legt sie als `data.colorPreview` an, der gespeicherte Stand
    in `nodes` bleibt unberührt. Nach dem Klick hält sie, bis die neuen
    Farben über `notes` zurück sind.
  */
  const [bulkColorPreview, setBulkColorPreview] = useOptionPreview<ReadonlyMap<string, string | null>>(notes)
  // Die Schrift gilt für alle gleich (wie `handleBulkFont`) — ein Wert für die gemeinten Kennungen.
  const [bulkFontPreview, setBulkFontPreview] = useOptionPreview<{ ids: ReadonlySet<string>; font: string | null }>(notes)

  const flowNodes = useMemo(() => {
    let out: Node<NoteNodeData>[] | null = null
    nodes.forEach((node, index) => {
      const colorPreviewed = bulkColorPreview?.has(node.id) ?? false
      const fontPreviewed = bulkFontPreview?.ids.has(node.id) ?? false
      const previewed = colorPreviewed || fontPreviewed
      const lifted = node.data?.note?.kind !== 'area' && node.zIndex !== SWAP_LIFTED_Z
        && (node.dragging || node.id === editingNoteId || groupLiftIds.has(node.id))
      if (!lifted && !previewed) return
      out ??= [...nodes]
      out[index] = {
        ...node,
        ...(lifted ? { zIndex: SWAP_LIFTED_Z } : {}),
        ...(previewed
          ? {
            data: {
              ...node.data,
              ...(colorPreviewed ? { colorPreview: { color: bulkColorPreview!.get(node.id)! } } : {}),
              ...(fontPreviewed ? { fontPreview: { font: bulkFontPreview!.font } } : {}),
            },
          }
          : {}),
      }
    })
    return out ?? nodes
  }, [nodes, editingNoteId, groupLiftIds, bulkColorPreview, bulkFontPreview])

  /*
    Hilfslinien und Maßklammern ebenfalls als Ref: `handleNodesChange` liest
    sie, um nur bei Änderung zu setzen. Lägen sie in seiner Abhängigkeitsliste,
    wäre es nach jeder neuen Linie eine neue Funktion — und React Flow bekäme
    beim Ziehen je Frame ein neues `onNodesChange`, samt Store-Update.
  */
  const guidesRef = useRef(guides)
  guidesRef.current = guides
  const measuresRef = useRef(measures)
  measuresRef.current = measures

  /**
   * Die `notes`-Prop gespiegelt — für `handleResizeStart`, der die ART des
   * angefassten Canvas-Elements (`kind`) braucht, aber genau wie oben nicht an
   * `notes` hängen darf: Er wandert stabil in die `data` der Nodes.
   */
  const notesRef = useRef(notes)
  notesRef.current = notes

  /**
   * Der sichtbare Ausschnitt in Canvas-Koordinaten. Aus der Verschiebung des
   * Viewports und den Maßen der Pane, beides aus React Flows Store.
   */
  const viewportBounds = useCallback(() => {
    const { transform, width, height } = storeApi.getState()
    const [x, y, zoom] = transform
    const left = -x / zoom
    const top = -y / zoom
    return toBounds({ x: left, y: top, width: width / zoom, height: height / zoom })
  }, [storeApi])

  /**
   * **Der erste Blick** — wohin ein Projekt aufgeht, für das noch nichts
   * gemerkt ist (`.claude/tasks/erster-blick-auf-die-zettel.md`).
   *
   * Ohne ihn stand der Ausschnitt auf `{0, 0}`. Wer seine Zettel weiter draußen
   * abgelegt hatte, sah beim ersten Öffnen auf DIESEM Rechner leeres Papier und
   * hatte keinen Anhaltspunkt, in welche Richtung er suchen soll.
   *
   * **Warum ein Effekt und nicht `defaultViewport`.** Den liest React Flow nur
   * beim Aufbau von `XYPanZoom` (siehe `useCanvasViewport`); zu diesem
   * Zeitpunkt sind die Zettel noch nicht aus Dexie zurück. Es gäbe nichts,
   * worauf man zielen könnte.
   *
   * **Die eine Bedingung: es ist gerade NICHTS zu sehen.** Liegt auch nur ein
   * Zettel im Ausschnitt, fällt der Riegel und die Frage stellt sich nicht
   * mehr. Der erste Blick soll einen leeren Anblick beheben, nicht einen
   * vorhandenen ersetzen — ohne diese Grenze sprang der Ausschnitt auf den
   * ersten Zettel, den man auf einem frischen Canvas ablegt, und die Fläche
   * fuhr einem unter der Hand weg.
   *
   * **Ein gemerkter Ausschnitt hält ihn NICHT auf.** Das war erst anders und
   * ging schief: Wer in einem Projekt einmal geschoben hat, hat einen —
   * und der gewann auch dann, wenn er ins Leere zeigte. Gemessen: Fläche
   * 889…1490, Zettel bei x=3098 (2026-09-08). Die Bedingung oben trägt allein:
   * Ein gemerkter Ausschnitt, in dem etwas steht, wird ohnehin nie angefasst.
   * Was man aufgibt, ist die leere Fläche, auf die man bewusst geschoben und
   * dann nichts abgelegt hat — die kommt nicht zurück.
   *
   * **Genau einmal.** Der Riegel liegt in einem Ref und nicht im State — er
   * soll kein Rendern auslösen.
   *
   * `isActiveTab` steht in der Liste wegen der TabStack-Falle (CLAUDE.md):
   * Hinter `display: none` misst die Fläche 0, `firstGlanceViewport` meldet
   * dann `null`, und der Riegel bleibt offen. Ohne die Abhängigkeit liefe der
   * Effekt beim Aktivieren nicht noch einmal — die Zettel ändern sich dabei ja
   * nicht.
   *
   * Gesetzt wird OHNE Dauer. Das ist eine Ausgangslage, keine Bewegung —
   * dieselbe Haltung wie `html.canvas-restoring` beim maximiert
   * wiederhergestellten Canvas (globals.scss).
   */
  const firstGlanceRef = useRef(false)
  useEffect(() => {
    if (firstGlanceRef.current) return
    const { width, height } = storeApi.getState()
    if (width <= 0 || height <= 0) return

    const rects = nodes.map(n => ({ id: n.id, ...n.position, ...sizeOf(n) }))
    if (rects.length === 0) return

    const seen = viewportBounds()
    if (rects.some(r => isVisible(toBounds(r), seen))) {
      firstGlanceRef.current = true
      return
    }

    const view = firstGlanceViewport(rects, { width, height })
    if (!view) return
    firstGlanceRef.current = true
    setViewport(view)
  }, [nodes, isActiveTab, storeApi, sizeOf, setViewport, viewportBounds])

  /*
    **Ein Treffer der Suche** (#233). Hinter dem ersten Blick: Der setzt seinen Ausschnitt ohne
    Dauer, die Fahrt rechnet danach von dort. Gewartet wird, bis das Element als Node dasteht und
    die Fläche Maße hat — beim Aufgehen der Pinnwand ist beides erst ein paar Bilder später so.
  */
  const handledRevealSeqRef = useRef<number | null>(null)
  useEffect(() => {
    if (!revealRequest || revealRequest.seq === handledRevealSeqRef.current || !isActiveTab) return
    const { width, height } = storeApi.getState()
    const target = nodes.find(n => n.id === revealRequest.noteId)
    if (width <= 0 || height <= 0 || !target) return
    handledRevealSeqRef.current = revealRequest.seq
    setNodes(ns => ns.map(n => (n.id === target.id
      ? (n.selected ? n : { ...n, selected: true })
      : (n.selected ? { ...n, selected: false } : n))))
    const box = toBounds({ ...target.position, ...sizeOf(target) })
    if (!isFullyVisible(box, viewportBounds())) {
      void setCenter(box.centerX, box.centerY, { zoom: getZoom() || 1, duration: 200 })
    }
  }, [revealRequest, nodes, isActiveTab, storeApi, setNodes, sizeOf, viewportBounds, setCenter, getZoom])

  /*
    **Das Auswahlrechteck nimmt einen Bereich nur GANZ** (CONTEXT.md, Bereich).
    Wer von außen über die Zettel eines Bereichs zieht, sammelt die Zettel ein
    — der Bereich bleibt liegen. Mit React Flows `Partial` (unten, für die
    Zettel richtig) fasste jedes Rechteck, das einen großen Bereich auch nur
    streift, ihn mit, samt allem, was er trägt; wer drei Zettel darin
    einsammeln wollte, zöge beim nächsten Griff den ganzen Bereich fort.

    Zwei Hälften: HIER entscheidet ein Abo auf das Rechteck selbst, ob ein
    Bereich vollständig darin liegt — React Flow meldet einen Bereich nur
    einmal, beim ersten Streifen, und schweigt, wenn das Rechteck danach
    über ihn hinauswächst. In `handleNodesChange` fällt umgekehrt React Flows
    eigenes „ausgewählt" für einen nur gestreiften Bereich weg. Der Klick
    wählt ihn wie immer: Das gilt nur, solange das Rechteck steht.
  */
  useEffect(() => {
    let previous: unknown = null
    return storeApi.subscribe(state => {
      const rect = state.userSelectionActive ? state.userSelectionRect : null
      if (rect === previous) return
      previous = rect
      if (!rect) return
      const [tx, ty, zoom] = state.transform
      const box = toBounds({ x: (rect.x - tx) / zoom, y: (rect.y - ty) / zoom, width: rect.width / zoom, height: rect.height / zoom })
      const changes: NodeChange<Node<NoteNodeData>>[] = []
      for (const n of nodesRef.current) {
        if (n.data?.note?.kind !== 'area') continue
        const inside = isFullyVisible(toBounds({ ...n.position, ...sizeOf(n) }), box)
        if (inside !== Boolean(n.selected)) changes.push({ id: n.id, type: 'select', selected: inside })
      }
      if (changes.length > 0) onNodesChange(changes)
    })
  }, [storeApi, onNodesChange, sizeOf])

  /**
   * Die Ausrichtungspartner einmal je Vorgang einsammeln, statt bei jedem
   * Zeigerimpuls erneut über alle Nodes zu laufen.
   *
   * @param exclude die Kennungen, die selbst bewegt werden
   */
  const collectAlignTargets = useCallback((exclude: Set<string>, seeAreas = false) => {
    alignTargetsRef.current = nodesRef.current
      // Der **Bereich** ist für die Ordnungslogik der Zettel unsichtbar (ADR
      // 0012). Bereiche untereinander sehen sich aber: Wer einen Bereich zieht
      // oder skaliert, richtet ihn auch an den anderen Bereichen aus — zwei
      // Bereiche nebeneinander sollen oben fluchten können wie zwei Zettel.
      .filter(n => !exclude.has(n.id) && (seeAreas || n.data?.note?.kind !== 'area'))
      .map(n => toBounds({ ...n.position, ...sizeOf(n) }))
  }, [sizeOf])

  /**
   * Legt zu jedem übergebenen Canvas-Element eine Kopie an der mitgegebenen
   * Stelle an und liefert deren Kennungen — in derselben Reihenfolge.
   *
   * Der Aufrufer bestimmt die Zielposition VOLLSTÄNDIG. ⌘D sucht dafür den
   * nächsten freien Platz (`getDuplicateOffset`), das Alt-Ziehen reicht die
   * eingefrorene Ausgangsposition durch — ein gemeinsamer „Versatz" trüge
   * beides nicht.
   *
   * Die Kennungen werden vorab vergeben, wie beim Absetzen
   * (`handlePaneClick`): die Live-Query meldet die neuen Zeilen asynchron,
   * Auswahl und Umhängen müssen aber schon wissen, auf wen sie zeigen.
   *
   * Der Reihe nach angelegt, nicht alle auf einmal: Ohne `zOrder` entscheidet
   * `createdAt` über vorn und hinten (`compareZ`), und gleichzeitig angelegte
   * Notizen bekämen denselben Zeitstempel — ihre Reihenfolge entschiede dann
   * nur noch die Kennung.
   *
   * `taskId`/`convertedAt` fehlen nicht aus Versehen: `CreateCanvasNote` kennt
   * sie gar nicht, `createCanvasNote` setzt sie hart auf `null`. Beim
   * Alt-Ziehen hängt `handOverTaskLink` sie nachträglich um (ADR 0007).
   */
  const duplicateNodes = useCallback((
    entries: { node: Node<NoteNodeData>; x: number; y: number; areaOf?: string }[],
  ): string[] => {
    const ids = entries.map(() => crypto.randomUUID())

    // Die Kopien sind ab sofort Ausrichtungspartner: Man dupliziert per Ziehen,
    // um etwas DANEBEN zu legen — ohne das bliebe ausgerechnet der eben
    // erzeugte Nachbar stumm. `collectAlignTargets` lief beim Anfassen, da gab
    // es ihn noch nicht. Maße und Lage sind hier schon bekannt; auf die
    // Live-Query zu warten käme zu spät.
    alignTargetsRef.current = [
      ...alignTargetsRef.current,
      ...entries.map(entry => toBounds({ x: entry.x, y: entry.y, ...sizeOf(entry.node) })),
    ]

    // Die Kopien als EIN Schritt. Beim Alt-Ziehen kommt gleich noch die
    // Bewegung der Originale dazu — sie landet über `handleNodeDragStop` in
    // einem eigenen Schritt, und das ist die eine Stelle, an der zweimal ⌘Z
    // nötig ist. Beides zusammenzufassen hieße, den Schritt über zwei
    // Ereignisse offen zu halten, die auch einzeln auftreten können.
    const at = new Date()
    recordStep(step => {
      for (const id of ids) step.created(id, at)
    })

    void (async () => {
      for (const [index, entry] of entries.entries()) {
        // Der frischeste Stand: der Datensatz plus das, was gerade erst
        // geschrieben wurde (siehe `pendingEditsRef`).
        const note = { ...entry.node.data.note, ...pendingEditsRef.current.get(entry.node.id) }
        const size = sizeOf(entry.node)
        /*
          Die Felder stehen NAMENTLICH da und nicht als `{ ...note }`: `taskId`,
          `convertedAt`, `cloudId` und `synced` dürfen nicht mitkopiert werden
          (ADR 0007), und eine Ausnahmeliste wäre dieselbe Aufzählung, nur
          andersherum und fehleranfälliger.

          Der Preis ist Vergesslichkeit: Wer ein Feld hinzufügt, muss hier
          daran denken. Ein Test vergleicht die Kopie deshalb feldweise gegen
          das Original.
        */
        await dataSource.createCanvasNote({
          id: ids[index]!,
          projectId,
          kind: note.kind,
          title: note.title,
          content: note.content,
          x: entry.x,
          y: entry.y,
          width: size.width,
          height: size.height,
          // **Die Kopie kommt geneigt.** Die Neigung ist eine Absicht der
          // Hand, keine Erscheinung des Zufalls — sie fährt mit wie Farbe und
          // Text. Gewürfelt wird hier ohnehin nichts (CONTEXT.md, „Neigung").
          rotation: note.rotation,
          color: note.color,
          icon: note.icon,
          // Ein zugeschalteter Autor gehört zum Zettel, nicht zu seiner
          // Kennung. Die Liste hier ist namentlich und deshalb vergesslich —
          // ein Test vergleicht sie feldweise gegen das Original.
          showAuthor: note.showAuthor,
          font: note.font,
          // Beim **Text**: Größe und Ausrichtung sagen, wie er aussieht.
          fontSize: note.fontSize,
          textAlign: note.textAlign,
          // **Die Kopie kommt geklebt.** Wer einen **Bereich** dupliziert, will
          // das Arrangement samt seiner Fixpunkte — und ein geklebtes Mitglied
          // wird mitkopiert, obwohl es in keiner Auswahl steht (siehe die
          // Mitglieder-Sammlung in `handleDuplicate`). Die Erscheinung fährt
          // dabei MIT: Sie steht in der Zeile, und eine Kopie ist eine Kopie.
          // Gewürfelt wird beim Ankleben, nicht beim Duplizieren.
          tape: note.tape,
          // Ein mitkopiertes Mitglied gehört zur KOPIE seines Bereichs.
          areaId: entry.areaOf !== undefined
            ? ids[entries.findIndex(e => e.node.id === entry.areaOf)] ?? null
            : null,
        })
      }
    })()

    return ids
  }, [projectId, sizeOf, recordStep, dataSource])

  /**
   * Die Aufgaben-Verbindung bleibt bei dem Canvas-Element, das LIEGEN BLEIBT
   * (`CONTEXT.md` → Duplizieren, ADR 0007). Beim Alt-Ziehen ist das die frisch
   * angelegte Zeile — ohne dieses Umhängen zöge der User „das Original" weg und
   * ließe ein unverbundenes Element zurück, und sobald umgewandelte Elemente
   * eine Markierung tragen, spränge sie sichtbar auf das falsche.
   *
   * @param copies gezogene Kennung → Kennung der zurückgebliebenen Kopie
   */
  const handOverTaskLink = useCallback((copies: Map<string, string>) => {
    for (const [movedId, copyId] of copies) {
      const note = nodesRef.current.find(n => n.id === movedId)?.data.note
      // Fast immer der Normalfall: nichts umzuhängen, kein Schreibvorgang.
      if (!note?.taskId) continue
      void dataSource.updateCanvasNote(copyId, { taskId: note.taskId, convertedAt: note.convertedAt })
      void dataSource.updateCanvasNote(movedId, { taskId: null, convertedAt: null })
    }
  }, [dataSource])

  /**
   * Eine Notiz wird an einer Ecke angefasst. Der Node meldet, an welcher —
   * herleiten ließe sich das nicht (siehe `NoteNodeData.onResizeStart`).
   *
   * MUSS stabil bleiben: die Funktion wandert in die `data` der Nodes, und der
   * Abgleich weiter unten vergleicht sie nicht mit. Deshalb liest sie die Nodes
   * über `nodesRef` statt über `nodes`.
   */
  const handleResizeStart = useCallback((id: string, corner: ResizeCorner, mode: ResizeMode = 'corner', axes: LedAxes = { x: true, y: true }) => {
    // Die Ausgangsgröße mit einfrieren: Das Rückgängig muss sie
    // wiederherstellen, und beim Loslassen ist sie längst überschrieben.
    const node = nodesRef.current.find(n => n.id === id)
    resizeRef.current = {
      id,
      // Aus `notesRef`, nicht aus `node.type`/`node.data.note`: Mehrere Tests
      // hier (u. a. „Einrasten beim Ziehen an der Ecke", „Notiz: Breite
      // ziehen") mocken `useNodesState` komplett und lassen es einen eigenen,
      // von `noteToFlowNode` unabhängigen Node-Stand liefern — nur
      // `id`/`position`/`measured`/`selected`, ohne `type` und `data`. Sie
      // stehen für das, was React Flow SELBST während des Ziehens über die
      // Geometrie meldet, nicht für den von CanvasFlow gebauten Node. Über
      // `node.type` bekäme `handleResizeStart` dort für jede Notiz
      // `undefined` und fiele auf `sticky` zurück — nachgestellt: Dieselben
      // drei Tests aus „Notiz: Breite ziehen" schlagen dann fehl, mit exakt
      // dem seitenverhältnistreuen Rechenweg, den dieser Fix gerade
      // abgestellt hat. `notesRef` spiegelt stattdessen unmittelbar die
      // `notes`-Prop, die in jedem Test vollständig gefüllt ist.
      //
      // `?? 'note'` bleibt als Typ-Anker stehen: Findet `notesRef` die
      // Notiz nicht (der angefasste Node existiert nicht mehr in der Prop),
      // ist dieser Zweig unerreichbar — SICHER nur, weil er nie läuft, nicht
      // weil `sticky` für eine Notiz das richtige Verhalten wäre. Für eine
      // echte Notiz stellte er exakt den oben beschriebenen Drift-Fehler
      // wieder her.
      kind: notesRef.current.find(n => n.id === id)?.kind ?? 'note',
      mode,
      moving: movingEdgesFor(corner),
      axes,
      start: node ? { ...node.position, ...sizeOf(node) } : null,
      ...(() => {
        const note = notesRef.current.find(n => n.id === id)
        return note?.kind === 'text' ? { fontSize: canvasTextSizeOf(note), content: note.content } : {}
      })(),
    }
    lastResizeRef.current = null
    collectAlignTargets(new Set([id]), resizeRef.current.kind === 'area')
  }, [collectAlignTargets, sizeOf])

  /**
   * Das Canvas-Element, das gerade am **Tauschpunkt** getragen wird.
   *
   * Als Ref und nicht als State: Der Abgleich unten liest es, soll aber nicht
   * bei jedem Anfassen und Loslassen neu laufen — die Höhe setzt ohnehin
   * `applySwapPositions`, hier wird sie nur verteidigt.
   */
  const swapLiftedRef = useRef<string | null>(null)

  /**
   * Die Rückrufe, die in jede `data` wandern. Als eigenes Objekt, damit der
   * Abgleich unten nur EINE Abhängigkeit hat statt dreier — und weil alle drei
   * stabil sind, ist es das hier auch.
   */
  /*
    Der Klick auf das **Eselsohr**, stabil verpackt.

    Über ein Ref und nicht direkt: `onOpenTask` kommt von außen und kann sich
    bei jedem Zeichnen der Seite ändern. Läge es unmittelbar in `nodeHandlers`,
    würde jedes Mal der ganze Node-Baum neu gebaut — und die `data` der Nodes
    ist genau das, was der Abgleich unten mühsam stabil hält.
  */
  const openTaskRef = useRef(onOpenTask)
  openTaskRef.current = onOpenTask
  const handleOpenTask = useCallback((taskId: string) => {
    openTaskRef.current?.(taskId)
  }, [])

  /*
    Die **Anlegepunkte** (siehe `CanvasSpawnHandles`): Der Node meldet nur
    Seite und Zettel; wo der neue landet und wer dafür rückt, rechnet der
    Canvas — er kennt die Nachbarn, aus denen die **Reihe** besteht
    (`getInsertPlan`). Die Vorschau beim Überfahren und der Klick nehmen
    DIESELBE Rechnung: Man sieht vorher genau die Lage, die der Klick dann
    herstellt.
  */
  /*
    `committed`: Der Klick ist gefallen, der echte Zettel ist unterwegs.

    Das Bild bleibt dann STEHEN, bis er da ist — es verliert nur seine
    Transparenz. Nähme man es sofort weg, klaffte an seiner Stelle eine Lücke:
    Zwischen dem Klick und dem Zettel liegen der Schreibvorgang in Dexie und
    die Antwort der Live-Query. Genau dort blitzte der weiße Grund durch, und
    das Anlegen zerfiel in zwei Ereignisse.
  */
  const [spawnPreview, setSpawnPreview] = useState<{ rect: MovingRect; kind: 'note' | 'memo'; color: string | null; committed: boolean } | null>(null)

  /*
    Die Ausgangslage der Zettel, die die Vorschau gerade gerückt hat — dieselbe
    Rolle wie `swapStartRef` beim **Platz tauschen**: Gegen sie wird
    zurückgestellt, wenn der Zeiger den Punkt verlässt, aus ihr kommen beim
    Klick die `before`-Werte des Rückgängig-Schritts, und gegen sie rechnet die
    nächste Vorschau. Ohne dieses Einfrieren rechnete jede Bewegung des Zeigers
    auf einer schon verschobenen Reihe weiter, und die Zettel wanderten mit
    jedem Wechsel der Kante ein Stück weiter fort.
  */
  const spawnStartRef = useRef<Map<string, XYPosition> | null>(null)

  /*
    Ob gerade eine Vorschau steht. Ein eigener Merker und nicht `spawnStartRef`:
    Am Rand einer Reihe rückt niemand, die Vorschau steht trotzdem — und
    Escape muss auch sie zurücknehmen können.
  */
  const spawnPreviewRef = useRef(false)

  /*
    Nach dem Klick auf einen Anlegepunkt muss der Tastaturfokus MITWANDERN.

    Der Punkt ist ein `<button>` und hat den Fokus beim Klick bekommen; im
    selben Atemzug verschwindet er, weil die Auswahl auf den neuen Zettel
    weiterzieht. Der Fokus fiele auf `document.body` — aus dem Canvas heraus.
    Der neue Zettel stünde ausgewählt da, und Entf ginge ins Leere:
    `useCanvasDeleteShortcut` horcht am Canvas-Element, nicht am Dokument.

    Beim **Duplizieren** stellt sich die Frage nicht (der Fokus liegt schon im
    Canvas), deshalb ein eigener Merker und nicht der gemeinsame Weg.
  */
  const focusSpawnedRef = useRef(false)

  /* Der Wecker, der `SHIFT_CLASS` nach dem Gleiten wieder abnimmt. */
  const shiftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /* Der Notausgang für ein übernommenes Bild, dessen Zettel nie eintrifft. */
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /* Nimmt `POP_CLASS` nach dem Federstoß wieder ab. */
  const popTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    clearTimeout(shiftTimerRef.current ?? undefined)
    clearTimeout(commitTimerRef.current ?? undefined)
    clearTimeout(popTimerRef.current ?? undefined)
  }, [])

  /** Die wahre Lage eines Nodes — die der Vorschau vorenthalten, falls sie ihn rückt. */
  const spawnBaseRect = useCallback((node: Node<NoteNodeData>) => {
    const start = spawnStartRef.current?.get(node.id)
    return { ...(start ?? node.position), ...sizeOf(node) }
  }, [sizeOf])

  const spawnTargetFor = useCallback((noteId: string, side: SpawnSide) => {
    const origin = nodesRef.current.find(n => n.id === noteId)
    if (!origin) return null
    const { note } = origin.data
    const originRect = spawnBaseRect(origin)
    // Die Notiz behält seine Maße (Quadrat oder Rechteck); das Memo ihre
    // Breite — ihre Höhe kommt aus dem Inhalt, und der ist noch leer.
    const size = note.kind === 'memo'
      ? { width: originRect.width, height: getPlacementSize('memo').height }
      : { width: originRect.width, height: originRect.height }
    /*
      **Bereiche sind für die Ordnungslogik unsichtbar** (CONTEXT.md): Sie sind
      kein Glied einer Reihe — sonst schöbe ein Anlegepunkt eine ganze Karte
      beiseite. Ihre Zettel sind es sehr wohl, jeder in seiner eigenen Welt.
    */
    const others = nodesRef.current
      .filter(n => n.data?.note?.kind !== 'area')
      .map(n => ({ id: n.id, rect: spawnBaseRect(n), areaId: n.data?.note?.areaId ?? null, taped: Boolean(n.data?.note?.tape) }))
    const plan = getInsertPlan(
      { id: noteId, rect: originRect, areaId: note.areaId ?? null },
      side,
      size,
      others,
    )
    // Kein Raum: An der Kante steht eine Wand, und rücken darf sie nicht. Der
    // Punkt bleibt dann still — lieber gar nichts als ein Zettel auf einem
    // fremden.
    if (plan.blocked) return null
    return { note, plan }
  }, [spawnBaseRect])

  /**
   * Die Reihe rücken — für die Vorschau, ohne die Datenbank anzufassen.
   *
   * Erst alles auf die eingefrorene Ausgangslage zurück, dann die neuen
   * Positionen darüber: Wandert der Zeiger von einer Kante zur nächsten, muss
   * die vorige Reihe an ihren Platz zurück — sonst blieben Zettel auf halbem
   * Weg liegen. Dasselbe Vorgehen wie beim **Platz tauschen**.
   */
  const applySpawnMoves = useCallback((moves: readonly InsertMove[] | null) => {
    const back = spawnStartRef.current
    if (!back && !moves) return
    // Die Geste geht weiter — ein Wecker, der die Klasse gleich abnähme, ist
    // hinfällig: Der nächste Weg soll auch wieder gleiten.
    if (shiftTimerRef.current) {
      clearTimeout(shiftTimerRef.current)
      shiftTimerRef.current = null
    }
    setNodes(ns => ns.map(node => {
      const move = moves?.find(m => m.id === node.id)
      const position = move ? { x: move.x, y: move.y } : back?.get(node.id)
      if (!position) return node
      if (node.position.x === position.x && node.position.y === position.y) return node
      // Wer steht, bekommt die Klasse nicht: Nur wer sich bewegt, soll gleiten.
      return { ...node, position, className: SHIFT_CLASS }
    }))
  }, [setNodes])

  /**
   * Das Gleiten ist zu Ende — nach seiner Dauer nimmt die Klasse sich wieder
   * ab. Bliebe sie stehen, zöge das nächste Ziehen desselben Zettels nach.
   */
  const endShift = useCallback(() => {
    if (shiftTimerRef.current) clearTimeout(shiftTimerRef.current)
    shiftTimerRef.current = setTimeout(() => {
      shiftTimerRef.current = null
      setNodes(ns => ns.some(n => n.className === SHIFT_CLASS)
        ? ns.map(n => (n.className === SHIFT_CLASS ? { ...n, className: undefined } : n))
        : ns)
    }, SPAWN_ANIM_MS)
  }, [setNodes])

  const handleSpawnHover = useCallback((noteId: string, side: SpawnSide | null) => {
    const target = side ? spawnTargetFor(noteId, side) : null
    const previous = spawnStartRef.current

    // Die Ausgangslage der neu Gerückten — aus der alten Karte, falls sie schon
    // darin steht (sonst wäre eine schon verschobene Lage der „Ursprung").
    const starts = new Map<string, XYPosition>()
    for (const move of target?.plan.moves ?? []) {
      const node = nodesRef.current.find(n => n.id === move.id)
      const origin = previous?.get(move.id) ?? node?.position
      if (origin) starts.set(move.id, { x: origin.x, y: origin.y })
    }

    applySpawnMoves(target?.plan.moves ?? null)
    // Der Zeiger ist vom Punkt herunter — die Geste ist zu Ende, das Gleiten
    // läuft noch. Beim WECHSEL der Kante nicht: Dort soll die Reihe weiter
    // gleiten, statt zwischendurch zu springen.
    if (!target) endShift()
    spawnStartRef.current = target ? starts : null
    spawnPreviewRef.current = Boolean(target)
    // Ein übernommenes Bild überlebt das Verlassen des Punkts: Es wartet auf
    // seinen Zettel, nicht auf den Zeiger.
    setSpawnPreview(previous => target
      ? { rect: target.plan.rect, kind: target.note.kind as 'note' | 'memo', color: target.note.color, committed: false }
      : previous?.committed ? previous : null)
  }, [spawnTargetFor, applySpawnMoves, endShift])

  /** Die Vorschau zurücknehmen — Zeiger weg vom Punkt, Escape, Auswahlwechsel. */
  const cancelSpawnPreview = useCallback(() => {
    if (!spawnPreviewRef.current) return
    applySpawnMoves(null)
    endShift()
    spawnStartRef.current = null
    spawnPreviewRef.current = false
    setSpawnPreview(previous => (previous?.committed ? previous : null))
  }, [applySpawnMoves, endShift])

  /*
    Wie `placeNoteAt`, nur mit Lage und Eigenschaften vom Original: Ein LEERER
    Zettel derselben Art in derselben Farbe und Schrift, mit derselben
    Autor-Anzeige und demselben Titel-Schalter — ohne Inhalt, Emoji oder
    Aufgabe. Er wird AUSGEWÄHLT, nicht bearbeitet: Wie beim Duplizieren
    wandert die Auswahl auf den neuen Zettel (`pendingDuplicatesRef`), sobald
    die Live-Query ihn meldet — wer schreiben will, tippt oder doppelklickt.

    **Anlegen und Rücken sind EIN Schritt** für das Rückgängig: Sie sind eine
    Handlung, auch wenn sie mehrere Zeilen schreiben — dieselbe Haltung wie
    beim **Platz tauschen**.
  */
  const handleSpawn = useCallback((noteId: string, side: SpawnSide) => {
    if (gestureRef.current) return
    if (pendingDuplicatesRef.current) return
    const target = spawnTargetFor(noteId, side)
    if (!target) return
    const { note, plan } = target
    // Die Lage VOR der Vorschau: Sie steht in der eingefrorenen Karte, und wo
    // nicht (Klick ohne vorheriges Überfahren), ist die Lage des Nodes selbst
    // schon die richtige.
    const before = spawnStartRef.current

    const id = crypto.randomUUID()
    const at = new Date()
    // Auch ohne Überfahren muss die Reihe rücken — der Klick ist der Auftrag,
    // die Vorschau war nur das Versprechen.
    applySpawnMoves(plan.moves)
    endShift()
    spawnStartRef.current = null
    spawnPreviewRef.current = false
    /*
      Das Bild bleibt und wird undurchsichtig — es hält den Platz, bis der
      echte Zettel eintrifft (aufgelöst im `pendingDuplicatesRef`-Effekt). Auch
      ohne vorheriges Überfahren gesetzt: Dort ist die Lücke genauso da.
    */
    // Anlegepunkte hat nur, was Papier ist — ein Bereich kommt hier nie an.
    setSpawnPreview({ rect: plan.rect, kind: note.kind as 'note' | 'memo', color: note.color, committed: true })
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current)
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null
      setSpawnPreview(previous => (previous?.committed ? null : previous))
    }, SPAWN_COMMIT_TIMEOUT)
    pendingDuplicatesRef.current = [id]
    focusSpawnedRef.current = true
    recordStep(step => {
      step.created(id, at)
      for (const move of plan.moves) {
        const start = before?.get(move.id) ?? nodesRef.current.find(n => n.id === move.id)?.position
        if (!start) continue
        step.update(move.id, { x: start.x, y: start.y }, { x: move.x, y: move.y })
        void dataSource.updateCanvasNote(move.id, { x: move.x, y: move.y })
      }
      // Nach ⌘Z ist der neue Zettel weg — die Auswahl geht auf den, an dem
      // angelegt wurde, nicht auf die gerückte Reihe (CONTEXT.md, Rückgängig).
      step.select([noteId])
    })
    void dataSource.createCanvasNote({
      id,
      projectId,
      kind: note.kind,
      content: '',
      x: plan.rect.x,
      y: plan.rect.y,
      width: plan.rect.width,
      height: plan.rect.height,
      color: note.color,
      font: note.font,
      showAuthor: note.showAuthor,
      // Ein eingeschalteter Titel ist ein leerer String, kein `null`.
      title: note.title === null ? null : '',
    })
  }, [spawnTargetFor, applySpawnMoves, endShift, projectId, recordStep, dataSource])

  /*
    ── Der **Klebestreifen** ────────────────────────────────────────────────

    **Ankleben legt den Zettel weg, Ablösen gibt ihn zurück in die Hand.**

    Ankleben: Der Streifen kommt drauf, die Auswahl fällt weg, nichts ist
    danach angetippt — man hat ihn ja gerade weggelegt. Bei einer Auswahl
    mehrerer trifft es alle auf einmal, als EIN Schritt: Ein ⌘Z löst sie alle
    wieder ab.

    Ablösen: Erst rollt sich der Streifen zurück (`peelingId`), dann fällt die
    Zeile, und der Zettel steht ausgewählt da — aus dem einen Knopf werden die
    sechs. Man löst ab, um weiterzuarbeiten.

    Der **Bereich** bekommt keinen Streifen: Was ein geklebter Bereich für
    seine Mitglieder bedeutete, ist eine eigene Frage.
  */
  /*
    Klick auf den **Klebestreifen**: Der Zettel wird **angetippt** — und die
    Auswahl daneben fällt weg.

    **Es ist immer nur EINES gemeint.** Angetippt und ausgewählt sind zwei
    Zustände, aber sie beantworten dieselbe Frage: „Wovon redet die Leiste?"
    Stünden beide zugleich, stünden auch zwei Leisten zugleich — und genau das
    war zu sehen (gemeldet 2026-09-06: ein ausgewählter Zettel, daneben ein
    geklebter angetippt, beide Leisten offen).

    Die Gegenrichtung gibt es schon: Ein Klick auf einen anderen Zettel nimmt
    das Angetippte zurück (`handleNodeClick`), ein Klick daneben ebenso
    (`handlePaneClick`), und **Tab** setzt beim Weitergehen genau eines von
    beiden.
  */
  const handleTapeClick = useCallback((noteId: string) => {
    setTappedId(current => (current === noteId ? null : noteId))
    setNodes(ns => (ns.some(n => n.selected) ? ns.map(n => (n.selected ? { ...n, selected: false } : n)) : ns))
  }, [setNodes])

  /*
    Die zuletzt gezogene Erscheinung — sie kommt beim nächsten Ankleben nicht
    wieder dran (`drawCanvasTape`). **Einer für den ganzen Canvas und nicht je
    Zettel:** Der Fall, um den es geht, ist „ablösen, neu ankleben, gefällt mir
    immer noch nicht" — und dabei ist der letzte Wurf immer der desselben
    Zettels. Beim Sammel-Ankleben trägt derselbe Merker nebenbei, dass keine
    zwei aufeinanderfolgende gleich aussehen.

    Ein Ref und kein Zustand: Niemand rendert daraufhin neu.
  */
  const lastTapeRef = useRef<string | null>(null)

  const handleTape = useCallback((noteId?: string) => {
    const notes = noteId
      ? nodesRef.current.filter(n => n.id === noteId).map(n => n.data?.note).filter(Boolean) as CanvasNote[]
      : selectedNotesOf(nodesRef.current)
    // Geklebt wird Papier — der **Text** hat keins (CONTEXT.md, Text).
    const taping = notes.filter(note => note.kind !== 'area' && note.kind !== 'text' && !note.tape)
    if (taping.length === 0) return
    recordStep(step => {
      for (const note of taping) {
        // Gezogen, EINMAL, und nie dasselbe zweimal hintereinander: Wer ablöst
        // und neu anklebt, zieht ein ANDERES Stück aus der Schachtel. Das
        // Ergebnis steht danach in der Zeile.
        const after = { tape: drawCanvasTape(note.kind, lastTapeRef.current) }
        lastTapeRef.current = after.tape
        step.update(note.id, { tape: note.tape }, after)
        trackNoteWrite(dataSource.updateCanvasNote(note.id, after))
      }
    })
    setTappedId(null)
    // Von Hand abwählen statt über `clearSelection`: Das steht weiter unten,
    // und dieser Block muss VOR `nodeHandlers` stehen — die Rückrufe gehen von
    // dort an die Nodes.
    setNodes(ns => (ns.some(n => n.selected) ? ns.map(n => (n.selected ? { ...n, selected: false } : n)) : ns))
  }, [recordStep, trackNoteWrite, setNodes, dataSource])

  const [peelingId, setPeelingId] = useState<string | null>(null)
  const peelingRef = useRef<string | null>(null)
  /** Der Streifen, der gerade abgezogen wird — die Erscheinung, nicht der Zettel. */
  const peeledTapeRef = useRef<string | null>(null)
  const peelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (peelTimerRef.current) clearTimeout(peelTimerRef.current)
  }, [])

  /*
    **Abgezogen bleibt abgezogen, bis der Streifen aus dem Bild ist.**

    Die Bewegung ist durch, die Zeile ist geschrieben — aber sie kommt erst
    über die Live-Query zurück, und bis dahin trägt der Zettel sein `tape`
    noch. Fiele `peelingId` schon mit dem Schreiben, stünde der Streifen in
    diesen Frames wieder als ganz normaler geklebter da. Weil sich damit der
    NAME seiner Animation ändert (`peel-off` → `roll-on`), startet der Browser
    das **Ankleben** neu: Der eben abgelöste Streifen blitzt kurz auf, bevor er
    verschwindet (gemeldet 2026-09-06).

    Die zwei Zustände dürfen also nicht aneinander stoßen, sie müssen sich
    ÜBERLAPPEN. Losgelassen wird deshalb NICHT nach einer Zeit, sondern an der
    einzigen Tatsache, die zählt: Der Streifen, den wir abgezogen haben, liegt
    nicht mehr dort. `forwards` hält ihn solange am Ende seiner Bewegung — also
    unsichtbar.

    Der Vergleich geht über die ERSCHEINUNG und nicht über „hat überhaupt
    einen": Wer mitten im Abziehen den Streifen wechselt, bekommt einen neuen —
    und der soll sich ankleben, nicht als abgezogen gelten.

    An `notes` und nicht an den Nodes: Das ist die Live-Query selbst, und der
    Node-Abgleich hängt ohnehin an ihr.
  */
  useEffect(() => {
    if (!peelingId) return
    const note = notes.find(n => n.id === peelingId)
    if (note?.tape !== peeledTapeRef.current) setPeelingId(null)
  }, [peelingId, notes])

  /**
   * **Streifen wechseln** — ein anderes Stück aus der Schachtel, nie dasselbe
   * wie das aktuelle.
   *
   * Der Zettel bleibt **angetippt**: Man wechselt, sieht, wechselt weiter. Ein
   * Knopf, der sich nach dem Drücken selbst wegnimmt, wäre kein Wähler.
   *
   * **Ausgenommen wird der Streifen, der GERADE draufliegt** — nicht der zuletzt
   * gezogene: Beim Wechseln ist die Frage „was liegt hier", nicht „was habe ich
   * zuletzt gezogen". Bei zwei Klicks hintereinander ist es ohnehin dasselbe.
   */
  const handleRetape = useCallback((noteId: string) => {
    const note = nodesRef.current.find(n => n.id === noteId)?.data?.note
    if (!note?.tape) return
    // `retapeCanvasTape` und nicht `drawCanvasTape`: Was die Hand am Winkel
    // gesetzt hat, gehört ihr — gewechselt werden dann Muster und Lage (ADR 0013).
    const after = { tape: retapeCanvasTape(note.kind, note.tape) }
    lastTapeRef.current = after.tape
    recordStep(step => {
      step.update(noteId, { tape: note.tape }, after)
      trackNoteWrite(dataSource.updateCanvasNote(noteId, after))
    })
  }, [recordStep, trackNoteWrite, dataSource])

  /**
   * **Neigung ziehen** — die Raste, auf der ein Zug am Streifen geendet ist
   * (CONTEXT.md, Klebestreifen).
   *
   * Der Gegensatz zu `handleRetape`: Der wechselt das Stück, dieser zielt. Und
   * er setzt das Merkmal — ab jetzt gehört der Winkel der Hand, und kein Würfel
   * rührt ihn mehr an (ADR 0013).
   *
   * EIN Schritt für den ganzen Zug: Die Meldung kommt erst beim Loslassen.
   */
  const handleTapeAngle = useCallback((noteId: string, angle: number) => {
    const note = nodesRef.current.find(n => n.id === noteId)?.data?.note
    const look = readCanvasTape(note?.tape ?? null)
    if (!note?.tape || !look || look.angle === angle) return
    const after = { tape: writeCanvasTape({ ...look, angle, byHand: true }) }
    recordStep(step => {
      step.update(noteId, { tape: note.tape }, after)
      trackNoteWrite(dataSource.updateCanvasNote(noteId, after))
    })
  }, [recordStep, trackNoteWrite, dataSource])

  /** **Ankleben** für die ganze Auswahl — der Knopf der Gruppen-Leiste. */
  const handleBulkTape = useCallback(() => { handleTape() }, [handleTape])

  /*
    **Das Abziehen ist durch — jetzt fällt die Zeile.**

    Gemeldet vom Streifen selbst (`CanvasTape`, `onPeeled`), sobald seine
    Bewegung wirklich fertig ist. Eine Uhr täte es nicht: Sie startete im Klick,
    die Animation aber erst, wenn der Stil steht — einen Frame später. Der
    Streifen verschwand deshalb kurz vor dem Ende seiner Bewegung (gemeldet
    2026-09-06).

    `peelingRef` sperrt das zweite Mal: Meldung und Sicherheitsnetz können beide
    kommen, fallen darf die Zeile nur einmal.
  */
  const finishPeel = useCallback((noteId: string) => {
    if (peelingRef.current !== noteId) return
    peelingRef.current = null
    if (peelTimerRef.current) {
      clearTimeout(peelTimerRef.current)
      peelTimerRef.current = null
    }
    const note = nodesRef.current.find(n => n.id === noteId)?.data?.note
    setTappedId(null)
    /*
      `peelingId` bleibt stehen — es fällt oben, wenn der Streifen wirklich weg
      ist. Hier fiele es einen Schreibvorgang zu früh.
    */
    if (!note?.tape) {
      setPeelingId(null)
      return
    }
    recordStep(step => {
      step.update(noteId, { tape: note.tape }, { tape: null })
      trackNoteWrite(dataSource.updateCanvasNote(noteId, { tape: null }))
    })
    // Zurück in die Hand: Der Zettel steht ausgewählt da, und die Leiste, die
    // eben zwei Knöpfe trug, füllt sich.
    setNodes(ns => ns.map(n => (n.id === noteId ? { ...n, selected: true } : (n.selected ? { ...n, selected: false } : n))))
  }, [recordStep, trackNoteWrite, setNodes, dataSource])

  const handleUntape = useCallback((noteId: string) => {
    const note = nodesRef.current.find(n => n.id === noteId)?.data?.note
    if (!note?.tape) return
    peelingRef.current = noteId
    peeledTapeRef.current = note.tape
    setPeelingId(noteId)
    /*
      Das Sicherheitsnetz, großzügig bemessen: Meldet der Streifen nichts — er
      wurde mitten in der Bewegung aus dem Bild genommen —, bliebe der Zettel
      sonst für immer geklebt und angetippt. Es soll NIE der Weg sein, auf dem
      die Zeile fällt; deshalb ein Vielfaches der Bewegung und keine knappe
      Zugabe.
    */
    if (peelTimerRef.current) clearTimeout(peelTimerRef.current)
    peelTimerRef.current = setTimeout(() => finishPeel(noteId), CANVAS_TAPE_PEEL_MS * 4)
  }, [finishPeel])

  const nodeHandlers = useMemo(() => ({
    onResizeStart: handleResizeStart,
    onEdit: handleNoteEdit,
    onDelete: handleNoteDelete,
    onOpenTask: handleOpenTask,
    onSpawnHover: handleSpawnHover,
    onSpawn: handleSpawn,
    onEditingChange: handleEditingChange,
    onFrameGrab: handleFrameGrab,
    onApply: onApplyNote,
    onFitToContent: handleFitToContent,
    onTapeClick: handleTapeClick,
    onTape: handleTape,
    onRetape: handleRetape,
    onUntape: handleUntape,
    onPeeled: finishPeel,
    onAngle: handleTapeAngle,
    onEmptied: handleTextEmptied,
  }), [handleResizeStart, handleNoteEdit, handleNoteDelete, handleOpenTask, handleSpawnHover, handleSpawn, handleEditingChange, onApplyNote, handleFitToContent, handleFrameGrab, handleTapeClick, handleTape, handleRetape, handleUntape, finishPeel, handleTapeAngle, handleTextEmptied])

  useEffect(() => {
    /*
      Die noch offenen Feldänderungen aufräumen, sobald die Live-Query sie
      zurückgebracht hat (siehe `pendingEditsRef`). Was noch aussteht, bleibt
      liegen — der Schreibvorgang ist ja noch unterwegs.

      HIER und nicht in der Updater-Funktion unten: Die läuft nicht
      zuverlässig genau einmal (React darf sie erneut aufrufen), und ein
      Seiteneffekt gehört ohnehin nicht hinein.
    */
    if (pendingEditsRef.current.size > 0) {
      const byNote = new Map(notes.map(n => [n.id, n as unknown as Record<string, unknown>]))
      for (const [id, pending] of pendingEditsRef.current) {
        const note = byNote.get(id)
        if (!note) { pendingEditsRef.current.delete(id); continue }
        const landed = Object.entries(pending).every(([key, value]) => note[key] === value)
        if (landed) pendingEditsRef.current.delete(id)
      }
    }

    setNodes(current => {
      const byId = new Map(current.map(node => [node.id, node]))
      let changed = current.length !== notes.length

      // **Vorn und hinten** (`compareZ`): erst die Bereiche, dann die Zettel,
      // darin nach `zOrder` bzw. Alter. Der Index ist der `zIndex` — und weil
      // die Auswahl nicht mehr anhebt (`elevateNodesOnSelect`), bleibt ein
      // angeklickter Bereich auch ohne Versatz unter seinen Zetteln.
      const ordered = [...notes].sort(compareZ)
      const next = ordered.map((note, index) => {
        const existing = byId.get(note.id)
        // Das getragene Element behält seine Höhe. Der Abgleich vergäbe sonst
        // wieder seinen Stapelplatz und ließe es mitten in der Geste hinter
        // seinen Nachbarn fallen — die Live-Query feuert währenddessen ja
        // weiter.
        const zIndex = note.id === swapLiftedRef.current ? SWAP_LIFTED_Z : index
        const rebuilt = noteToFlowNode(note, autoEditNoteId, tool === 'select', zIndex, nodeHandlers, editRequest, placementMode !== null)
        if (
          existing
          && existing.data.note === note
          && existing.data.startEditing === rebuilt.data.startEditing
          && existing.data.editingEnabled === rebuilt.data.editingEnabled
          && existing.data.editRequest === rebuilt.data.editRequest
          && existing.zIndex === rebuilt.zIndex
          && existing.style === rebuilt.style
        ) {
          return existing
        }
        changed = true
        /*
          Das gerade GEZOGENE Element behält Maße und Lage.

          Der Neubau trägt `width`/`height`/`position` aus der Datenbank, und
          dort steht während eines Zugs noch der Stand von VOR dem Zug. Die
          Live-Query feuert aber mittendrin: Wird eine **Notiz** breiter,
          bricht ihr Text neu um, die gemessene Höhe ändert sich und wird
          geschrieben (`useMeasuredNoteHeight`) — die Abfrage meldet ein neues
          Notiz-Objekt, der Abgleich baut neu, und die gezogene Breite ist
          fort. Von außen sah das aus, als spränge die Notiz bei stillstehendem
          Zeiger auf ihre alte Breite zurück, und zwar umso weiter, je weiter
          man gezogen hatte.

          Dieselbe Ausnahme hat das getragene Element beim **Platz-Tausch**
          schon (`swapLiftedRef` oben) und aus demselben Grund: Was eine Geste
          gerade in der Hand hat, gehört ihr bis zum Loslassen.

          Der Inhalt geht trotzdem mit: Nur die Geometrie bleibt stehen, `data`
          kommt wie sonst aus dem Neubau — eine Farbänderung aus der Cloud ist
          also auch mitten im Zug zu sehen.
        */
        if (existing && resizeRef.current?.id === note.id) {
          return { ...existing, data: rebuilt.data, zIndex: rebuilt.zIndex }
        }
        /*
          Dasselbe für die LAGE beim Verschieben. Meldet die Live-Query den
          gezogenen Zettel mitten im Zug neu — das Sync-Echo markiert ihn
          `synced`, eine andere Schreibstelle trifft ihn —, trüge der Neubau die
          Lage aus der Datenbank: den Stand vor dem Zug. Gemessen (E2E
          `canvas-drag-db-echo`): Der Zettel stand ein Bild lang auf seiner
          Quelle, im nächsten holte React Flow ihn wieder unter den Zeiger.

          Zwei Wege bewegen einen Zettel, und beide werden gehalten:

          - **React Flows Ziehen** (ein nicht ausgewählter Zettel): was es als
            gezogen markiert, und was der Zug selbst trägt (`moving`, die
            Mitglieder eines Bereichs in `carried` — die kommen OHNE
            `dragging`-Fahne, siehe `handleNodesChange`).
          - **Die eigenen Gesten** (`gestureHeldRef`): **Platz tauschen** — der
            Normalfall, denn ein ausgewählter Zettel wird über sie gezogen und
            ohne Ableg-Fläche frei **verschoben** — und die Gesten am Rahmen
            einer Mehrfachauswahl. Sie schreiben Lage UND Maße direkt in die
            Nodes, ohne `dragging` und ohne `dragRef`; an ihnen gingen die
            ersten beiden Versuche vorbei. Gehalten wird hier auch die Größe,
            denn die Rahmen-Geste skaliert.

          Nach dem Loslassen gilt wieder die Datenbank.
        */
        const held = dragRef.current
        if (existing && (existing.dragging || held?.moving.has(note.id) || held?.carried?.has(note.id))) {
          return { ...existing, ...rebuilt, data: rebuilt.data, position: existing.position }
        }
        if (existing && gestureHeldRef.current?.has(note.id)) {
          return {
            ...existing,
            ...rebuilt,
            data: rebuilt.data,
            position: existing.position,
            width: existing.width,
            height: existing.height,
            measured: existing.measured,
          }
        }
        // Auswahl und Position gehören React Flow, nicht der Datenbank —
        // beim Ersetzen eines Nodes müssen sie mitwandern.
        return existing ? { ...existing, ...rebuilt, data: rebuilt.data } : rebuilt
      })

      // Gegenstück zum Log in `useCanvasNotes`: Meldet die Live-Query
      // unverändert viele Notizen, während hier die Node-Zahl fällt, liegt der
      // Verlust an diesem Abgleich — sonst an der Abfrage.
      if (isDev && next.length < current.length) {
        console.warn(`[Canvas/nodes] Abgleich: ${next.length} Nodes aus ${notes.length} Notizen (vorher ${current.length})`)
      }

      return changed ? next : current
    })
  }, [notes, setNodes, autoEditNoteId, tool, nodeHandlers, editRequest, placementMode])

  // Der Wechsel zum Hand-Werkzeug hebt die Auswahl auf. Sonst bliebe die
  // Options-Leiste einer ausgewählten Notiz bedienbar — sie hängt an
  // `selected` und rendert per Portal AUSSERHALB des Node-Baums, wird von
  // dessen `pointer-events: none` also gar nicht erfasst.
  //
  // Der `some`-Wächter ist kein Zierrat: ohne ihn erzeugte jeder
  // Werkzeugwechsel eine neue Array-Identität und damit ein Neuzeichnen
  // sämtlicher Notizen, auch wenn nichts ausgewählt war.
  useEffect(() => {
    if (tool !== 'hand') return
    setNodes(ns => (ns.some(n => n.selected)
      ? ns.map(n => (n.selected ? { ...n, selected: false } : n))
      : ns))
  }, [tool, setNodes])

  /**
   * Der Griff bestimmt, was sich bewegt — und das muss hier entschieden
   * werden, weil React Flow es zu früh entscheidet.
   *
   * `XYDrag.startDrag` (@xyflow/system) meldet erst die neue Auswahl
   * (`onNodeMouseDown` → `addSelectedNodes`) und liest unmittelbar danach
   * `nodeLookup`, um seine Zieh-Menge zu bilden. Die Meldung läuft aber über
   * React-State; `nodeLookup` erfährt davon erst nach dem nächsten Render.
   * Wer einen NICHT ausgewählten Notizen anfasst, während ein anderer noch
   * ausgewählt ist, zieht deshalb beide — und der andere fällt zugleich als
   * Ausrichtungspartner aus. Sichtbar wurde das an den Abstandsklammern: Sie
   * blieben beim ersten Anfassen aus und kamen erst beim zweiten.
   *
   * Die Absicht ist dabei nicht strittig, React Flows eigenes
   * `handleNodeClick` schreibt sie fest:
   *
   * | Griff | Auswahl danach |
   * |---|---|
   * | auf ein **nicht** ausgewähltes Element | nur dieses |
   * | auf ein **ausgewähltes** Element | unverändert |
   * | mit ⇧/⌘/Strg (`multiSelectionKeyCode`) | Auswahl **plus** dieses |
   *
   * Genau diese Regel steht unten. Sie stützt sich auf `nodesRef.current` —
   * die Auswahl VOR dem Griff, dieselbe, die `handleNodeClick` gelesen hat.
   *
   * Nicht stattdessen früher auswählen: Ein `addSelectedNodes` im
   * `pointerdown` machte `nodeLookup` nur dann rechtzeitig frisch, wenn React
   * seine passiven Effekte (der Node-Abgleich von React Flow ist einer)
   * zwischen `pointerdown` und dem ersten `pointermove` abarbeitet. Bei einem
   * bedächtigen Griff tut es das, bei einem schnellen womöglich nicht — also
   * wieder ein Fehler, der „häufig" auftritt.
   */
  /**
   * **Klick in der Mehrfachauswahl** löst die Gruppe auf und wählt nur das
   * geklickte Element (CONTEXT.md, „Klick oder Ziehen"). React Flow selbst
   * ließe sie stehen — `handleNodeClick`: „schon ausgewählt → unverändert" —
   * und mit ⇧/⌘/Strg bleibt es dabei, dann gehört der Klick der Mehrfachwahl.
   *
   * Nur der KLICK, nie das Ziehen: `onNodeClick` kommt erst beim Loslassen
   * ohne Bewegung (`nodeClickDistance`). Wer ein Mitglied anfasst und zieht,
   * bewegt weiter die ganze Gruppe (`handleNodeDragStart`).
   */
  const handleNodeClick = useCallback((event: React.MouseEvent, node: { id: string }) => {
    // Wer einen anderen Zettel anfasst, meint den **angetippten** nicht mehr.
    // Der geklebte selbst meldet hier nichts: Seine Fläche nimmt keine
    // Pointer-Events an, und sein Streifen hält den Klick auf.
    if (tappedRef.current && tappedRef.current !== node.id) setTappedId(null)
    if (event.shiftKey || event.metaKey || event.ctrlKey) return
    const selected = nodesRef.current.filter(n => n.selected)
    if (selected.length < 2) return
    storeApi.getState().addSelectedNodes([node.id])
  }, [storeApi])

  const handleNodeDragStart = useCallback<OnNodeDrag<Node<NoteNodeData>>>((event, node) => {
    const selected = nodesRef.current.filter(n => n.selected).map(n => n.id)
    const wasSelected = selected.includes(node.id)
    // Dieselben Tasten wie `multiSelectionKeyCode` weiter unten.
    const multi = Boolean(event && (event.shiftKey || event.metaKey || event.ctrlKey))
    const moving = new Set(wasSelected || multi ? [...selected, node.id] : [node.id])
    // Ein Bereich TRÄGT, was in ihm liegt (CONTEXT.md): Die Mitglieder eines
    // gezogenen Bereichs fahren mit — starr, ohne Umsortieren. Nicht in
    // `moving` (das bewegt React Flow selbst), sondern daneben.
    const carried = new Map<string, string>()
    const movingAreas = new Set(
      nodesRef.current.filter(n => moving.has(n.id) && n.data?.note?.kind === 'area').map(n => n.id),
    )
    for (const n of nodesRef.current) {
      const areaId = n.data?.note?.areaId
      if (areaId && movingAreas.has(areaId) && !moving.has(n.id)) carried.set(n.id, areaId)
    }

    // Die Partner einmal je Ziehvorgang einsammeln (siehe
    // `collectAlignTargets`). Ausgenommen ist alles, was mitwandert — an sich
    // selbst richtet sich nichts aus.
    collectAlignTargets(new Set([...moving, ...carried.keys()]), movingAreas.size > 0)
    gestureRef.current = true

    // Alt EINMAL festhalten, nicht fortlaufend abfragen. Wer sie unterwegs
    // drückt, dupliziert nicht; wer sie unterwegs loslässt, dupliziert
    // trotzdem. Figma entscheidet fortlaufend — das hieße, die Kopie mitten im
    // Ziehen anzulegen und wieder zu löschen, mitsamt den Sync-Einträgen dafür.
    //
    // Die Ausgangslage ALLER Nodes, nicht nur der hier gezogenen: welche Menge
    // React Flow wirklich bewegt, steht erst in `handleNodesChange` fest (siehe
    // dort). Ein Map-Aufbau je Geste ist dafür der günstigere Preis.
    dragRef.current = {
      alt: Boolean(event?.altKey),
      moving,
      origins: new Map(nodesRef.current.map(n => [n.id, { ...n.position }])),
      done: false,
      copies: new Map(),
      carried,
    }
  }, [collectAlignTargets])

  /**
   * Der Eingriff zwischen React Flows Rechnung und dem Node-State.
   *
   * React Flows eigenes `snapToGrid` ist bewusst AUS: es rundet die Position
   * schon in `updateNodes` (@xyflow/system), also bevor dieser Handler sie zu
   * sehen bekommt. Eine Notiz ließe sich dann nie auf eine Kante legen, die
   * nicht zufällig auf dem Raster liegt — die Ausrichtung könnte das Raster
   * nicht schlagen. Dasselbe gilt beim Ziehen an einer Ecke: dort läse
   * `XYResizer` denselben Schalter aus dem Store. Alle Regeln liegen deshalb
   * hier.
   */
  const handleNodesChange = useCallback((incoming: NodeChange<Node<NoteNodeData>>[]) => {
    // Ein nur gestreifter Bereich bleibt draußen (siehe das Abo auf das
    // Rechteck oben); sein Abwählen läuft weiter durch.
    const marquee = storeApi.getState().userSelectionActive
    const changes = marquee
      ? incoming.filter(c => !(c.type === 'select' && c.selected
          && nodesRef.current.find(n => n.id === c.id)?.data?.note?.kind === 'area'))
      : incoming
    /*
      Über die Refs, nicht über die Abhängigkeitsliste — siehe `guidesRef`.
      `byId` einmal je Änderungspaket statt `nodes.find` je Änderung: Bei einer
      Mehrfachauswahl liefe die Suche sonst k-mal über alle Nodes, je Frame.
    */
    const nodes = nodesRef.current
    const guides = guidesRef.current
    const measures = measuresRef.current
    const byId = new Map(nodes.map(n => [n.id, n]))
    // Zuerst das Ziehen an einer Ecke: seine Änderungen tragen KEIN `dragging`
    // und fielen sonst in den Zweig für „nichts wird gezogen".
    const sizing = changes.find(
      (c): c is Extract<NodeChange<Node<NoteNodeData>>, { type: 'dimensions' }> =>
        c.type === 'dimensions' && c.dimensions !== undefined
    )
    const resize = resizeRef.current
    if (sizing && resize && resize.id === sizing.id) {
      const node = byId.get(sizing.id)

      if (sizing.resizing === false) {
        // Loslassen. Auch hier kommt React Flows eigene Rechnung zurück
        // (`prevValues`), nicht unsere — deshalb die gemerkten Werte
        // einsetzen und DIESE festschreiben.
        const settled = lastResizeRef.current
        resizeRef.current = null
        lastResizeRef.current = null
        if (guides !== NO_GUIDES) setGuides(NO_GUIDES)
        if (measures !== NO_MEASURES) setMeasures(NO_MEASURES)
        clearAdoption()
        if (!settled) {
          onNodesChange(changes)
          return
        }
        // Die Ausgangsgröße steht seit dem Anfassen in `resizeRef`.
        const before = resize.start
        /*
          Beim **Text** wächst an den Ecken die SCHRIFT, im Verhältnis der
          Höhen — die Box folgt ihr (CONTEXT.md, Text). Im selben Schritt wie
          die Lage: Ein ⌘Z nimmt beides zusammen zurück.
        */
        const textSize = resize.kind === 'text' && resize.fontSize !== undefined && before
          ? (() => {
            const fontSize = scaleCanvasTextSize(resize.fontSize, before.height, settled.height)
            // Eigene Größen einzelner Stellen im selben Verhältnis — sonst
            // wüchse der Rest und die Hervorhebung bliebe klein.
            const content = resize.content === undefined ? undefined : scaleCanvasTextSizes(resize.content, fontSize / resize.fontSize)
            const contentChanged = content !== undefined && content !== resize.content
            return {
              before: { fontSize: resize.fontSize, ...(contentChanged ? { content: resize.content } : {}) },
              after: { fontSize, ...(contentChanged ? { content } : {}) },
            }
          })()
          : null
        if (before) {
          recordStep(step => step.update(
            sizing.id,
            { width: before.width, height: before.height, x: before.x, y: before.y, ...textSize?.before },
            { width: settled.width, height: settled.height, x: settled.x, y: settled.y, ...textSize?.after },
          ))
        }
        void dataSource.updateCanvasNote(sizing.id, {
          width: settled.width,
          height: settled.height,
          x: settled.x,
          y: settled.y,
          ...textSize?.after,
        })
        if (textSize) rememberNoteDefaults('text', textSize.after)
        if (resize.kind === 'area') {
          // Wer beim Verkleinern herausfällt, fällt still heraus; wer beim
          // Vergrößern darunter gerät, gehört dazu (ADR 0012). Die frische
          // Größe steht noch nicht im Node — als Lage vormerken.
          lastPositionRef.current.set(sizing.id, { x: settled.x, y: settled.y })
          recordStep(step => settleAreas(step, null))
          lastPositionRef.current.delete(sizing.id)
        }
        // `setAttributes` gehört dazu: ohne das schreibt React Flow nur
        // `measured` und die Maße am Node fielen auf den alten Stand zurück
        // (`applyChange` in @xyflow/react).
        onNodesChange([{
          ...sizing,
          setAttributes: true,
          dimensions: { width: settled.width, height: settled.height },
        }])
        return
      }

      /*
        Nicht jede Größenänderung kommt vom Zeiger: misst React Flow den Node
        neu (ResizeObserver), schickt es ebenfalls eine — nur ohne `resizing`.

        **Während eines Zugs an DIESEM Element gehört die Größe dem Zeiger.**
        Roh durchgereicht wechseln sich zwei Werte ab: unsere korrigierte
        Größe und die am DOM gemessene, die bei einem Zoom ≠ 1 gebrochen ist —
        die Karte pendelt zwischen ihnen, und das ist als Flackern zu sehen.
        Bei der **Memo** fällt es öfter an als beim **Notizen**, weil ihre
        Höhe ohnehin laufend gemessen wird (`useMeasuredNoteHeight`) und jede
        Höhenänderung eine neue Beobachtung anstößt.

        Verworfen wird nur die Meldung für das gezogene Element; jede andere
        geht weiter wie bisher, sonst bekäme ein frisch gerenderter Nachbar
        seine Maße nie.

        Ein fehlendes `node` fällt in denselben Zweig: Ohne den Node gibt es
        nichts, wogegen wir rechnen könnten.
      */
      if (sizing.resizing !== true) {
        const rest = changes.filter(c => c !== sizing)
        if (rest.length > 0) onNodesChange(rest)
        return
      }
      if (!node) {
        onNodesChange(changes)
        return
      }

      // Zieht man links oder oben, kommt eine Positionsänderung mit; rechts
      // unten bleibt der Ursprung stehen und es fehlt sie. Beides ist richtig.
      const move = changes.find(
        (c): c is Extract<NodeChange<Node<NoteNodeData>>, { type: 'position' }> =>
          c.type === 'position' && c.position !== undefined
      )
      const zoom = getZoom() || 1
      const targets = alignTargetsRef.current.filter(b => isVisible(b, viewportBounds()))
      const raw = {
        x: move?.position?.x ?? node.position.x,
        y: move?.position?.y ?? node.position.y,
        width: sizing.dimensions!.width,
        height: sizing.dimensions!.height,
      }
      /*
        Die Notiz hat kein Seitenverhältnis — ihre Höhe kommt vom Text, nicht
        vom Zeiger (siehe `useMeasuredNoteHeight`) — und braucht deshalb einen
        eigenen Pfad: nur die Breite ändert sich, Höhe und `y` bleiben stehen,
        und es gelten ihre eigenen Grenzen statt der der Notiz. Vorher lief
        auch sie über `getResizeAlignment`, dessen `height = width / aspect`
        bei jedem Breitenzug eine erfundene Höhe und ein daraus verschobenes
        `y` festschrieb (siehe `getWidthResizeAlignment`).
      */
      /*
        Die Verzweigung hängt an der ANFASSSTELLE, nicht an der Art.

        An einer Ecke skaliert der **Notiz** seitenverhältnistreu — Breite
        und Höhe zusammen, die Schrift wächst mit. An einer Kante ändert sich
        allein die Breite; Höhe und `y` bleiben stehen. Beides gibt es an
        DEMSELBEN Element, deshalb kann die Art nicht entscheiden.

        Für die Notiz sind an der Kante nur zwei Breiten erlaubt (Quadrat
        oder Rechteck, siehe `stickyWidths`) — die Notiz zieht dort frei.
      */
      /*
        Der **Bereich** skaliert FREI: beide Maße unabhängig, kein
        Seitenverhältnis, nichts im Inneren ändert sich — mit denselben
        Hilfslinien und Klammern wie am Zettel, je geführter Achse für sich
        (`getFreeResizeAlignment`); Mindestmaß, damit ein Bereich ein Bereich
        bleibt. Eine Achse, die der Zeiger nicht führt (Kantenzug), bleibt auf
        ihrem Ausgangswert: React Flow meldet dort nur die Messung, und die
        kann von der gespeicherten abweichen.
      */
      const start = resize.start ?? raw
      const result = resize.mode === 'free'
        ? getFreeResizeAlignment(
            {
              x: resize.axes.x ? raw.x : start.x,
              y: resize.axes.y ? raw.y : start.y,
              width: resize.axes.x ? raw.width : start.width,
              height: resize.axes.y ? raw.height : start.height,
            },
            resize.moving,
            resize.axes,
            targets,
            ALIGNMENT_THRESHOLD / zoom,
            GRID,
            MIN_AREA_SIZE,
          )
        : resize.mode === 'edge'
        ? getWidthResizeAlignment(
            raw,
            resize.moving.x,
            targets,
            ALIGNMENT_THRESHOLD / zoom,
            GRID,
            MEMO_SIZE_LIMITS,
            resize.kind === 'note'
              ? nextStickyWidth(raw.width, raw.height, GRID)
              : undefined,
          )
        : resize.kind === 'text'
        // Der **Text** skaliert stufenlos — auf dem Raster sprängen kleine
        // Schriften in Zwanzigerschritten. Fluchten mit anderen bleibt.
        ? getResizeAlignment(raw, resize.moving, targets, ALIGNMENT_THRESHOLD / zoom, 0, TEXT_SIZE_LIMITS)
        : getResizeAlignment(raw, resize.moving, targets, ALIGNMENT_THRESHOLD / zoom, GRID, SIZE_LIMITS)

      lastResizeRef.current = result.rect
      // Der **Aufnahme-Umriss** auch beim Skalieren eines Bereichs: Wer unter
      // die wachsende Fläche gerät, trägt die Marke — wie beim Tragen.
      if (resize.mode === 'free') previewAdoption([{ id: sizing.id, rect: result.rect }])
      if (!sameGuides(result.vertical, guides.vertical) ||
          !sameGuides(result.horizontal, guides.horizontal)) {
        setGuides({ vertical: result.vertical, horizontal: result.horizontal })
      }
      if (!sameMeasures(result.measures, measures)) setMeasures(result.measures)

      /*
        Die Position IMMER mitschicken: rechts unten ändert sie sich zwar
        nicht, aber unsere Korrektur kann sie auch dort verschieben, wenn die
        Größe an eine Grenze stößt.

        **Und `setAttributes` schon WÄHREND des Zugs, nicht erst beim
        Loslassen.** Die `NodeResizeControl` sind Geschwister der Karte, ihr
        `position: absolute` bezieht sich also auf React Flows Node-Hülle.
        Deren Breite kommt aus `node.width` (`getNodeInlineStyleDimensions` in
        @xyflow/react), und die schreibt `applyChange` NUR bei gesetztem
        `setAttributes` — ohne das bekommt sie bloß `measured`. Die Karte folgte
        damit dem Zug, die Hülle blieb auf ihrer Ausgangsbreite stehen, und die
        Ziehfläche stand um die gezogene Strecke neben der sichtbaren Kante.

        Bei der **Notiz** nur `'width'`: Ihre Höhe gehört der Messung
        (`useMeasuredNoteHeight`), nicht dem Zug. Beim **Notizen** beides — er
        skaliert wirklich in beide Richtungen.
      */
      onNodesChange([
        { id: sizing.id, type: 'position', position: { x: result.rect.x, y: result.rect.y } },
        {
          ...sizing,
          setAttributes: resize.mode === 'edge' ? 'width' : true,
          dimensions: { width: result.rect.width, height: result.rect.height },
        },
      ])
      return
    }

    // Was diese Geste bewegt, steht seit `handleNodeDragStart` fest. React
    // Flow schickt darüber hinaus Positionen für Elemente, die es eine Runde
    // zu früh für ausgewählt hielt (siehe dort) — die fallen hier weg, sonst
    // wanderte ein Unbeteiligter mit und landete am Ende in der Datenbank.
    // Nur Positionen: Auswahl, Maße und Löschungen gehen jeden an.
    const moving = dragRef.current?.moving
    const own = moving
      ? changes.filter(c => c.type !== 'position' || moving.has(c.id))
      : changes
    // Die Mitglieder eines gezogenen **Bereichs** fahren mit: dieselbe
    // Verschiebung wie ihr Bereich, gemessen an dessen gerade gerechneter
    // Lage (`lastPositionRef`) — also samt Einrasten.
    const carry = (out: NodeChange<Node<NoteNodeData>>[]) => {
      const st = dragRef.current
      if (!st?.carried || st.carried.size === 0) return out
      const extra: NodeChange<Node<NoteNodeData>>[] = []
      for (const [memberId, areaId] of st.carried) {
        const areaOrigin = st.origins.get(areaId)
        const memberOrigin = st.origins.get(memberId)
        const areaNow = lastPositionRef.current.get(areaId) ?? byId.get(areaId)?.position
        if (!areaOrigin || !memberOrigin || !areaNow) continue
        const position = { x: memberOrigin.x + (areaNow.x - areaOrigin.x), y: memberOrigin.y + (areaNow.y - areaOrigin.y) }
        lastPositionRef.current.set(memberId, position)
        /*
          **`dragging: false`, obwohl gerade gezogen wird.** Die Fahne gehört
          React Flows eigener Geste, und die Mitglieder sind nicht darin: Das
          Ende meldet es nur für die Knoten, die es selbst gezogen hat — ein
          Mitglied mit `dragging: true` behielte sie für immer. Und dann zählt
          `getNodesInside` es bei JEDEM Auswahlrechteck als getroffen, ohne
          Ansehen der Lage (`isVisible || node.dragging`, @xyflow/system): Ein
          getragener Zettel wäre quer über die Fläche sofort ausgewählt
          (gemeldet 2026-09-05). Gezogen wird ohnehin der Bereich; das
          Mitglied setzen wir selbst.
        */
        extra.push({ id: memberId, type: 'position', position, dragging: false })
      }
      return [...out, ...extra]
    }

    const drags = own.filter(
      (c): c is Extract<NodeChange<Node<NoteNodeData>>, { type: 'position' }> =>
        c.type === 'position' && c.dragging === true && c.position !== undefined
    )

    if (drags.length === 0) {
      if (guides !== NO_GUIDES) setGuides(NO_GUIDES)
      if (measures !== NO_MEASURES) setMeasures(NO_MEASURES)
      // Das Loslassen kommt als Positionsänderung mit `dragging: false` — und
      // trägt wieder React Flows ROHE Zeigerrechnung, nicht unsere
      // ausgerichtete. Unverändert durchgereicht spränge die Notiz im letzten
      // Moment zurück. Deshalb die gemerkte Position noch einmal einsetzen.
      onNodesChange(own.map(change => {
        if (change.type !== 'position' || !change.position) return change
        const settled = lastPositionRef.current.get(change.id)
        return settled ? { ...change, position: settled } : change
      }))
      return
    }

    // Alt-Ziehen: die Kopien entstehen erst, wenn der Zeiger die Schwelle
    // hinter sich hat — und genau einmal je Geste. Ohne die Schwelle erzeugte
    // ein Alt-KLICK deckungsgleiche, unsichtbare Kopien; ⌘Z räumte sie zwar
    // wieder weg, aber erst muss man merken, dass welche da sind.
    //
    // Kopiert wird, was sich bewegt: `drags` ist auf `dragRef.current.moving`
    // gefiltert. Über `n.selected` liefe es schief — die Auswahl zieht dem
    // Griff eine React-Runde hinterher (siehe `handleNodeDragStart`), ein
    // Alt-Griff auf ein noch nicht ausgewähltes Element legte im ersten Frame
    // die Kopien der ALTEN Auswahl an.
    const dragState = dragRef.current
    if (dragState?.alt && !dragState.done) {
      const lead = drags[0]!
      const origin = dragState.origins.get(lead.id)
      // Die Schwelle gilt in BILDSCHIRM-Pixeln, wie `ALIGNMENT_THRESHOLD`:
      // sonst löste die Geste herausgezoomt bei drei Pixeln aus und
      // hereingezoomt erst bei vierundzwanzig.
      const distance = origin
        ? Math.hypot(lead.position!.x - origin.x, lead.position!.y - origin.y)
        : 0
      if (origin && distance > DUPLICATE_DRAG_THRESHOLD / (getZoom() || 1)) {
        const entries = drags
          .map(change => ({ node: byId.get(change.id), start: dragState.origins.get(change.id) }))
          .filter((entry): entry is { node: Node<NoteNodeData>; start: XYPosition } =>
            Boolean(entry.node && entry.start))
          .map(({ node, start }) => ({ node, x: start.x, y: start.y }))
        const ids = duplicateNodes(entries)
        // Auch bei leerer Menge gesetzt: sonst liefe die Prüfung je Frame neu.
        dragState.done = true
        dragState.copies = new Map(entries.map((entry, index) => [entry.node.id, ids[index]!]))
      }
    }

    if (drags.length > 1) {
      // Mehrfachauswahl: Ausgerichtet wird der AUSWAHLRAHMEN, und sein Versatz
      // gilt für alle (siehe `getGroupAlignment`). Jede Notiz für sich
      // auszurichten ließe die Gruppe zerfallen — Notizen mit
      // unterschiedlichem Rest sprängen unterschiedlich weit.
      const moved = drags
        .map(change => {
          const node = byId.get(change.id)
          // Die Ausgangslage der GESTE, nicht die des letzten Frames: Aus ihr
          // und React Flows roher Zeigerrechnung ergibt sich der Versatz der
          // ganzen Bewegung. `dragRef` steht seit `handleNodeDragStart`; ohne
          // ihn (etwa in einem Test ohne Anfassen) tut es die aktuelle Lage.
          const origin = dragRef.current?.origins.get(change.id) ?? node?.position
          return node && origin ? { change, rect: { ...origin, ...sizeOf(node) } } : null
        })
        .filter((entry): entry is { change: typeof drags[number]; rect: MovingRect } => entry !== null)

      if (moved.length === 0) {
        onNodesChange(own)
        return
      }

      const lead = moved[0]!
      const zoom = getZoom() || 1
      const targets = alignTargetsRef.current.filter(b => isVisible(b, viewportBounds()))
      const result = getGroupAlignment(
        moved.map(entry => entry.rect),
        {
          x: lead.change.position!.x - lead.rect.x,
          y: lead.change.position!.y - lead.rect.y,
        },
        targets,
        ALIGNMENT_THRESHOLD / zoom,
        GRID,
      )

      const positions = new Map(moved.map((entry, index) => {
        const rect = result.rects[index]!
        const position = { x: rect.x, y: rect.y }
        lastPositionRef.current.set(entry.change.id, position)
        return [entry.change.id, position]
      }))
      previewAdoption(moved.map((entry, index) => ({ id: entry.change.id, rect: result.rects[index]! })))

      if (!sameGuides(result.vertical, guides.vertical) ||
          !sameGuides(result.horizontal, guides.horizontal)) {
        setGuides({ vertical: result.vertical, horizontal: result.horizontal })
      }
      if (!sameMeasures(result.measures, measures)) setMeasures(result.measures)

      onNodesChange(carry(own.map(change => {
        const position = change.type === 'position' ? positions.get(change.id) : undefined
        return position ? { ...change, position } : change
      })))
      return
    }

    const drag = drags[0]!
    const node = byId.get(drag.id)
    if (!node) {
      onNodesChange(own)
      return
    }

    // Die Schwelle gilt in BILDSCHIRM-Pixeln: durch den Zoom geteilt fühlt
    // sich das Einrasten bei jeder Zoomstufe gleich an. Der Zoom kommt über
    // `getZoom()` statt als Prop — sonst rendert der Canvas bei jeder
    // Zoomstufe neu.
    const zoom = getZoom() || 1
    // Nur an Sichtbarem ausrichten: eine Hilfslinie zu einer Notiz weit
    // außerhalb des Bildes erklärt nichts, sie erscheint nur grundlos.
    //
    // Der Filter läuft je Bewegung, nicht einmal beim Anfassen: React Flow
    // schiebt den Ausschnitt mit, wenn man an den Rand zieht
    // (`autoPanOnNodeDrag`), die Auswahl änderte sich sonst nicht mit.
    const targets = alignTargetsRef.current.filter(b => isVisible(b, viewportBounds()))
    const result = getAlignment(
      { ...drag.position!, ...sizeOf(node) },
      targets,
      ALIGNMENT_THRESHOLD / zoom,
      GRID,
    )

    lastPositionRef.current.set(drag.id, result.position)
    previewAdoption([{ id: drag.id, rect: { ...result.position, ...sizeOf(node) } }])
    // Nur setzen, wenn sich wirklich etwas ändert: sonst ein Rendern je
    // Zeigerimpuls, auch wenn dieselbe Linie stehen bleibt. Verglichen wird
    // über die Positionen, weil die Arrays bei jedem Aufruf neu entstehen.
    if (!sameGuides(result.vertical, guides.vertical) ||
        !sameGuides(result.horizontal, guides.horizontal)) {
      setGuides({ vertical: result.vertical, horizontal: result.horizontal })
    }
    // Die Klammern für den gleichen Abstand — leer, wenn keine Achse darüber
    // eingerastet ist.
    if (!sameMeasures(result.measures, measures)) setMeasures(result.measures)
    onNodesChange(carry([{ ...drag, position: result.position }]))
  }, [onNodesChange, getZoom, sizeOf, viewportBounds, duplicateNodes, recordStep, settleAreas, previewAdoption, clearAdoption, storeApi, dataSource, rememberNoteDefaults])

  const handleNodeDragStop = useCallback<OnNodeDrag<Node<NoteNodeData>>>((_event, node, dragged) => {
    setGuides(NO_GUIDES)
    setMeasures(NO_MEASURES)
    gestureRef.current = false
    const dragState = dragRef.current
    dragRef.current = null
    // Bewusst NICHT `node.position`: React Flow übergibt dort seine eigene
    // Zeigerrechnung, nicht die ausgerichtete Position (siehe
    // `lastPositionRef`). Bei einer Mehrfachauswahl bewegen sich alle
    // mitgezogenen Notizen, deshalb über die Liste.
    //
    // Gefiltert auf die Menge der Geste: React Flow reicht hier dieselbe
    // Liste durch, die es beim Anfassen zu früh gebildet hat (siehe
    // `handleNodeDragStart`). Ungefiltert schriebe ein unbeteiligter Notiz
    // seine ROHE, nie angezeigte Position in die Datenbank.
    clearAdoption()
    const carriedIds = new Set(dragState?.carried?.keys() ?? [])
    const movedNodes = [
      ...(dragged.length > 0 ? dragged : [node]).filter(moved => !dragState || dragState.moving.has(moved.id)),
      ...nodesRef.current.filter(n => carriedIds.has(n.id)),
    ]
    // Ein Griff, ein Schritt: Fünf mitgezogene Notizen sind fünf
    // Schreibvorgänge und trotzdem EIN ⌘Z. Die Ausgangslage steht seit dem
    // Anfassen in `dragRef.origins` — genau das, was ein Rückgängig
    // wiederherstellen muss.
    //
    // Wer nichts bewegt hat, kommt nicht in den Schritt: Ein Klick auf einen
    // Notizen schreibt zwar dieselbe Lage zurück, ist aber keine Änderung,
    // die man zurücknehmen wollte.
    recordStep(step => {
      for (const moved of movedNodes) {
        const position = lastPositionRef.current.get(moved.id) ?? moved.position
        const origin = dragState?.origins.get(moved.id)
        if (origin && (origin.x !== position.x || origin.y !== position.y)) {
          step.update(moved.id, { x: origin.x, y: origin.y }, { x: position.x, y: position.y })
        }
        void dataSource.updateCanvasNote(moved.id, { x: position.x, y: position.y })
      }
    })
    // Zugehörigkeit neu entscheiden (ADR 0012), im SELBEN Schritt wie die
    // Lage: die bewegten Zettel gegen alle Bereiche — hat sich ein Bereich
    // bewegt, alle Zettel, denn er nimmt am Ziel auf, was unter ihm liegt.
    const areaMoved = movedNodes.some(n => n.data?.note?.kind === 'area')
    recordStep(step => settleAreas(step, areaMoved ? null : new Set(movedNodes.map(n => n.id))))
    lastPositionRef.current.clear()
    if (dragState?.done) handOverTaskLink(dragState.copies)
  }, [handOverTaskLink, recordStep, settleAreas, clearAdoption, dataSource])

  /**
   * ⌘/Strg + D — die ausgewählten Notizen kopieren, rechts daneben.
   *
   * Die Kennungen werden vorab vergeben, wie beim Absetzen
   * (`handlePaneClick`): die Live-Query meldet die neuen Zeilen asynchron, die
   * Auswahl muss aber schon wissen, auf wen sie wandern soll.
   */
  const handleDuplicate = useCallback((fromNoteId?: string) => {
    // Noch offene Kopien: siehe `pendingDuplicatesRef`.
    if (pendingDuplicatesRef.current) return
    // Läuft eine Zeiger-Geste: siehe `gestureRef`.
    if (gestureRef.current) return

    /*
      Aus dem **Bearbeiten-Modus**: Gemeint ist der Zettel, in dem getippt
      wird — ein frisch abgesetzter ist gar nicht ausgewählt (CONTEXT.md).
      Erst festschreiben und den Text verlassen, wie es ⇥ tut
      (`handleTabSpawn`): Der Zustand ist Node-lokal, niemand von außen kann
      ihn beenden, und die Kopie soll tragen, was gerade dasteht.
    */
    if (fromNoteId) {
      ;(document.activeElement as HTMLElement | null)?.blur()
      /*
        **Und der Fokus zurück auf den Node** — wie beim Verlassen des Textes
        mit Escape. Ohne ihn läge er auf `body`, und das nächste ⌘D käme am
        Canvas-Element gar nicht mehr an (dort horcht das Kürzel, siehe
        `useCanvasDuplicateShortcut`): Die Kette bräche nach der ersten Kopie.
        Auf den Node, in dem getippt wurde, und nicht auf die Kopie — die gibt
        es noch nicht; die Auswahl wandert erst hinüber, wenn ihre Zeile
        eintrifft (`pendingDuplicatesRef`).
      */
      canvasElement
        ?.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(fromNoteId)}"]`)
        ?.focus({ preventScroll: true })
    }
    const selected = fromNoteId
      ? nodesRef.current.filter(n => n.id === fromNoteId)
      : nodesRef.current.filter(n => n.selected)
    if (selected.length === 0) return

    // Die übrigen Notizen gehen als Hindernisse mit: Steht rechts schon
    // einer, weicht die Kopie nach links aus (siehe `getDuplicateOffset`).
    // Der Versatz kann dadurch negativ sein.
    const offset = getDuplicateOffset(
      selected.map(n => ({ ...n.position, ...sizeOf(n) })),
      nodesRef.current.filter(n => !n.selected).map(n => ({ ...n.position, ...sizeOf(n) })),
    )
    // Duplizieren TRÄGT den Inhalt mit (CONTEXT.md, Bereich): Zu jedem
    // ausgewählten Bereich kommen seine Mitglieder, um denselben Versatz, und
    // sie gehören danach zur Kopie. Ein selbst ausgewähltes Mitglied kommt einmal.
    const selectedIds = new Set(selected.map(n => n.id))
    const members = nodesRef.current.filter(n => {
      const areaId = n.data?.note?.areaId
      return areaId && selectedIds.has(areaId) && !selectedIds.has(n.id)
    })
    const areaOf = (node: Node<NoteNodeData>) => {
      const areaId = node.data?.note?.areaId
      return areaId && selectedIds.has(areaId) ? areaId : undefined
    }
    pendingDuplicatesRef.current = duplicateNodes([
      ...selected.map(node => ({ node, x: node.position.x + offset, y: node.position.y, areaOf: areaOf(node) })),
      ...members.map(node => ({ node, x: node.position.x + offset, y: node.position.y, areaOf: areaOf(node) })),
    ])
  }, [sizeOf, duplicateNodes, canvasElement])

  useCanvasDuplicateShortcut({
    enabled: isActiveTab && tool === 'select',
    container: canvasElement,
    onDuplicate: handleDuplicate,
  })

  /* ── Vorn und hinten ──────────────────────────────────────────────────────
     „Ganz nach vorn" / „Ganz nach hinten" (CONTEXT.md) — aus dem Menü am
     Rechtsklick und über ⇧⌘↑ / ⇧⌘↓. Geschrieben wird `zOrder`; die Live-Query
     bringt die Zeilen zurück, und der Abgleich stapelt neu (`compareZ`). Ein
     Schritt für ⌘Z, auch bei einer Mehrfachauswahl.
  */
  const handleZOrder = useCallback((direction: ZOrderDirection, ids: readonly string[]) => {
    const changes = planZOrder(notesRef.current, ids, direction)
    if (changes.length === 0) return
    recordStep(step => {
      for (const change of changes) {
        step.update(change.id, change.before, change.after)
        trackNoteWrite(dataSource.updateCanvasNote(change.id, change.after))
      }
    })
  }, [recordStep, trackNoteWrite, dataSource])

  const selectedIds = useCallback(() => nodesRef.current.filter(n => n.selected).map(n => n.id), [])

  useCanvasZOrderShortcut({
    enabled: isActiveTab && tool === 'select',
    container: canvasElement,
    onZOrder: direction => handleZOrder(direction, selectedIds()),
  })

  /*
    Das Menü am Rechtsklick. Worauf es wirkt, steht beim Öffnen fest
    (`menuTargetRef`): die Auswahl, wenn das Element dazugehört — sonst allein
    das Element, das dabei ausgewählt wird (wie in den Aufgabenlisten). Die
    Auswahl selbst ist dann noch nicht geschrieben; `setNodes` läuft erst mit
    dem nächsten Zeichnen.

    Ein stabiles Objekt über Refs: Es liegt im Context JEDES Elements, und ein
    neues bei jedem Werkzeug- oder Auswahlwechsel zeichnete alle neu.
  */
  const menuTargetRef = useRef<string[]>([])
  const menuStateRef = useRef({ tool, editingNoteId, canvasElement, handleZOrder })
  menuStateRef.current = { tool, editingNoteId, canvasElement, handleZOrder }
  const elementMenu = useMemo<CanvasElementMenuApi>(() => ({
    beforeOpen: id => {
      const { tool: currentTool, editingNoteId: editing } = menuStateRef.current
      // Beim Schreiben gehört der Rechtsklick dem Text.
      if (currentTool !== 'select' || editing === id) return false
      const node = nodesRef.current.find(n => n.id === id)
      if (!node) return false
      if (node.selected) {
        menuTargetRef.current = selectedIds()
      } else {
        menuTargetRef.current = [id]
        setNodes(ns => ns.map(n => (n.id === id ? { ...n, selected: true } : (n.selected ? { ...n, selected: false } : n))))
      }
      return true
    },
    availability: () => ({
      front: planZOrder(notesRef.current, menuTargetRef.current, 'front').length > 0,
      back: planZOrder(notesRef.current, menuTargetRef.current, 'back').length > 0,
    }),
    apply: direction => menuStateRef.current.handleZOrder(direction, menuTargetRef.current),
    closeAutoFocus: (id, event) => {
      // Nicht auf den Auslöser (er hat keinen Kasten), sondern auf den Node — dort horchen ⌘Z, ⌫ und ⇧⌘↑.
      event.preventDefault()
      menuStateRef.current.canvasElement
        ?.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(id)}"]`)
        ?.focus({ preventScroll: true })
    },
  }), [selectedIds, setNodes])

  /**
   * Tab — einen Schritt in der **Tab-Reihenfolge** (`canvasTabOrder.ts`).
   *
   * Meldet `false`, wenn nichts ausgewählt ist: Dann gehört die Taste wieder
   * dem Browser, und der Rest der App behält seine Tastaturnavigation.
   *
   * Der Anker bei einer Mehrfachauswahl ist bewusst richtungsabhängig — vorwärts
   * der letzte, rückwärts der erste. Man verlässt den Block damit auf der
   * Seite, auf die man zugeht.
   */
  const handleTabStep = useCallback((direction: 1 | -1) => {
    const order = buildTabOrder(
      nodesRef.current.map(n => ({ id: n.id, ...n.position, ...sizeOf(n) })),
    )
    /*
      Ein **geklebter** Zettel zählt als Anker mit, obwohl er in keiner Auswahl
      steht: Er ist **angetippt**, und das ist für Tab dasselbe „wo bin ich
      gerade". Ohne ihn endete die Reihenfolge an ihm — man käme hin und nicht
      mehr weiter.
    */
    const selectedIds = new Set(nodesRef.current.filter(n => n.selected).map(n => n.id))
    if (tappedRef.current) selectedIds.add(tappedRef.current)
    if (selectedIds.size === 0) return false

    const anchors = order.filter(id => selectedIds.has(id))
    const anchor = direction === 1 ? anchors[anchors.length - 1] : anchors[0]
    if (!anchor) return false

    const next = stepTabOrder(order, anchor, direction)
    if (!next) return false

    /*
      **Tab hält an ihm an, wählt ihn aber nicht aus.** Landet der Schritt auf
      einem geklebten Zettel, wird er angetippt: Seine Leiste geht auf, Enter
      löst ab, ein weiteres Tab geht weiter. Sonst wäre der Klebestreifen die
      einzige Funktion im Canvas ohne Tastaturweg zurück — und er ist
      ausgerechnet die, die man ohne ihn nicht mehr los wird, wenn ein jüngerer
      Nachbar seinen Streifen verdeckt.
    */
    const taped = Boolean(nodesRef.current.find(n => n.id === next)?.data?.note?.tape)
    setTappedId(taped ? next : null)
    setNodes(ns => ns.map(n => {
      const selected = !taped && n.id === next
      // `Boolean`, weil ein frischer Node `selected` noch gar nicht trägt.
      return Boolean(n.selected) === selected ? n : { ...n, selected }
    }))

    /*
      Ganz ins Bild holen, wenn auch nur eine Kante draußen liegt — und zwar
      MITTIG, wie Miro es hält.

      `isFullyVisible` und nicht `isVisible`: Das Ziel anzusteuern heißt, es
      ganz zu sehen. Genau daran scheitert React Flows eigenes
      `autoPanOnNodeFocus` (unten abgeschaltet): Es fragt
      `getNodesInside(..., partially: true)` und hält schon ein sichtbares
      Eckchen für genug — eine Notiz, von dem 15% hereinragen, blieb so am
      Rand kleben.

      Der Zoom muss mitgegeben werden: ohne ihn nimmt `setCenter` die
      Obergrenze (@xyflow/react, `index.js:3643`) und schnappte nebenbei auf
      die 1:1-Ansicht. 200 ms wie beim Duplizieren.
    */
    const target = nodesRef.current.find(n => n.id === next)
    if (target) {
      const box = toBounds({ ...target.position, ...sizeOf(target) })
      if (!isFullyVisible(box, viewportBounds())) {
        void setCenter(box.centerX, box.centerY, { zoom: getZoom() || 1, duration: 200 })
      }
    }

    /*
      Der Fokus wandert mit: Ließe man ihn stehen, säße er nach zehn Tabs zehn
      Notizen hinter der Auswahl — React Flows Pfeiltasten verschöben dann den
      falschen.

      Erst im nächsten Frame, denn der Node trägt seinen neuen Zustand noch
      nicht und ein `focus()` auf das alte DOM ginge ins Leere.
    */
    requestAnimationFrame(() => {
      canvasElement
        ?.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(next)}"]`)
        ?.focus()
    })
    return true
  }, [sizeOf, setNodes, canvasElement, viewportBounds, setCenter, getZoom])

  /**
   * Tab im **Bearbeiten-Modus** — einen neuen, leeren **Notizen** daneben,
   * sofort wieder im Text (wie in Miro).
   *
   * Maße, Farbe und Art werden geerbt, der Inhalt NICHT: Wer im Text sitzt und
   * Tab drückt, will „noch so einen".
   */
  const handleTabSpawn = useCallback((fromNoteId: string) => {
    // Noch offene Kopien: siehe `pendingDuplicatesRef`. Ohne diesen Riegel
    // landete der zweite Notiz auf dem Platz des ersten —
    // `getDuplicateOffset` sieht die noch nicht eingetroffene Zeile nicht und
    // hielte den Platz für frei.
    if (pendingDuplicatesRef.current) return
    if (gestureRef.current) return

    const source = nodesRef.current.find(n => n.id === fromNoteId)
    if (!source) return

    // Den laufenden Text festschreiben und den Bearbeiten-Modus verlassen. Der
    // Zustand ist Node-lokal, niemand von außen kann ihn beenden — ohne das
    // blieben zwei Notizen gleichzeitig im Text.
    ;(document.activeElement as HTMLElement | null)?.blur()

    const rect = { ...source.position, ...sizeOf(source) }
    const offset = getDuplicateOffset(
      [rect],
      nodesRef.current.filter(n => n.id !== fromNoteId).map(n => ({ ...n.position, ...sizeOf(n) })),
    )
    const spawned = { ...rect, x: rect.x + offset }

    /*
      Den neuen Notizen ganz ins Bild holen — dieselbe Regel wie beim Schritt
      durch die Reihenfolge (`handleTabStep`).

      Nötig, weil der Platz daneben sehr wohl außerhalb des **Ausschnitts**
      liegen kann: Wer eine Notiz am rechten Rand bearbeitet und Tab
      drückt, bekommt den neuen dahinter — und tippt dann in etwas, das er
      kaum sieht.

      JETZT und nicht erst, wenn die Zeile eintrifft: Die Lage steht hier
      bereits fest, und der Weg über den `pendingDuplicatesRef`-Effekt unten
      käme einen Dexie-Umlauf zu spät — man tippt schon. Jener Effekt prüft
      dasselbe (er holt die Auswahl herüber und rückt ebenfalls nach), nur eben
      später; hier greift die Regel sofort.
    */
    if (!isFullyVisible(toBounds(spawned), viewportBounds())) {
      const box = toBounds(spawned)
      void setCenter(box.centerX, box.centerY, { zoom: getZoom() || 1, duration: 200 })
    }

    // Die Kennung wird vorab vergeben, wie beim Absetzen (`placeNoteAt`):
    // Die Live-Query meldet die neue Zeile asynchron, `startEditing` und die
    // Auswahl müssen aber schon wissen, auf wen sie zeigen.
    const id = crypto.randomUUID()
    setAutoEditNoteId(id)
    // Über denselben Weg wie ⌘D: Der Effekt unten holt die Auswahl herüber,
    // sobald die Notiz eingetroffen ist. Ohne sie endete der Tastaturfluss
    // beim ersten Escape — der Bearbeiten-Modus ist NICHT dasselbe wie
    // ausgewählt (CONTEXT.md).
    pendingDuplicatesRef.current = [id]
    recordStep(step => step.created(id, new Date()))
    void dataSource.createCanvasNote({
      id,
      projectId,
      kind: source.data.note.kind,
      content: '',
      x: spawned.x,
      y: spawned.y,
      width: rect.width,
      height: rect.height,
      color: source.data.note.color ?? DEFAULT_CANVAS_NOTE_COLOR,
      // `showAuthor` sagt, WIE die Notiz aussieht — dieselbe Art Entscheidung
      // wie `color` oben, die der Nachfolger deshalb ebenfalls übernimmt.
      // `title` erbt er dagegen nie: Ein Titel ist Inhalt, und Inhalt erbt
      // ein per Tab erzeugter Nachfolger grundsätzlich nicht (siehe `content`
      // oben, das immer leer beginnt).
      showAuthor: source.data.note.showAuthor,
      // Aus demselben Grund wie `showAuthor`: die Schrift sagt, WIE der
      // Nachfolger aussieht, nicht was auf ihm steht.
      font: source.data.note.font,
    })
  }, [projectId, sizeOf, viewportBounds, setCenter, getZoom, recordStep, dataSource])

  useCanvasTabShortcut({
    enabled: isActiveTab && tool === 'select',
    container: canvasElement,
    onStep: handleTabStep,
    onSpawn: handleTabSpawn,
  })

  /**
   * Die Auswahl wandert auf die Kopien, sobald sie eingetroffen sind — vorher
   * ginge es nicht, die Nodes gibt es noch nicht.
   *
   * Und wenn keine von ihnen im Bild liegt, rückt der Ausschnitt nach. Das ist
   * kein Sonderfall für weit auseinanderliegende Auswahlen: Schon eine Notiz
   * nah am rechten Rand schiebt ihre Kopie hinaus. Auf dem Schirm passierte
   * dann nur, dass das Original seinen Auswahlrahmen verliert — das liest sich
   * wie „kaputt", nicht wie „dupliziert".
   */
  useEffect(() => {
    const pending = pendingDuplicatesRef.current
    if (!pending) return

    const copies = notes.filter(note => pending.includes(note.id))
    if (copies.length < pending.length) return
    pendingDuplicatesRef.current = null

    const isCopy = new Set(pending)
    /*
      Kam der Zettel über einen **Anlegepunkt**, federt er beim Erscheinen
      einmal auf (`POP_CLASS`) und holt den Fokus. Klasse und Auswahl in EINEM
      `setNodes`: Der Zettel soll nicht erst dastehen und dann anfangen.
    */
    const spawned = focusSpawnedRef.current ? pending[0] ?? null : null
    focusSpawnedRef.current = false

    setNodes(ns => ns.map(n => {
      const selected = isCopy.has(n.id)
      const className = n.id === spawned ? POP_CLASS : n.className
      // `Boolean`, weil ein frischer Node `selected` noch gar nicht trägt —
      // `undefined === false` wäre falsch und baute jeden Node neu auf.
      return Boolean(n.selected) === selected && className === n.className
        ? n
        : { ...n, selected, className }
    }))

    if (spawned) {
      if (popTimerRef.current) clearTimeout(popTimerRef.current)
      popTimerRef.current = setTimeout(() => {
        popTimerRef.current = null
        setNodes(ns => ns.some(n => n.className === POP_CLASS)
          ? ns.map(n => (n.className === POP_CLASS ? { ...n, className: undefined } : n))
          : ns)
      }, SPAWN_ANIM_MS)

      // Der Zettel steht an der Stelle des Bildes und sieht aus wie es —
      // die Ablösung fällt damit nicht auf.
      if (commitTimerRef.current) {
        clearTimeout(commitTimerRef.current)
        commitTimerRef.current = null
      }
      setSpawnPreview(previous => (previous?.committed ? null : previous))

      requestAnimationFrame(() => {
        canvasElement
          ?.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(spawned)}"]`)
          ?.focus({ preventScroll: true })
      })
    }

    const rects = copies.map(note => ({
      x: note.x,
      y: note.y,
      width: note.width || DEFAULT_CANVAS_NOTE_WIDTH,
      height: note.height || DEFAULT_CANVAS_NOTE_HEIGHT,
    }))
    /*
      Nachrücken, solange nicht JEDE Kopie GANZ zu sehen ist.

      Hier stand `rects.some(isVisible)` — zwei Fehler in einer Zeile:

        `some`      Bei einer Mehrfachauswahl genügte EINE sichtbare Kopie, die
                    übrigen durften draußen liegen. Dabei ist die Auswahl
                    gerade auf sie alle gewandert.
        `isVisible` „überhaupt zu sehen" reicht nicht: Eine Kopie, von der ein
                    Zipfel hereinragt, ist angekommen und trotzdem nicht
                    angeschaut. Dieselbe Unterscheidung trägt die
                    **Tab-Reihenfolge** (`handleTabStep`).

      Passt die ganze Gruppe nicht ins Bild, ist Zentrieren das Bestmögliche —
      `isFullyVisible` kann dann nie erfüllt sein, und genau das ist gewollt.
    */
    const viewport = viewportBounds()
    if (rects.every(rect => isFullyVisible(toBounds(rect), viewport))) return

    const box = getBoundingBox(rects)
    if (!box) return
    // Der Zoom MUSS mitgegeben werden: ohne ihn nimmt `setCenter` die
    // Obergrenze (@xyflow/react, `index.js:3643`), der Canvas schnappte also
    // nebenbei auf die 1:1-Ansicht. 200 ms wie die Brücke bei ⇧0.
    void setCenter(box.centerX, box.centerY, { zoom: getZoom() || 1, duration: 200 })
  }, [notes, setNodes, viewportBounds, setCenter, getZoom, canvasElement])

  /**
   * Legt einen **Text** an — beim Klick in der gemerkten Größe, beim
   * **Aufziehen** in der Größe, die das Quadrat festlegt (`fontSize`).
   *
   * Er beginnt im **Bearbeiten-Modus** wie ein Zettel, mit dem Platzhalter
   * „Text hinzufügen", und gehört dem **Bereich**, in dem er landet. Seine
   * Maße sind hier nur geschätzt; die Messung ersetzt sie sofort.
   *
   * Eine aufgezogene Größe ist eine Wahl: Sie gilt für den nächsten Text
   * (CONTEXT.md, „Zuletzt gewählt gilt weiter").
   */
  const placeTextAt = useCallback((position: XYPosition, drawnSize: number | null) => {
    const defaults = readCanvasNoteDefaults(projectId, 'text')
    const fontSize = drawnSize ?? defaults.fontSize ?? DEFAULT_CANVAS_TEXT_SIZE
    const size = estimateCanvasTextSize(CANVAS_TEXT_PLACEHOLDER_ESTIMATE, fontSize)
    const id = crypto.randomUUID()
    setAutoEditNoteId(id)
    setNodes(ns => (ns.some(n => n.selected)
      ? ns.map(n => (n.selected ? { ...n, selected: false } : n))
      : ns))
    recordStep(step => step.created(id, new Date()))
    void dataSource.createCanvasNote({
      id,
      projectId,
      kind: 'text',
      content: '',
      x: position.x,
      y: position.y,
      width: size.width,
      height: size.height,
      color: defaults.color,
      font: defaults.font,
      fontSize,
      areaId: areaFor(
        { ...position, ...size },
        nodesRef.current
          .filter(n => n.data?.note?.kind === 'area')
          .map(n => ({ id: n.id, rect: { ...n.position, ...sizeOf(n) } })),
      )?.id ?? null,
    })
    if (drawnSize !== null) rememberNoteDefaults('text', { fontSize })
    onPlacementConsumed()
  }, [projectId, setNodes, recordStep, dataSource, sizeOf, rememberNoteDefaults, onPlacementConsumed])

  /*
    **Aufziehen** eines Texts — ein Quadrat, dessen Seite die Schriftgröße
    festlegt (siehe `hooks/useTextDrawing`). Die Ecke landet aufs Raster wie
    beim Klick.
  */
  const { square: textSquare, drewRef: drewTextRef } = useTextDrawing({
    placementMode,
    canvasElement,
    screenToFlowPosition,
    clickThreshold: CLICK_DRAG_THRESHOLD,
    onDrawn: useCallback((square: { x: number; y: number; side: number }) => {
      placeTextAt(getCanvasTextPosition(square, GRID), canvasTextSizeFromSide(square.side))
    }, [placeTextAt]),
  })

  /**
   * Legt ein **Canvas-Element** an der Zeigerstelle ab — mittig darunter und
   * aufs Raster gerastert (siehe `getPlacementPosition`).
   *
   * Welche Art, sagt der `placementMode`. Von BEIDEN Wegen genutzt: dem Klick
   * auf die Fläche (Werkzeug scharf) und dem Loslassen nach einem Zug vom
   * Werkzeug-Knopf.
   */
  const placeNoteAt = useCallback((client: { x: number; y: number }, kind: 'note' | 'memo' | 'area' | 'text') => {
    // Der **Text** hat seinen eigenen Weg (Ecke statt Mitte, Schriftgröße).
    if (kind === 'text') {
      placeTextAt(getCanvasTextPosition(screenToFlowPosition(client), GRID), null)
      return
    }
    // Dieselbe Zahl wie die Mindesthöhe einer Notiz: Sie soll nicht erst
    // springen. Die Messung bestätigt sie beim ersten Zeichnen
    // (`useMeasuredNoteHeight`) — sie muss hier nur stehen, damit React Flow
    // den Node nicht als ungemessen versteckt.
    const size = getPlacementSize(kind)

    const position = getPlacementPosition(screenToFlowPosition(client), size, GRID)

    // Die Kennung wird hier vergeben statt aus dem Schreibvorgang gelesen:
    // die Notizen kommen über eine Live-Query, und Dexie meldet die neue Zeile
    // seinerseits asynchron. Wartete man auf `created.id`, könnte die Meldung
    // zuerst da sein — der Node mountete dann mit `startEditing: false`, und
    // die frische Notiz bliebe stumm. Manchmal.
    const id = crypto.randomUUID()
    /*
      **Der Bereich ist ausgewählt, sobald er dasteht** (gewünscht
      2026-09-05), der Zettel nicht: Der beginnt im **Bearbeiten-Modus** und
      zeigt seine Leiste bewusst ohne Auswahl (CONTEXT.md). Der Bereich hat
      keinen Text, in dem er beginnen könnte — ohne Auswahl stünde er da,
      ohne Leiste und ohne Griffe, und das erste, was man täte, wäre ihn
      anzuklicken. Die Auswahl holt der Effekt herüber, sobald die Zeile
      eingetroffen ist — vorher gibt es den Node nicht.
    */
    if (kind === 'area') pendingDuplicatesRef.current = [id]
    else setAutoEditNoteId(id)
    // Die bestehende Auswahl geht: Zwei Leisten stünden sonst nebeneinander.
    setNodes(ns => (ns.some(n => n.selected)
      ? ns.map(n => (n.selected ? { ...n, selected: false } : n))
      : ns))
    recordStep(step => {
      step.created(id, new Date())
      // Ein Bereich nimmt auf, was unter ihm liegt — auch der geklickte.
      if (kind === 'area') adoptUnder(step, id, { x: position.x, y: position.y, width: size.width, height: size.height })
    })
    void dataSource.createCanvasNote({
      id,
      projectId,
      kind,
      content: '',
      x: position.x,
      y: position.y,
      width: size.width,
      // **Der Bereich kommt benannt auf die Fläche** (siehe `AREA_TITLE_KEY`).
      // Ein Zettel beginnt weiterhin ohne Titel — er hat seinen Text.
      ...(kind === 'area' ? { title: t(AREA_TITLE_KEY) } : {}),
      /*
        **Wer IN einem Bereich abgesetzt wird, gehört ihm** (gemeldet
        2026-09-05) — dieselbe Regel wie beim Hineinziehen (ADR 0012), nur
        über das Werkzeug. Ohne das läge der Zettel sichtbar drin und führe
        beim Verschieben doch nicht mit. Ein Bereich selbst gehört nie einem
        anderen.
      */
      areaId: kind === 'area' ? null : areaFor(
        { x: position.x, y: position.y, width: size.width, height: size.height },
        nodesRef.current
          .filter(n => n.data?.note?.kind === 'area')
          .map(n => ({ id: n.id, rect: { ...n.position, ...sizeOf(n) } })),
      )?.id ?? null,
      height: size.height,
      /*
        **Zuletzt gewählt gilt weiter** — Farbe und Schrift des zuletzt
        eingestellten Elements, je Projekt und je Art gemerkt (CONTEXT.md).

        Der **Bereich** ist seit 2026-09-06 dabei; er nimmt nur die Farbe mit.
        Ein `font` von ihm gäbe es ohnehin nicht (siehe `rememberNoteDefaults`),
        aber die Vorgabe `null` mitzuschreiben behauptete eine Einstellung, die
        er gar nicht kennt.
      */
      ...(kind === 'area'
        ? { color: readCanvasNoteDefaults(projectId, 'area').color }
        : readCanvasNoteDefaults(projectId, kind)),
    })
    onPlacementConsumed()
  }, [projectId, screenToFlowPosition, onPlacementConsumed, recordStep, setNodes, adoptUnder, sizeOf, t, dataSource, placeTextAt])

  const handlePaneClick = useCallback((event: React.MouseEvent) => {
    /*
      Der Klick, den der Browser nach dem **Aufziehen** noch meldet, wird
      geschluckt — und zwar VOR der Frage nach dem Werkzeug: Das ist zu diesem
      Zeitpunkt längst abgeräumt (`onPlacementConsumed` beim Loslassen), der
      Merker bliebe sonst stehen und schluckte irgendwann später den Klick,
      der wirklich etwas ablegen soll (gemeldet 2026-09-05).
    */
    if (drewRef.current || drewTextRef.current) {
      drewRef.current = false
      drewTextRef.current = false
      return
    }
    // Ein Klick daneben nimmt das **Angetippte** zurück — so wie er die Auswahl
    // zurücknimmt. Vor der Frage nach dem Werkzeug: Auch wer gerade etwas
    // ablegen will, meint den geklebten Zettel nicht mehr.
    if (tappedRef.current) setTappedId(null)
    if (placementMode === null) return
    // Während eines Zuges vom Knopf setzt das Loslassen ab, nicht der Klick —
    // sonst entstünden zwei Notizen. Ein Klick auf die Fläche kann dabei
    // ohnehin nicht auftreten (der `pointerdown` lag auf dem Knopf), das hier
    // ist der Gürtel zum Hosenträger.
    if (placementDrag) return
    placeNoteAt({ x: event.clientX, y: event.clientY }, placementMode)
  }, [placementMode, placementDrag, placeNoteAt, drewRef, drewTextRef])

  /**
   * **Doppelklick auf die freie Fläche — zwei Zoomstufen, ein Anschlag.**
   *
   * | Lage | wohin |
   * |---|---|
   * | noch nicht ganz nah | auf 1:1 |
   * | schon ganz nah | ganz heraus, auf die Untergrenze |
   *
   * **Der Ausschnitt bleibt stehen, es wechselt nur die Stufe.** Bis
   * September 2026 rückte die geklickte Stelle dabei in die Mitte — das war
   * als „zoomen an einer Stelle" gedacht, fühlte sich aber wie ein Sprung an:
   * Man wollte näher heran und stand danach woanders. Jetzt bleibt die Mitte,
   * wo sie ist (`zoomTo` statt `setCenter`), und der Zeiger spielt keine
   * Rolle mehr. Ein Einrahmen aller Notizen kommt aus demselben Grund nicht
   * in Frage: Das risse den Ausschnitt an eine Stelle, auf die niemand
   * gezeigt hat.
   *
   * **Aus jeder Zwischenstufe geht es erst hinein.** Der Doppelklick heißt
   * damit immer „näher heran", solange man nicht schon ganz drin ist — sonst
   * müsste man wissen, auf welcher Seite der Mitte man gerade steht.
   *
   * **Was React Flow von sich aus täte:** `zoomOnDoubleClick` steht auf `true`,
   * solange man es nicht abschaltet, und d3-zoom VERDOPPELT dann je Anschlag
   * bis zur Obergrenze. Von 0,3 aus sind das 0,6 und 1 — und danach nichts
   * mehr, denn zurück ginge es nur mit ⇧+Doppelklick, was niemand weiß. Der
   * Schalter am `<ReactFlow>` steht deshalb auf `false`.
   */
  const handlePaneDoubleClick = useCallback((event: React.MouseEvent) => {
    /*
      Nur die PANE. Ein Doppelklick auf eine Notiz öffnet den
      **Bearbeiten-Modus** (`NoteNode`), einer auf das **Eselsohr** ist
      dort schon abgefangen — beide steigen bis hierher auf, wenn man sie
      nicht unterscheidet.

      React Flow reicht für den Doppelklick keinen eigenen Rückruf durch (es
      gibt `onPaneClick`, aber kein `onPaneDoubleClick`), also hängt er am
      Container und prüft das Ziel selbst.
    */
    if (!(event.target as Element | null)?.classList?.contains('react-flow__pane')) return
    // Im Hand-Werkzeug zieht man auf der Fläche; dort ist der Doppelklick
    // nicht belegt und soll es auch nicht werden.
    if (tool !== 'select') return

    const zoom = isZoomedIn(getZoom()) ? CANVAS_MIN_ZOOM : CANVAS_MAX_ZOOM
    void zoomTo(zoom, { duration: 200 })
  }, [tool, getZoom, zoomTo])

  // ── Das Vorschaubild am Zeiger ───────────────────────────────────────────

  // `track` unabhängig von `active`: Der Zeiger wird durchgehend verfolgt,
  // damit das Vorschaubild im Moment des Scharfstellens schon weiß, wo es
  // hingehört — sonst erschiene es erst bei der ersten Mausbewegung.
  const placementPointer = useCanvasPlacementDrag({
    active: placementMode !== null,
    track: isActiveTab,
  })

  /**
   * Wo das Vorschaubild sitzt — seine MITTE in Client-Koordinaten.
   *
   * Über der Fläche zeigt es die EINGERASTETE Lage: Man sieht vorher, wo die
   * Notiz landet. Außerhalb folgt es dem Zeiger stufenlos, dort gibt es kein
   * Raster.
   *
   * **Gerastert wird mit den Maßen der Art, die gleich absetzt**
   * (`getPlacementSize`, dieselbe Stelle wie `placeNoteAt`). Vorher stand
   * hier immer die Größe der Notiz — bei einem Memo (249px breit statt
   * 168px) verschob das den gerasterten Ursprung, und das Vorschaubild
   * saß sichtbar woanders als die dann tatsächlich abgesetzte Notiz.
   */
  const previewPoint = useMemo(() => {
    if (!placementPointer) return null
    if (!placementPointer.overCanvas) return { x: placementPointer.x, y: placementPointer.y }

    // `?? 'note'` ist hier nur ein Typ-Anker: `placementPointer` existiert
    // laut `active: placementMode !== null` oben nur, während `placementMode`
    // selbst nicht `null` ist.
    // Der **Text** hängt mit seiner linken oberen Ecke am Fadenkreuz — das Bild
    // bekommt dann die Ecke, nicht die Mitte (siehe `CanvasPlacementPreview`).
    if (placementMode === 'text') {
      return flowToScreenPosition(getCanvasTextPosition(
        screenToFlowPosition({ x: placementPointer.x, y: placementPointer.y }),
        GRID,
      ))
    }
    const size = getPlacementSize(placementMode ?? 'note')
    const position = getPlacementPosition(
      screenToFlowPosition({ x: placementPointer.x, y: placementPointer.y }),
      size,
      GRID,
    )
    // Zurück auf den Schirm, und zwar die MITTE — das Bild ist um seinen
    // Mittelpunkt herum positioniert (`translate: -50% -50%`).
    return flowToScreenPosition({
      x: position.x + size.width / 2,
      y: position.y + size.height / 2,
    })
  }, [placementPointer, placementMode, screenToFlowPosition, flowToScreenPosition])

  /**
   * Das Loslassen nach einem Zug vom Werkzeug-Knopf.
   *
   * Über der Fläche legt es ab. Über der Werkzeug-Pille passiert nichts — wer
   * hinauszieht und wieder zurückkommt, hat es sich anders überlegt; der
   * Notiz bleibt scharfgestellt. Überall sonst wird abgebrochen, wie beim
   * Ziehen einer Datei ins Leere.
   *
   * **Die Reihenfolge der beiden Abfragen ist tragend.** Die Pille liegt IM
   * `.react-flow` (ADR 0008: nur als dessen Kind landet ihre Fläche im selben
   * Stapelkontext wie Punktraster und Notizen). Stünde die Flächen-Abfrage
   * zuerst, träfe sie auch auf der Pille zu — die Pillen-Zeile wäre toter Weg,
   * und ein Loslassen auf dem Knopf legte ab. Genau das war der Fehler.
   */
  useEffect(() => {
    if (!placementDrag) return

    const handlePointerUp = (e: PointerEvent) => {
      const target = e.target as Element | null
      if (target?.closest?.('.canvas-toolbar')) {
        // Der Zug ist zu Ende, auch wenn nichts abgelegt wurde. Bliebe er
        // stehen, tritt `handlePaneClick` beim nächsten Klick auf die Fläche
        // zurück (es wartet dann auf ein Loslassen, das nie kommt) — man
        // klickte, und nichts geschähe.
        onPlacementDragEnd()
        return
      }
      if (target?.closest?.('.react-flow')) {
        // `placementMode` ist während eines laufenden Zuges nie `null` — der
        // Zug startet erst, nachdem ein Werkzeug scharfgestellt wurde. Der
        // Wächter ist reine Typsicherheit.
        if (placementMode !== null) placeNoteAt({ x: e.clientX, y: e.clientY }, placementMode)
        return
      }
      onPlacementCancelled()
    }

    window.addEventListener('pointerup', handlePointerUp)
    return () => window.removeEventListener('pointerup', handlePointerUp)
  }, [placementDrag, placementMode, placeNoteAt, onPlacementCancelled, onPlacementDragEnd])

  // Im Hand-Werkzeug greift schon `panOnDrag` und damit React Flows eigener
  // Greif-Cursor — die Klasse wäre dort sinnlos.
  // ── Mehrfachauswahl ──────────────────────────────────────────────────────

  /** Die ausgewählten Notizen samt ihrer Lage. */
  // `accent`: die Farbe des Tauschpunkts — der Ring trägt die Farbe seines
  // Zettels (CONTEXT.md, Tauschpunkt). `data?.note`: React Flow trägt auch
  // Nodes ohne unsere Daten (siehe `selectedNotesOf`).
  const selectedRects = useMemo(
    () => nodes.filter(n => n.selected).map(n => ({
      id: n.id,
      rect: { ...n.position, ...sizeOf(n) },
      accent: getCanvasNoteRingColor(n.data?.note?.color),
      areaId: n.data?.note?.areaId ?? null,
      taped: Boolean(n.data?.note?.tape),
    })),
    [nodes, sizeOf],
  )
  const isMultiSelect = selectedRects.length >= 2

  // Die Kennungen der Auswahl als Schlüssel — `selectedRects` selbst wechselt
  // beim Ziehen in jedem Frame, die Auswahl nicht.
  const selectionKey = selectedRects.map(entry => entry.id).join('\u0000')
  useEffect(() => {
    applyZoomVars(true)
    // Wechselt die Auswahl, sind die Anlegepunkte weg — die Vorschau auch, und
    // mit ihr steht die gerückte Reihe wieder, wo sie war.
    cancelSpawnPreview()
  }, [selectionKey, cancelSpawnPreview, applyZoomVars])

  /**
   * Die ausgewählten Elemente selbst — für die gemeinsame **Options-Leiste**
   * über dem **Auswahlrahmen**, die Farben, Schriften und Zustände zeigt.
   */
  const selectedNotes = useMemo(() => selectedNotesOf(nodes), [nodes])

  /*
    Die Sammel-Aktionen lesen die Nodes über das vorhandene `nodesRef` statt
    über ihre Abhängigkeitsliste: `nodes` wechselt beim Ziehen in jedem Frame,
    und mit ihm bekäme der `SelectionOverlay` fünf frische Funktionen je Frame
    — Arbeit, die niemand braucht, denn geklickt wird erst nach dem Loslassen.
  */

  /*
    Die Sammel-Änderungen der gemeinsamen Leiste.

    **Ein Schritt, nicht n:** Wer fünf Zettel auf einmal einfärbt, hat eine
    Entscheidung getroffen, und ⌘Z soll genau die zurücknehmen — nicht fünfmal
    dieselbe. `recordStep` sammelt deshalb alle Änderungen in EINEN Schritt.
  */
  const bulkEdit = useCallback((change: (note: CanvasNote) => UpdateCanvasNote | null) => {
    const notes = selectedNotesOf(nodesRef.current)
    recordStep(step => {
      for (const note of notes) {
        const after = change(note)
        if (!after) continue
        const before = Object.fromEntries(
          Object.keys(after).map(key => [key, note[key as keyof CanvasNote]]),
        ) as UpdateCanvasNote
        step.update(note.id, before, after)
        trackNoteWrite(dataSource.updateCanvasNote(note.id, after))
        rememberNoteDefaults(note.kind, after)
      }
    })
  }, [recordStep, trackNoteWrite, rememberNoteDefaults, dataSource])

  // Gemischte Stufen bleiben gemischt: Nur die Farbe wechselt (siehe `bulkColorFor`).
  const handleBulkColor = useCallback((color: string) => {
    const apply = bulkColorFor(color, selectedNotesOf(nodesRef.current).map(n => n.color))
    bulkEdit(note => ({ color: apply(note.color), ...clearedCanvasTextStyle(note, 'color') }))
  }, [bulkEdit])

  // Dieselbe Rechnung wie `handleBulkColor` / `handleBulkColorReset`, nur ohne Schreiben.
  const handleBulkColorPreview = useCallback((preview: CanvasColorPreview) => {
    if (preview === null || preview === 'picked') {
      setBulkColorPreview(preview)
      return
    }
    const notes = selectedNotesOf(nodesRef.current)
    const colors = new Map<string, string | null>()
    if (preview.color === null) {
      for (const note of notes) if (note.kind === 'text') colors.set(note.id, null)
    } else {
      const apply = bulkColorFor(preview.color, notes.map(n => n.color))
      for (const note of notes) colors.set(note.id, apply(note.color))
    }
    setBulkColorPreview(colors)
  }, [setBulkColorPreview])

  const handleBulkFontPreview = useCallback((preview: CanvasFontPreview) => {
    if (preview === null || preview === 'picked') {
      setBulkFontPreview(preview)
      return
    }
    // Der Bereich hat keine Schrift — ihn meint die Wahl nicht.
    const ids = new Set(selectedNotesOf(nodesRef.current).filter(n => n.kind !== 'area').map(n => n.id))
    setBulkFontPreview({ ids, font: preview.font })
  }, [setBulkFontPreview])

  const handleBulkFont = useCallback((font: string | null) => {
    bulkEdit(note => ({ font, ...clearedCanvasTextStyle(note, 'font') }))
  }, [bulkEdit])

  // Die Größe gibt es nur am **Text** — die Leiste zeigt sie nur, wenn alle es sind.
  const handleBulkFontSize = useCallback((fontSize: number) => {
    bulkEdit(note => (note.kind === 'text' ? { fontSize, ...clearedCanvasTextStyle(note, 'size') } : null))
  }, [bulkEdit])

  const handleBulkColorReset = useCallback(() => {
    bulkEdit(note => (note.kind === 'text' ? { color: null, ...clearedCanvasTextStyle(note, 'color') } : null))
  }, [bulkEdit])

  /*
    Umschalter kippen GEMEINSAM, nicht jeder für sich: Zeigen alle ihren Titel,
    schaltet der Klick alle aus; sonst schaltet er alle an. Ein Umschalter, der
    aus einer gemischten Auswahl eine noch gemischtere machte, hätte nichts
    umgeschaltet.
  */
  const handleBulkToggleTitle = useCallback(() => {
    const notes = selectedNotesOf(nodesRef.current)
    const allOn = notes.every(note => note.title !== null)
    bulkEdit(note => (note.kind === 'memo' ? { title: allOn ? null : (note.title ?? '') } : null))
  }, [bulkEdit])

  const handleBulkToggleAuthor = useCallback(() => {
    const notes = selectedNotesOf(nodesRef.current)
    const allOn = notes.every(note => note.showAuthor)
    bulkEdit(() => ({ showAuthor: !allOn }))
  }, [bulkEdit])

  const handleBulkDelete = useCallback(() => {
    const notes = selectedNotesOf(nodesRef.current)
    const now = new Date()
    const deletedAreas = new Set(notes.filter(n => n.kind === 'area').map(n => n.id))
    recordStep(step => {
      for (const note of notes) {
        step.deleted(note.id, now)
        void dataSource.deleteCanvasNote(note.id)
      }
      // Die Zettel gelöschter Bereiche werden frei — sofern sie nicht selbst gehen.
      for (const n of nodesRef.current) {
        const areaId = n.data?.note?.areaId
        if (!areaId || !deletedAreas.has(areaId) || n.selected) continue
        step.update(n.id, { areaId }, { areaId: null })
        void dataSource.updateCanvasNote(n.id, { areaId: null })
      }
    })
  }, [recordStep, dataSource])

  /*
    Die Auswahl nach außen melden — sie lebt hier, gebraucht wird sie im Panel
    (für den Zustand des Übernehmen-Knopfs).

    Als KENNUNGEN und nur bei echter Änderung: `selectedRects` wird bei jedem
    Pixel eines Zugs neu gerechnet, die Menge der Ausgewählten ändert sich
    dabei nicht. Ohne den Vergleich feuerte die Meldung durch die ganze Geste
    hindurch und zöge das Panel bei jedem Frame neu.
  */
  const reportedSelectionRef = useRef<string>('')
  useEffect(() => {
    if (!onSelectionChange) return
    const ids = selectedRects.map(entry => entry.id)
    const key = ids.join(',')
    if (key === reportedSelectionRef.current) return
    reportedSelectionRef.current = key
    onSelectionChange(ids)
  }, [selectedRects, onSelectionChange])

  const clearSelection = useCallback(() => {
    // Das **Angetippte** geht mit: Es ist keine Auswahl, aber es ist dasselbe
    // „was gerade gemeint ist", und ein Escape meint beides.
    setTappedId(null)
    setNodes(ns => (ns.some(n => n.selected)
      ? ns.map(n => (n.selected ? { ...n, selected: false } : n))
      : ns))
  }, [setNodes])

  /**
   * Escape nimmt **zuerst die Vorschau** der **Anlegepunkte** zurück und erst
   * beim zweiten Mal die Auswahl: Ein Abbruch beendet die Geste, nicht das,
   * woran sie hing — dieselbe Reihenfolge wie beim **Platz tauschen**.
   */
  const handleEscape = useCallback(() => {
    if (spawnPreviewRef.current) {
      cancelSpawnPreview()
      return
    }
    clearSelection()
  }, [cancelSpawnPreview, clearSelection])

  useCanvasEscape({
    enabled: isActiveTab && tool === 'select',
    container: canvasElement,
    onEscape: handleEscape,
  })

  /** Entf/Backspace löscht die ausgewählten Notizen. */
  const handleNodesDelete = useCallback((deleted: Node<NoteNodeData>[]) => {
    const at = new Date()
    recordStep(step => {
      for (const node of deleted) {
        step.deleted(node.id, at)
        void dataSource.deleteCanvasNote(node.id)
      }
    })
  }, [recordStep, dataSource])

  /*
    Entf/Backspace am CANVAS-Element statt über React Flows `deleteKeyCode`.

    Dessen Weg horcht am Dokument und fragt allein, ob der Tastendruck aus
    einem Eingabefeld kommt — nicht, wo der Fokus liegt. Wer eine Notiz
    auswählte, dann in der **Aufgabenliste** eine Aufgabe anklickte und Entf
    drückte, löschte BEIDES. Ausführlich in `useCanvasDeleteShortcut`.

    Gelöscht wird über React Flows eigenes `deleteElements`, damit derselbe
    `onNodesDelete`-Weg läuft wie zuvor — die Buchführung fürs **Rückgängig**
    steht dort und soll nicht ein zweites Mal existieren.
  */
  const handleDeleteKey = useCallback(() => {
    const selected = nodesRef.current.filter(n => n.selected)
    if (selected.length === 0) return
    void deleteElements({ nodes: selected.map(n => ({ id: n.id })) })
  }, [deleteElements])

  useCanvasDeleteShortcut({
    enabled: isActiveTab && tool === 'select',
    container: canvasElement,
    onDelete: handleDeleteKey,
  })

  /** Live während des Ziehens am Gruppenrahmen. */
  const applyGroupRects = useCallback((next: { id: string; rect: MovingRect }[]) => {
    // Auch dieser Weg sperrt ⌘D (siehe `gestureRef`): `SelectionOverlay` holt
    // sich beim Erscheinen den Fokus, der Tastendruck erreicht das
    // Canvas-Element also auch während einer Gruppen-Geste.
    gestureRef.current = true
    const byId = new Map(next.map(entry => [entry.id, entry.rect]))
    setNodes(ns => ns.map(node => {
      const rect = byId.get(node.id)
      if (!rect) return node
      return {
        ...node,
        position: { x: rect.x, y: rect.y },
        width: rect.width,
        height: rect.height,
        measured: { width: rect.width, height: rect.height },
      }
    }))
  }, [setNodes])

  /**
   * Die Linien einer Gruppen-Geste. Sie kommen aus dem Hook heraus, gesetzt
   * werden sie hier: `guides` und `measures` gehören dem Canvas.
   */
  const showAlignment = useCallback((lines: AlignmentLines) => {
    if (!sameGuides(lines.vertical, guides.vertical) ||
        !sameGuides(lines.horizontal, guides.horizontal)) {
      setGuides({ vertical: lines.vertical, horizontal: lines.horizontal })
    }
    if (!sameMeasures(lines.measures, measures)) setMeasures(lines.measures)
  }, [guides, measures])

  /**
   * Die Mitglieder der ausgewählten **Bereiche**, die nicht selbst ausgewählt
   * sind — auf der Lage, die ihnen der aktuelle Stand des Rahmens gibt: der
   * Ausgangsplatz plus der Weg ihres Bereichs (CONTEXT.md: der Bereich trägt).
   * Immer alle, auch bei Weg null — beim Abbruch ist `next` die Ausgangslage,
   * und die Vorschau hat sie schon verschoben.
   */
  const carriedGroupRects = useCallback((next: { id: string; rect: MovingRect }[]) => {
    const before = new Map(groupStartRef.current.map(entry => [entry.id, entry.rect]))
    const now = new Map(next.map(entry => [entry.id, entry.rect]))
    const carried: { id: string; rect: MovingRect }[] = []
    for (const [id, start] of groupCarriedRef.current) {
      const from = before.get(start.areaId)
      const to = now.get(start.areaId)
      if (!from || !to) continue
      carried.push({
        id,
        rect: { x: start.x + (to.x - from.x), y: start.y + (to.y - from.y), width: start.width, height: start.height },
      })
    }
    return carried
  }, [])

  const changeGroupRects = useCallback((next: { id: string; rect: MovingRect }[], lines: AlignmentLines) => {
    // Die Mitglieder rücken schon unterwegs mit, nicht erst beim Loslassen —
    // wie beim Zug am Bereich selbst. Für den Umriss zählen nur die Bewegten:
    // Ein getragenes Mitglied bleibt, wo es ist — in seinem Bereich.
    applyGroupRects([...next, ...carriedGroupRects(next)])
    showAlignment(lines)
    previewAdoption(next)
  }, [applyGroupRects, carriedGroupRects, showAlignment, previewAdoption])

  const commitGroupRects = useCallback((next: { id: string; rect: MovingRect }[]) => {
    const carriedRects = carriedGroupRects(next)
    applyGroupRects([...next, ...carriedRects])
    // Ab hier gilt wieder die Datenbank (siehe `gestureHeldRef`).
    gestureHeldRef.current = null
    setGroupLiftIds(NO_LIFT)
    clearAdoption()
    setGuides(NO_GUIDES)
    setMeasures(NO_MEASURES)
    gestureRef.current = false
    // Die Ausgangslage der GANZEN Auswahl, eingefroren beim Anfassen
    // (`onGestureStart`). Ein Rückgängig setzt alle zusammen zurück — der
    // Rahmen ist eine Geste, nicht so viele wie er Notizen umschließt.
    const before = new Map(groupStartRef.current.map(entry => [entry.id, entry.rect]))
    /*
      Ein **Bereich** im Auswahlrahmen trägt seine Mitglieder mit (CONTEXT.md)
      — auch hier, wo der Rahmen und nicht der Bereich gezogen wurde: Wer
      nicht selbst ausgewählt ist, rückt um den Weg seines Bereichs. Ein
      Zettel rückt nie zweimal — ein ausgewähltes Mitglied steht in `next`
      und wird hier übersprungen.
    */
    const moving = new Set(next.map(entry => entry.id))
    // Geschrieben wird nur, wer sich bewegt hat — gerechnet gegen die
    // AUSGANGSLAGE aus `groupCarriedRef`, nicht gegen die Nodes: Die tragen
    // durch die Vorschau längst die neue Lage.
    const carried = carriedRects
      .map(({ id, rect }) => ({ id, x: rect.x, y: rect.y, start: groupCarriedRef.current.get(id)! }))
      .filter(entry => entry.x !== entry.start.x || entry.y !== entry.start.y)
    recordStep(step => {
      for (const { id, rect } of next) {
        const start = before.get(id)
        if (start && (start.x !== rect.x || start.y !== rect.y
          || start.width !== rect.width || start.height !== rect.height)) {
          step.update(
            id,
            { x: start.x, y: start.y, width: start.width, height: start.height },
            { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
          )
        }
        void dataSource.updateCanvasNote(id, {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        })
      }
      for (const { id, x, y, start } of carried) {
        step.update(id, { x: start.x, y: start.y }, { x, y })
        void dataSource.updateCanvasNote(id, { x, y })
      }
      // Zugehörigkeit neu entscheiden (ADR 0012): die Bewegten — und alle,
      // sobald ein Bereich dabei war.
      for (const { id, rect } of next) lastPositionRef.current.set(id, { x: rect.x, y: rect.y })
      for (const { id, x, y } of carried) lastPositionRef.current.set(id, { x, y })
      const areaMoved = next.some(({ id }) => nodesRef.current.find(n => n.id === id)?.data?.note?.kind === 'area')
      settleAreas(step, areaMoved ? null : new Set([...moving, ...carried.map(c => c.id)]))
      for (const { id } of next) lastPositionRef.current.delete(id)
      for (const { id } of carried) lastPositionRef.current.delete(id)
    })
    const copies = groupCopiesRef.current
    groupCopiesRef.current = null
    if (copies) handOverTaskLink(copies)
  }, [applyGroupRects, carriedGroupRects, handOverTaskLink, recordStep, settleAreas, clearAdoption, dataSource])

  /**
   * Alt + Ziehen an der Greiffläche der Mehrfachauswahl. Die Ausgangslage kommt
   * vom Hook — dort gehören die Kopien hin, nicht an die Stelle, an der die
   * Schwelle fiel.
   */
  const duplicateGroup = useCallback((start: { id: string; rect: MovingRect }[]) => {
    const byId = new Map(nodesRef.current.map(node => [node.id, node]))
    const entries = start
      .map(entry => ({ node: byId.get(entry.id), rect: entry.rect }))
      .filter((entry): entry is { node: Node<NoteNodeData>; rect: MovingRect } => Boolean(entry.node))
      .map(({ node, rect }) => ({ node, x: rect.x, y: rect.y }))
    const ids = duplicateNodes(entries)
    groupCopiesRef.current = new Map(entries.map((entry, index) => [entry.node.id, ids[index]!]))
  }, [duplicateNodes])

  // Die Ausgangslage kommt über ein Ref, nicht über `selectedRects`: Der Hook
  // liest sie EINMAL beim Anfassen, und `selectedRects` ändert sich während des
  // Ziehens jeden Frame.
  const selectedRectsRef = useRef(selectedRects)
  selectedRectsRef.current = selectedRects

  /** Die Lage der Auswahl beim Anfassen des Rahmens — für das Rückgängig. */
  const groupStartRef = useRef<{ id: string; rect: MovingRect }[]>([])
  /**
   * Was der Rahmen außerdem trägt: die nicht ausgewählten Mitglieder der
   * ausgewählten Bereiche, mit Ausgangslage und Maßen — für die Vorschau, das
   * Rückgängig und das Zurückstellen beim Abbruch (siehe `carriedGroupRects`).
   */
  const groupCarriedRef = useRef<Map<string, { areaId: string; x: number; y: number; width: number; height: number }>>(new Map())

  const { startResize, startMove } = useGroupDrag({
    getRects: useCallback(() => selectedRectsRef.current, []),
    toFlow: screenToFlowPosition,
    // Beim Anfassen einmal einsammeln, was NICHT mitbewegt wird — derselbe
    // Weg wie bei `handleNodeDragStart`.
    onGestureStart: useCallback((start: { id: string; rect: MovingRect }[]) => {
      const ids = new Set(start.map(entry => entry.id))
      // Für das Rückgängig: Beim Loslassen ist die Ausgangslage längst
      // überschrieben (`applyGroupRects` schreibt je Bewegung in die Nodes).
      groupStartRef.current = start
      // Die Mitglieder der ausgewählten Bereiche fahren mit (CONTEXT.md) —
      // ein ausgewähltes Mitglied steht in `start` und rückt nie zweimal.
      const areas = new Set(nodesRef.current.filter(n => ids.has(n.id) && n.data?.note?.kind === 'area').map(n => n.id))
      const carried = new Map<string, { areaId: string; x: number; y: number; width: number; height: number }>()
      for (const n of nodesRef.current) {
        const areaId = n.data?.note?.areaId
        if (!areaId || !areas.has(areaId) || ids.has(n.id)) continue
        carried.set(n.id, { areaId, ...n.position, ...sizeOf(n) })
      }
      groupCarriedRef.current = carried
      gestureHeldRef.current = new Set([...ids, ...carried.keys()])
      setGroupLiftIds(ids)
      // Was mitwandert, ist kein Ausrichtungspartner — es stünde sonst
      // ständig fluchtend neben sich selbst.
      collectAlignTargets(new Set([...ids, ...carried.keys()]), areas.size > 0)
    }, [collectAlignTargets, sizeOf]),
    // Je Bewegung neu gefiltert, nicht einmal beim Anfassen: Der Ausschnitt
    // kann sich unterwegs verschieben (siehe `handleNodesChange`).
    getAlignTargets: useCallback(
      () => alignTargetsRef.current.filter(b => isVisible(b, viewportBounds())),
      [viewportBounds],
    ),
    threshold: useCallback(() => ALIGNMENT_THRESHOLD / (getZoom() || 1), [getZoom]),
    onChange: changeGroupRects,
    onCommit: commitGroupRects,
    limits: SIZE_LIMITS,
    grid: GRID,
    onDuplicate: duplicateGroup,
  })
  startMoveRef.current = startMove

  /* ── Platz tauschen ──────────────────────────────────────────────────────
     Die Geste am **Tauschpunkt**: ein ausgewähltes Element tauscht seinen
     Platz mit einem anderen — ab zwei Ausgewählten mit einem der Auswahl,
     bei EINEM mit jedem Zettel, der beim Anfassen im Ausschnitt liegt
     (`swapCandidates`). Siehe `useSwapDrag` und `utils/canvasSwap`.
  */

  /**
   * Der Rahmen, wie er beim Anfassen war. Solange gesetzt, läuft ein Tausch.
   *
   * Er friert ein, weil ein Tausch nur INNERHALB der Auswahl bewegt: Vorher
   * und nachher ist dasselbe Rechteck belegt. Ein Rahmen, der währenddessen
   * dem gezogenen Element hinterherwächst und am Ende zurückschnappt, hätte
   * nur gezappelt.
   */
  const [swapBox, setSwapBox] = useState<Bounds | null>(null)
  const [swapDraggedId, setSwapDraggedId] = useState<string | null>(null)

  /** Die Lage vor dem Tausch, für den Abbruch. */
  const swapStartRef = useRef<{ id: string; rect: MovingRect }[]>([])
  /** Die **Ableg-Flächen** des Zettels nahe dem Zeiger, und welche getroffen ist. */
  const [swapZones, setSwapZones] = useState<SwapZone[]>([])
  const [swapHit, setSwapHit] = useState<SwapZone | null>(null)
  /** Die **Zielfläche** — wo der Getragene beim Loslassen liegt (siehe `SwapPreview.landing`). */
  const [swapLanding, setSwapLanding] = useState<MovingRect | null>(null)
  /**
   * Die **Fuge** der laufenden Geste (siehe `SwapSeam`). Sie sagt, dass gerade
   * EINGEREIHT und nicht getauscht wird — beide bewegen fremde Zettel, und bei
   * zwei Nachbarn ist das dieselbe Strecke in dieselbe Richtung. Den Tausch
   * markiert der Partner selbst (`SWAP_PARTNER_CLASS`).
   */
  const [swapSeam, setSwapSeam] = useState<SwapSeam | null>(null)
  /*
    Sie blendet ab, statt zu verschwinden — und dafür muss sie noch dastehen,
    während sie es tut. Der Wecker räumt sie danach weg; ein neues Angebot
    stellt ihn ab (siehe `showSeam`).
  */
  const seamTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showSeam = useCallback((next: SwapSeam | null) => {
    clearTimeout(seamTimerRef.current ?? undefined)
    seamTimerRef.current = null
    if (next) {
      setSwapSeam({ ...next, on: true })
      return
    }
    setSwapSeam(current => {
      if (current === null || !current.on) return current
      seamTimerRef.current = setTimeout(() => {
        seamTimerRef.current = null
        showSeam(null)
      }, SEAM_FADE_MS)
      return { ...current, on: false }
    })
  }, [])
  useEffect(() => () => { clearTimeout(seamTimerRef.current ?? undefined) }, [])
  /**
   * Der Ring-Ton des GETRAGENEN Zettels, für seine **Fuge**. Einmal beim
   * Anfassen gelesen: Die Farbe kann sich während einer Geste nicht ändern,
   * und je Bewegung in den Nodes zu suchen wäre Arbeit für nichts.
   */
  const swapInkRef = useRef<{ accent: string }>({ accent: '' })

  /**
   * Setzt die Lagen einer Vorschau oder eines Abbruchs in die Nodes — und hebt
   * das getragene Canvas-Element über alle anderen.
   *
   * **Warum das Anheben nötig ist:** React Flow rechnet
   * `zIndex + (selected ? 1000 : 0)` (`calculateZ` in @xyflow/system). Alle
   * Ausgewählten bekommen also DIESELBEN +1000, und innerhalb der Auswahl
   * entscheidet weiter der `zIndex` nach Alter (`noteToFlowNode`). Ein
   * getragener Notiz verschwände damit hinter jedem jüngeren, über den er
   * gerade hinwegzieht.
   *
   * @param liftedId das getragene Element; `null` stellt die Stapelung wieder her
   * @param gliding  wer GLEITET statt springt — die Reihe beim **Dazwischen
   *                 schieben**, hin wie zurück (`SHIFT_CLASS`, wie beim
   *                 Dazwischen anlegen). Wer NICHT darin steht und die Klasse
   *                 noch trägt, legt sie ab: Wer ein gerade abgesetztes,
   *                 noch gleitendes Element sofort wieder anfasst, soll es
   *                 ohne Verzug am Zeiger haben. Der Aufrufer ruft danach
   *                 `endShift`.
   * @param dockingId wer gerade ANDOCKT und deshalb seinen Auswahl-Rahmen
   *                 ablegt (siehe `DOCKING_CLASS`)
   * @param yielding wer durch die Geste AUSWEICHT und darum den stillen
   *                 **Umriss** trägt (siehe `YIELDING_CLASS`)
   */
  const applySwapPositions = useCallback((
    positions: Map<string, XYPosition>,
    liftedId: string | null,
    gliding: ReadonlySet<string> | null = null,
    partnerId: string | null = null,
    fadingId: string | null = null,
    dockingId: string | null = null,
    yielding: ReadonlySet<string> | null = null,
  ) => {
    // Damit der Node-Abgleich die Höhe nicht gleich wieder einkassiert.
    swapLiftedRef.current = liftedId
    setNodes(ns => ns.map((node, index) => {
      const position = positions.get(node.id)
      // Zurück auf den Stapelplatz: derselbe Wert, den `noteToFlowNode`
      // vergibt — der Node-Index. Die Liste ist nach `compareZ` sortiert, der
      // Index IST vorn und hinten.
      const zIndex = node.id === liftedId ? SWAP_LIFTED_Z : index
      const moves = position !== undefined && (node.position.x !== position.x || node.position.y !== position.y)
      /*
        **Wer gleitet, gleitet zu Ende.** Nicht nur, wer sich in DIESEM Bild
        rührt: Die Vorschau läuft bei jeder Zeigerbewegung, und im nächsten
        Bild steht der Partner schon auf seinem neuen Platz. Hinge die Klasse
        an `moves`, fiele sie dort weg — und mit ihr die `transition`, die sie
        trägt. Ein laufender Übergang ohne `transition`-Angabe bricht sofort
        ab; von den 240 ms sah man nichts, der Zettel sprang (gemeldet
        2026-09-05: „man erkennt nicht, dass ein Partner seine Position
        wechselt zu der Stelle, wo ich gerade einen Zettel weggenommen habe").

        Behalten wird sie, bis die Gleit-Menge ihn nicht mehr führt: Loslassen
        und Abbruch rufen `applySwapPositions(…, null)`, danach nimmt
        `endShift` sie ab. Wer sich nie gerührt hat, bekommt sie weiterhin
        nicht — die Zahl der neu gezeichneten Nodes bleibt dieselbe.
      */
      const glide = gliding !== null && gliding.has(node.id)
        && (moves || carries(node.className, SHIFT_CLASS))
      // Der **Umriss** kommt ZUSÄTZLICH zum Gleiten: Der Partner tut beides
      // zugleich — er trägt die Marke und rutscht auf den Platz des
      // Getragenen.
      const marked = node.id === partnerId
      // Und der Getragene wird durchscheinend, solange die **Fuge** steht.
      const fading = node.id === fadingId
      // Angedockt nimmt er seinen Auswahl-Rahmen ab (siehe `DOCKING_CLASS`).
      const docking = node.id === dockingId
      // Und wer nur Platz macht, trägt den stillen Umriss.
      const yields = yielding !== null && yielding.has(node.id)
      // Er bleibt gewachsen, solange er getragen wird — der Zeiger steht ja auf
      // seinem Ring, seit er ihn angefasst hat. Und danach weiter, wenn die
      // Hand dort liegen bleibt (`ringHoverRef`).
      const swollen = node.id === (liftedId ?? ringHoverRef.current)
      const wanted = [
        glide && SHIFT_CLASS,
        marked && SWAP_PARTNER_CLASS,
        fading && SWAP_FADE_CLASS,
        swollen && RING_HOVER_CLASS,
        docking && DOCKING_CLASS,
        yields && YIELDING_CLASS,
      ].filter(Boolean).join(' ') || undefined
      // Angefasst wird die Klasse nur, wo sie dem Ring gehört (siehe `ringOwns`).
      const restyle = (glide || marked || fading || swollen || docking || yields || ringOwns(node.className))
        && wanted !== node.className
      if (!position && node.zIndex === zIndex && !restyle) return node
      return {
        ...node,
        ...(position ? { position } : {}),
        zIndex,
        ...(restyle ? { className: wanted } : {}),
      }
    }))
  }, [setNodes])

  /**
   * Ob eine Geste am Punkt die GRUPPE meint: ab zwei Ausgewählten, und das
   * gezogene Element gehört dazu. Sonst tauscht der eine mit dem Ausschnitt.
   */
  const isGroupSwap = useCallback((dragged: string) => {
    const selected = selectedRectsRef.current
    return selected.length >= 2 && selected.some(entry => entry.id === dragged)
  }, [])

  const { startSwap } = useSwapDrag({
    /*
      Wer Partner sein kann, hängt an der Auswahl (CONTEXT.md, „Tauschpunkt"):
      ab zwei Ausgewählten die Auswahl selbst; sonst JEDER andere Zettel des
      Canvas — auch außerhalb des Bildes, der Ausschnitt fährt am Rand mit
      (`autoPan` unten). In Node-Reihenfolge, damit `findSwapTarget` weiter
      das Oberste gewinnen lässt.
    */
    getEntries: useCallback((dragged: string) => {
      if (isGroupSwap(dragged)) return selectedRectsRef.current
      return nodesRef.current
        .filter(n => n.data?.note?.kind !== 'area')
        // Die Zugehörigkeit muss mit: An der Bereichskante endet eine **Reihe**
        // (CONTEXT.md, Bereich; `rowAfter` in `canvasSpawn.ts`). Und der
        // **Klebestreifen**: Ein geklebter Zettel ist dieselbe **Wand** und
        // obendrein kein Tauschpartner.
        .map(n => ({ id: n.id, rect: { ...n.position, ...sizeOf(n) }, areaId: n.data?.note?.areaId ?? null, taped: Boolean(n.data?.note?.tape) }))
    }, [isGroupSwap, sizeOf]),
    toFlow: screenToFlowPosition,
    // Die Bänder der Ableg-Flächen sind bildschirmgroß — gerechnet in Canvas-Einheiten.
    getZoom: useCallback(() => storeApi.getState().transform[2], [storeApi]),
    onStart: useCallback((start: { id: string; rect: MovingRect }[], dragged: string) => {
      // Auch dieser Weg sperrt ⌘D, wie die Gruppen-Gesten (siehe `gestureRef`).
      gestureRef.current = true
      swapStartRef.current = start
      // Die Vorschau setzt ALLE Kandidaten je Bewegung neu — gehalten werden sie alle (siehe `gestureHeldRef`).
      gestureHeldRef.current = new Set(start.map(entry => entry.id))
      const color = nodesRef.current.find(node => node.id === dragged)?.data?.note?.color ?? null
      swapInkRef.current = { accent: getCanvasNoteRingColor(color) }
      if (isGroupSwap(dragged)) {
        // Einzufrieren gibt es nur einen Rahmen, und den erst ab zwei — die
        // Kandidaten der Einzelauswahl sind keine Auswahl.
        setSwapBox(getBoundingBox(start.map(entry => entry.rect)))
      } else {
        // Vom nur überfahrenen Zettel aus: Anfassen wählt ihn aus, wie ein
        // Klick — der Tauschpunkt schluckt den Klick ja (`startSwap`).
        storeApi.getState().addSelectedNodes([dragged])
        // Für den freien Zustand (**Verschieben**): woran ausgerichtet wird,
        // steht beim Anfassen fest — derselbe Weg wie in
        // `handleNodeDragStart` und bei den Gruppen-Gesten.
        collectAlignTargets(new Set([dragged]))
      }
    }, [collectAlignTargets, isGroupSwap, storeApi]),
    // Schieben darf nur der Einzelne — die Gruppe tauscht unter Mitgliedern
    // und fährt nicht an den Rand (CONTEXT.md, Dazwischen schieben).
    canShove: useCallback((dragged: string) => !isGroupSwap(dragged), [isGroupSwap]),
    /*
      **Verschieben** statt Andocken (CONTEXT.md, Tauschpunkt): Wo keine
      Fläche mehr trifft, rastet der Getragene ein wie am Zettelkörper. Je
      Bewegung neu gefiltert, nicht einmal beim Anfassen — der Ausschnitt
      fährt am Rand mit, und sichtbar ist danach anderes.
    */
    align: useMemo(() => ({
      getTargets: () => alignTargetsRef.current.filter(b => isVisible(b, viewportBounds())),
      threshold: () => ALIGNMENT_THRESHOLD / (getZoom() || 1),
      grid: GRID,
    }), [getZoom, viewportBounds]),
    onPreview: useCallback((preview: SwapPreview) => {
      setSwapDraggedId(preview.dragged.id)
      setSwapZones(preview.zones)
      setSwapHit(preview.hit)
      setSwapLanding(preview.landing)
      // Alles auf die Ausgangslage zurück, dann die Vorschau darüber: Wandert
      // der Zeiger von einem Partner zum nächsten, muss der vorige an seinen
      // Platz zurück — sonst blieben Elemente auf halbem Weg liegen. Beim
      // Schieben rückt vor dem Loslassen niemand; nur die Fläche hebt sich.
      const positions = new Map(swapStartRef.current.map(entry => [entry.id, { x: entry.rect.x, y: entry.rect.y }]))
      positions.set(preview.dragged.id, { x: preview.dragged.x, y: preview.dragged.y })
      // Frei getragen: der Aufnahme-Umriss am Bereich, der ihn bekäme. Beim
      // Andocken nicht — dort ordnet der Canvas, und der Zettel landet im Feld.
      const draggedNode = nodesRef.current.find(n => n.id === preview.dragged.id)
      if (preview.free && draggedNode) {
        previewAdoption([{ id: preview.dragged.id, rect: { x: preview.dragged.x, y: preview.dragged.y, ...sizeOf(draggedNode) } }])
      } else {
        clearAdoption()
      }
      if (preview.target) positions.set(preview.target.id, { x: preview.target.x, y: preview.target.y })
      // Das Angebot am Band: Die Lücke geht auf, beide Seiten weichen aus.
      for (const move of preview.spread) positions.set(move.id, { x: move.x, y: move.y })
      // Der Partner GLEITET auf den Platz des Getragenen — hin wie zurück —,
      // wie die Reihe beim Dazwischen anlegen; nur das Getragene selbst hängt
      // ohne Verzug am Zeiger. Ein noch laufender Wecker vom vorigen Gleiten
      // nähme die Klasse mitten in der Geste ab: weg damit, `endShift` kommt
      // beim Loslassen.
      if (shiftTimerRef.current) {
        clearTimeout(shiftTimerRef.current)
        shiftTimerRef.current = null
      }
      const gliding = new Set(swapStartRef.current.map(entry => entry.id))
      gliding.delete(preview.dragged.id)
      // Der Partner bekommt den **Umriss** — die Marke des Tauschs; und der
      // Getragene wird durchscheinend, solange die **Fuge** steht.
      applySwapPositions(
        positions,
        preview.dragged.id,
        gliding,
        preview.target?.id ?? null,
        preview.seam ? preview.dragged.id : null,
        // Angedockt heißt: eine Fläche ist getroffen — dann kein blauer Rahmen.
        preview.free ? null : preview.dragged.id,
        new Set(preview.yielding),
      )
      // Hilfslinien und Maßklammern gehören dem freien Zustand; beim Andocken
      // ordnet der Canvas selbst, dort sind sie leer (`NO_LINES` im Hook).
      showAlignment(preview.lines)
      // Die **Fuge** — die Marke des Einreihens. Den Tausch markiert der
      // Partner selbst, mit seinem **Umriss** (oben).
      showSeam(preview.seam ? { seam: preview.seam, accent: swapInkRef.current.accent, on: true } : null)
    }, [applySwapPositions, showAlignment, showSeam, previewAdoption, clearAdoption, sizeOf]),
    onCommit: useCallback((moved: { id: string; x: number; y: number }[]) => {
      // VOR dem Absetzen lesen — `applySwapPositions(…, null)` löscht es.
      const dragged = swapLiftedRef.current
      gestureHeldRef.current = null
      // Erst jetzt bewegt sich etwas: Partner und gerückte Reihe GLEITEN an
      // ihren Platz (`SHIFT_CLASS`), das Getragene springt hinein.
      // Erst alle zurück auf die Ausgangslage, dann das Ergebnis darüber: Wer
      // nur ausgewichen war (das Angebot) und seinen Platz behält, gleitet von
      // dort zurück — dieselbe Bewegung rückwärts. Und das Getragene GLEITET
      // vom Zeiger an seinen Platz, wie ein Magnet: Das Einrasten gehört dem
      // Loslassen, nicht dem Ziehen (CONTEXT.md, Tauschpunkt, Leerplätze).
      const landing = new Map(swapStartRef.current.map(entry => [entry.id, { x: entry.rect.x, y: entry.rect.y }]))
      for (const entry of moved) landing.set(entry.id, { x: entry.x, y: entry.y })
      applySwapPositions(landing, null, new Set(swapStartRef.current.map(entry => entry.id)))
      endShift()
      setSwapZones([])
      setSwapHit(null)
      setSwapLanding(null)
      showSeam(null)
      setGuides(NO_GUIDES)
      setMeasures(NO_MEASURES)
      // Beide Notizen in EINEM Schritt: Ein Tausch ist eine Handlung, auch
      // wenn er zwei Zeilen schreibt. Die Ausgangslage steht in
      // `swapStartRef` — der eingefrorenen Liste, gegen die auch die
      // Treffersuche läuft.
      const before = new Map(swapStartRef.current.map(entry => [entry.id, entry.rect]))
      recordStep(step => {
        for (const { id, x, y } of moved) {
          const start = before.get(id)
          if (start) step.update(id, { x: start.x, y: start.y }, { x, y })
          void dataSource.updateCanvasNote(id, { x, y })
        }
        // Auch wer am **Tauschpunkt** landet — frei getragen, getauscht oder
        // eingereiht —, bekommt seinen **Bereich** neu entschieden (ADR 0012).
        // Die Landeplätze stehen noch nicht in den Nodes: kurz vormerken.
        for (const { id, x, y } of moved) lastPositionRef.current.set(id, { x, y })
        settleAreas(step, new Set(moved.map(entry => entry.id)))
        for (const { id } of moved) lastPositionRef.current.delete(id)
        // Nach ⌘Z zeigt die Auswahl auf das, was man angefasst hatte — der
        // Partner (und die gerückte Reihe) haben nur Platz gemacht. Aus der
        // Gruppe heraus bleibt es die Gruppe (CONTEXT.md, Rückgängig).
        if (dragged) step.select(isGroupSwap(dragged) ? selectedRectsRef.current.map(entry => entry.id) : [dragged])
      })
      setSwapBox(null)
      clearAdoption()
      setSwapDraggedId(null)
      gestureRef.current = false
    }, [applySwapPositions, endShift, isGroupSwap, recordStep, showSeam, settleAreas, clearAdoption, dataSource]),
    /*
      Nur der EINZELNE fährt an den Rand: Die Gruppe tauscht innerhalb der
      Auswahl, und die steht im Bild — fahren müsste sie nirgendwohin.
      Geschwindigkeit und Behälter aus React Flows Store, damit es sich genau
      so anfühlt wie das Ziehen einer Notiz (`autoPanOnNodeDrag`).
    */
    autoPan: useMemo(() => ({
      enabled: (dragged: string) => !isGroupSwap(dragged),
      getBounds: () => storeApi.getState().domNode?.getBoundingClientRect() ?? null,
      panBy: (delta: { x: number; y: number }) => storeApi.getState().panBy(delta),
      speed: () => storeApi.getState().autoPanSpeed,
    }), [isGroupSwap, storeApi]),
    onCancel: useCallback(() => {
      // Alle zurück an den Ursprung — gleitend, auch das Getragene: Escape
      // stellt zurück, es reißt nicht.
      gestureHeldRef.current = null
      applySwapPositions(
        new Map(swapStartRef.current.map(entry => [entry.id, { x: entry.rect.x, y: entry.rect.y }])),
        null,
        new Set(swapStartRef.current.map(entry => entry.id)),
      )
      endShift()
      setSwapZones([])
      setSwapHit(null)
      setSwapLanding(null)
      showSeam(null)
      setGuides(NO_GUIDES)
      setMeasures(NO_MEASURES)
      setSwapBox(null)
      clearAdoption()
      setSwapDraggedId(null)
      gestureRef.current = false
    }, [applySwapPositions, endShift, showSeam, clearAdoption]),
  })

  /*
    Wer einen Tauschpunkt trägt (CONTEXT.md, „Tauschpunkt"): ab zwei die
    Auswahl; dazu das getragene Element (der Zeiger eilt ihm beim Tragen
    voraus, der Zettel meldete sonst ein Verlassen); und, solange nichts
    getragen wird, der überfahrene Zettel. Beim Tragen NICHT der überfahrene:
    Das Ziel bekommt seinen Ring geliehen, alles andere wäre Gezappel.

    **Unter der Hand niemand** (CONTEXT.md, Werkzeug-Pille). Die Hand nimmt den
    Nodes ihre Pointer-Events und hebt die Auswahl auf — der Ring aber lebt
    im `ViewportPortal`, außerhalb des Node-Baums, und `hoveredNoteId` bleibt
    stehen, wenn die Hand per `H` kommt, während der Zeiger auf dem Zettel
    steht: Kein Ereignis meldet dann ein Verlassen. Also entscheidet das
    Werkzeug, nicht der Zeiger. Eine LAUFENDE Geste behält ihren Ring — sie
    endet mit dem Loslassen, nicht mit dem Werkzeug. Gemeldet 2026-09-06.
  */
  const swapPointEntries = useMemo(() => {
    const smallText = (id: string) => {
      const node = nodes.find(n => n.id === id)
      return node?.data?.note?.kind === 'text' && sizeOf(node).height < MIN_CANVAS_TEXT_SWAP_HEIGHT
    }
    const points = selectedRects.length >= 2 ? selectedRects.filter(entry => !smallText(entry.id)) : []
    const add = (id: string | null) => {
      if (!id || points.some(entry => entry.id === id)) return
      const node = nodes.find(n => n.id === id)
      // Ein Bereich hat keinen **Tauschpunkt**: Er tauscht nicht und reiht sich
      // nicht ein. Ein **geklebter** Zettel auch nicht — der Punkt ist der
      // Griff zum Wegtragen, und tragen kann man ihn nicht mehr.
      // Ein **Text** unter 32px trägt keinen: Der Ring verdeckte das Wort (CONTEXT.md, Text).
      const tooSmall = node?.data?.note?.kind === 'text' && sizeOf(node).height < MIN_CANVAS_TEXT_SWAP_HEIGHT
      if (node && node.data?.note?.kind !== 'area' && !node.data?.note?.tape && !tooSmall) points.push({ id, rect: { ...node.position, ...sizeOf(node) }, accent: getCanvasNoteRingColor(node.data?.note?.color), areaId: node.data?.note?.areaId ?? null, taped: false })
    }
    add(swapDraggedId)
    if (!swapDraggedId && tool === 'select') add(hoveredNoteId)
    return points
  }, [selectedRects, nodes, sizeOf, swapDraggedId, hoveredNoteId, tool])

  // Der Doppelklick auf den Tauschpunkt öffnet die Bearbeitung — siehe
  // `EditRequest`.
  const handleSwapPointDoubleClick = useCallback((id: string) => {
    setEditRequest(current => ({ id, seq: (current?.seq ?? 0) + 1 }))
  }, [])

  /**
   * Einen Schritt anwenden — dieselbe Rechnung für beide Richtungen.
   *
   * **Fehlende Zeilen werden übersprungen, der Rest läuft.** Ein Schritt kann
   * auf eine Notiz zeigen, den die Cloud inzwischen gelöscht hat; ein
   * halber Schritt ist besser als eine Fehlermeldung für etwas, das sich nicht
   * mehr ändern lässt. `updateCanvasNote` liefert dafür `null` zurück.
   *
   * Die Auswahl wandert auf die betroffenen Notizen, und liegt keiner von
   * ihnen ganz im Bild, rückt der Ausschnitt nach — derselbe Weg wie beim
   * Duplizieren (`pendingDuplicatesRef`). **Das ist der Ersatz für eine
   * Meldung:** Ein Rückgängig, dessen Wirkung außerhalb des Ausschnitts liegt,
   * sähe sonst aus wie „nichts passiert".
   */
  const applyHistory = useCallback((changes: { id: string; updates: UpdateCanvasNote }[], select: string[] | null) => {
    if (changes.length === 0) return
    for (const { id, updates } of changes) void dataSource.updateCanvasNote(id, updates)

    /*
      Die Auswahl wandert auf die betroffenen Notizen — über denselben Weg wie
      die Kopien: Die Live-Query meldet die neuen Stände asynchron, die Auswahl
      muss aber schon wissen, auf wen sie zeigt.

      **Nur auf die, die es danach noch gibt.** Nimmt ein Rückgängig ein
      Anlegen zurück, setzt es `deletedAt` — die Notiz verschwindet aus
      `notes`, und der wartende Effekt käme nie zum Zug: Er löst erst aus, wenn
      ALLE angemeldeten Zeilen eingetroffen sind. Der Ref bliebe für immer
      belegt und sperrte ⌘D und die Tab-Taste, die beide auf ihn prüfen.

      Bleibt nichts übrig, wird auch nichts angemeldet — ein Rückgängig, das
      nur verbirgt, hat keine Auswahl zu setzen.
    */
    const surviving = changes
      .filter(change => !(change.updates.deletedAt instanceof Date))
      .map(change => change.id)
    // Benennt der Schritt, worauf die Auswahl zeigt (Tausch, Schieben,
    // Anlegen: was man angefasst hatte), gilt das — sonst alle Betroffenen.
    const chosen = select ? select.filter(id => surviving.includes(id)) : surviving
    if (chosen.length === 0) return

    /*
      **Ein geklebter Zettel wird ANGETIPPT statt ausgewählt.** Rückgängig soll
      eine sichtbare Wirkung haben — das ist der Ersatz für eine Meldung —, aber
      er gerät in keine Auswahl (`selectable: false`), die Rückmeldung liefe
      also ins Leere. Angetippt ist der Weg, den **Tab** an ihm ohnehin geht:
      Seine Leiste geht auf, und man sieht, worauf sich das ⌘Z bezog.

      Betrifft die Schritte am **Klebestreifen** — **Neigung ziehen** und
      **Streifen wechseln**; sonst klebt nach einem Rückgängig niemand.
    */
    const taped = chosen.find(id => nodesRef.current.find(n => n.id === id)?.data?.note?.tape)
    if (taped) setTappedId(taped)
    const frei = chosen.filter(id => id !== taped)
    if (frei.length > 0) pendingDuplicatesRef.current = frei
  }, [dataSource])

  /**
   * Die Aufgaben eines Schritts — nur die **Übernahme** legt welche an
   * (ADR 0011). Zurück heißt: weich löschen; vorwärts: zurückholen.
   *
   * Weich, wie überall in dieser App: Ein Wiederherstellen muss sie
   * zurückbringen können, und ein echtes Löschen ließe die Verbindung des
   * Notiz ins Leere zeigen.
   */
  const applyHistoryTasks = useCallback((tasks: { ids: string[]; action: 'delete' | 'restore' }) => {
    if (tasks.ids.length === 0) return
    void (async () => {
      if (tasks.action === 'delete') {
        for (const id of tasks.ids) await dataSource.deleteTask(id)
      } else {
        await dataSource.restoreTasks(tasks.ids)
      }
    })()
  }, [dataSource])

  const handleUndo = useCallback(() => {
    // Nicht mitten in einer Geste: Ein Rückgängig schriebe gegen die laufende
    // Vorschau an. Derselbe Riegel wie bei ⌘D.
    if (gestureRef.current) return
    const move = undoHistory(historyRef.current)
    historyRef.current = move.state
    applyHistory(move.changes, move.select)
    applyHistoryTasks(move.tasks)
  }, [applyHistory, applyHistoryTasks])

  const handleRedo = useCallback(() => {
    if (gestureRef.current) return
    const move = redoHistory(historyRef.current)
    historyRef.current = move.state
    applyHistory(move.changes, move.select)
    applyHistoryTasks(move.tasks)
  }, [applyHistory, applyHistoryTasks])

  useCanvasHistoryShortcut({
    enabled: isActiveTab && tool === 'select',
    onUndo: handleUndo,
    onRedo: handleRedo,
  })


  const spaceHeld = useSpaceKeyHeld(isActiveTab && tool === 'select')

  // Markiert den scharfgestellten Zustand. Trägt seit dem Vorschaubild keine
  // eigene Optik mehr (der Zeiger bleibt, wie er ist) — die Klasse bleibt als
  // Zustandsmerkmal, an dem sich prüfen lässt, ob platziert wird.
  const placingClass = placementMode !== null ? ' canvas-flow--placing' : ''
  // Der **Bereich** wird aufgezogen, nicht getragen — er bekommt deshalb einen
  // eigenen Zeiger (siehe `CanvasFlow.scss`).
  const drawingClass = placementMode === 'area' ? ' canvas-flow--placing-area'
    // Der **Text** bekommt dasselbe Fadenkreuz — auch er wird aufgezogen.
    : placementMode === 'text' ? ' canvas-flow--placing-area canvas-flow--placing-text' : ''
  // Ab zwei Ausgewählten weichen die Eckpunkte und Options-Leisten der
  // EINZELNEN Notizen — sonst stünden bei drei Ausgewählten zwölf Eckpunkte
  // und drei überlappende Leisten da, jede nur für ihre eigene Notiz zuständig.
  // Als Klasse und nicht über `data`: Die Auswahl ändert sich über
  // `onNodesChange`, nicht über die Live-Query, der Node-Abgleich liefe also
  // gar nicht; und ein `useStore` je Notiz zählte bei jedem Store-Update über
  // alle Nodes.
  const multiSelectClass = isMultiSelect ? ' canvas-flow--multi-select' : ''
  const toolClass = tool === 'hand' ? ' canvas-flow--panning' : ''
  const spaceClass = spaceHeld ? ' canvas-flow--space-panning' : ''
  const nodeTypes = useMemo(() => NODE_TYPES, [])
  // Der **Angetippt**-Zustand als EIN Objekt, damit der Context nicht bei
  // jedem Zeichnen der Seite einen neuen Wert bekäme (siehe `useCanvasTapped`).
  const tapeState = useMemo(() => ({ tapped: tappedId, peeling: peelingId }), [tappedId, peelingId])

  return (
    <CanvasTappedProvider value={tapeState}>
    <CanvasElementMenuProvider value={elementMenu}>
    <ReactFlow
      className={`canvas-flow${placingClass}${drawingClass}${toolClass}${spaceClass}${multiSelectClass}`}
      // Eine Quelle für die Dauer: `CanvasFlow.scss` liest sie hier ab, statt
      // sie ein zweites Mal aufzuschreiben.
      style={{ '--canvas-spawn-anim': `${SPAWN_ANIM_MS}ms` } as React.CSSProperties}
      nodes={flowNodes}
      onNodesChange={handleNodesChange}
      /*
        **Die Auswahl hebt nicht an** (CONTEXT.md, „Vorn und hinten"). React
        Flows Vorgabe legte jedes angeklickte Element 1000 höher — „Ganz nach
        hinten" wäre dann erst nach dem Abwählen zu sehen gewesen. Was man
        trägt oder bearbeitet, hebt `flowNodes`; der Auswahlrahmen liegt
        ohnehin in seiner eigenen Schicht (`SelectionOverlay`).
      */
      elevateNodesOnSelect={false}
      onNodeDragStart={handleNodeDragStart}
      /*
        **Unter der Hand bekommt React Flow keine Klick- und Hover-Rückrufe.**
        Es schreibt jedem Node INLINE `pointer-events: all`, sobald er
        auswählbar, ziehbar ist ODER einen dieser Rückrufe hat (`hasPointerEvents`
        in seinem `NodeWrapper`) — und inline schlägt die (0,2,0) von
        `.canvas-flow--panning .react-flow__node { pointer-events: none }` in
        der SCSS. So fing jeder Zettel und jeder Bereich den Zeiger unter der
        Hand, mit dem Pfeil statt der Hand als Cursor; nur der geklebte nicht,
        der trägt sein `none` selbst inline (`TAPED_STYLE`). Ohne die Rückrufe
        schreibt React Flow selbst `none`, und der Zeiger fällt auf die Fläche.
        Verloren geht nichts: Unter der Hand gibt es weder Ring noch Auswahl.
        Gemeldet 2026-09-06.
      */
      onNodeClick={tool === 'select' ? handleNodeClick : undefined}
      onNodeDragStop={handleNodeDragStop}
      onPaneClick={handlePaneClick}
      onNodeMouseEnter={tool === 'select' ? handleNodeMouseEnter : undefined}
      onNodeMouseLeave={tool === 'select' ? handleNodeMouseLeave : undefined}
      // Der Doppelklick gehört uns (siehe `handlePaneDoubleClick`): React Flows
      // eigener verdoppelt nur und kennt keinen Rückweg.
      zoomOnDoubleClick={false}
      onDoubleClick={handlePaneDoubleClick}
      onNodesDelete={handleNodesDelete}
      nodeTypes={nodeTypes}
      nodesDraggable={tool === 'select'}
      // Im Hand-Werkzeug ist an den Objekten nichts mehr zu holen: nicht
      // auswählen, nicht anfassen, und über die Tabulator-Taste auch nicht
      // erreichbar — sonst käme man an das, was die Maus nicht mehr trifft.
      elementsSelectable={tool === 'select'}
      /*
        Die beiden Schwellen MÜSSEN denselben Wert tragen — sonst gehen Klicks
        ins Leere, und zwar nur manchmal.

        Sie beantworten dieselbe Frage, „war das ein Klick oder ein Ziehen?",
        und React Flows Vorgaben widersprechen sich dabei:

        | | Vorgabe | wirkt |
        |---|---|---|
        | `nodeClickDistance` | 0 | d3-drag setzt `mousemoving = d² > 0`; ist es wahr, UNTERDRÜCKT es das `click`-Ereignis (`yesdrag(view, mousemoving)` in `nodrag.js`) |
        | `nodeDragThreshold` | 1 | `XYDrag` ruft `startDrag` erst bei `d > 1` — und DORT wird ausgewählt (`onNodeMouseDown` → `handleNodeClick`) |

        Ausgewählt wird also auf zwei Wegen: über das `click`-Ereignis
        (`onSelectNodeHandler`, greift nur solange `nodeDragThreshold > 0`)
        oder über den Beginn des Ziehens. Bei genau 1px Bewegung greift
        KEINER: Der Klick ist schon unterdrückt, das Ziehen hat noch nicht
        begonnen. Ein Zittern von einem Pixel zwischen Drücken und Loslassen
        schluckt damit die Auswahl — gemeldet als „ungefähr jeder zehnte Klick
        geht ins Leere".

        Mit gleichem Wert schließt sich die Lücke von selbst: Bis
        einschließlich der Schwelle feuert der Klick, darüber greift das
        Ziehen.
      */
      nodeDragThreshold={CLICK_DRAG_THRESHOLD}
      nodeClickDistance={CLICK_DRAG_THRESHOLD}
      nodesFocusable={tool === 'select'}
      /* Das Ins-Bild-Holen gehört uns (siehe `handleTabStep`). React Flows
         eigenes fragt `getNodesInside(..., partially: true)` und hält schon
         ein sichtbares Eckchen für genug — eine Notiz, von dem 15%
         hereinragen, bliebe am Rand kleben. Blieben beide an, führen sie
         einander zudem ins Gehege: Unseres läuft über 200ms, React Flows
         springt hart. */
      autoPanOnNodeFocus={false}
      // Maus-Werkzeug: Linksziehen (Taste 0) gehört dem Auswahlrahmen, nur
      // Mittel- und Rechtsziehen schieben. Zwei Dinge folgen daraus von
      // selbst: React Flows `panActivationKeyCode` („Space") greift wieder,
      // und der Greif-Cursor bleibt aus, weil die `draggable`-Klasse an
      // `panOnDrag.includes(0)` hängt.
      panOnDrag={tool === 'hand' ? true : [1, 2]}
      // ⇧-Klick UND ⌘/Strg-Klick sammeln ein, wie in FigJam und Miro.
      // `useKeyPress` liest ein Array als „eine dieser Tasten"
      // (@xyflow/react, index.js:386); React Flows Vorgabe kennt nur ⌘/Strg.
      multiSelectionKeyCode={['Meta', 'Control', 'Shift']}
      // ⇧ darf nicht zusätzlich den Auswahlrahmen scharfstellen: `ZoomPane`
      // bekommt `panOnDrag: !selectionKeyPressed && panOnDrag`
      // (index.js:2114) — mit gehaltenem ⇧ wäre das Schieben im
      // HAND-Werkzeug aus. Der Rahmen kommt im Maus-Werkzeug ohnehin von
      // `selectionOnDrag`.
      selectionKeyCode={null}
      // Zwei-Finger-Wischen auf dem Trackpad verschiebt den Canvas, statt zu
      // zoomen — die Hausform von Figma und Miro. React Flow reicht ein
      // Rad-Ereignis mit `ctrlKey` weiterhin an den Zoom durch, und genau das
      // schickt macOS beim Aufziehen mit zwei Fingern. Pinch-to-Zoom und
      // ⌘/Strg + Wischen zoomen also unverändert, ohne dass hier jemand
      // Trackpad von Maus unterscheiden müsste. Preis: ein echtes Mausrad
      // scrollt jetzt, statt zu zoomen.
      // Bewusst KEIN `snapToGrid`: React Flow rundet damit die Position schon
      // in `updateNodes` (@xyflow/system), also bevor `onNodesChange` sie zu
      // sehen bekommt — die Ausrichtung an anderen Notizen könnte das Raster
      // dann nicht schlagen. Beide Regeln liegen in `handleNodesChange`.
      panOnScroll
      // React Flows Vorgabe ist 0.5, halbiert die Wischbewegung also. Das
      // fühlt sich zäh an; 1 bewegt den Canvas genau so weit, wie der Finger
      // wandert — wie in Figma und Miro.
      panOnScrollSpeed={1}
      zoomOnScroll={false}
      // Beim scharfen Bereichs-Werkzeug zieht der Zug den Bereich auf, nicht das Auswahlrechteck.
      selectionOnDrag={tool === 'select' && placementMode !== 'area' && placementMode !== 'text'}
      // Berühren genügt: Eine Notiz wird ausgewählt, sobald der Rahmen ihn
      // ANSCHNEIDET — React Flows Vorgabe (`Full`) verlangt, ihn vollständig
      // zu umschließen. Bei 168er-Notizen heißt das, weit auszuholen, nur um
      // einen Rand mitzunehmen.
      //
      // Intern der Unterschied zwischen `overlappingArea > 0` und
      // `overlappingArea >= area` (`getNodesInside` in @xyflow/system).
      selectionMode={SelectionMode.Partial}
      /*
        Entf/Backspace hört `useCanvasDeleteShortcut` ab, nicht React Flow.

        Dessen `deleteKeyCode` horcht am DOKUMENT und fragt allein, ob der
        Tastendruck aus einem Eingabefeld kommt — nicht, wo der Fokus liegt.
        Wer eine Notiz auswählte, dann in der **Aufgabenliste** eine Aufgabe
        anklickte und Entf drückte, löschte beides: Die Liste behandelte den
        Tastendruck als ihren, React Flow gleichzeitig als seinen.

        Gelöscht wird trotzdem über `deleteElements`, `onNodesDelete` bleibt
        also der einzige Weg, auf dem eine Notiz verschwindet.
      */
      deleteKeyCode={null}
      // Die Grenzen stehen in `utils/canvasViewport`: der gemerkte Ausschnitt
      // muss gegen dieselben Werte geklemmt werden, und zwei Fassungen davon
      // liefen früher oder später auseinander.
      minZoom={CANVAS_MIN_ZOOM}
      maxZoom={CANVAS_MAX_ZOOM}
      // Dort weitermachen, wo dieses Projekt verlassen wurde. React Flow liest
      // `defaultViewport` nur beim Aufbau — deshalb trägt das Panel den
      // Projektschlüssel, sonst zeigte ein Projektwechsel im selben Tab weiter
      // den alten Ausschnitt (siehe `ProjectPage`).
      defaultViewport={initialViewport}
      onMoveEnd={rememberViewport}
      // Blendet das React-Flow-Badge unten rechts aus. `@xyflow/react` steht
      // unter MIT (siehe `node_modules/@xyflow/react/LICENSE`) — die Lizenz
      // verlangt den Copyright-Hinweis in Kopien der Software, nicht ein
      // Badge in der Oberfläche. Der Schalter heißt aus historischen Gründen
      // `proOptions`, setzt aber kein Abo voraus; React Flow bittet lediglich
      // darum, das Projekt per Pro-Abo zu unterstützen.
      proOptions={{ hideAttribution: true }}
    >
      {/*
        Das Punktraster — ein `radial-gradient`, kein React Flow `<Background>`.
        Jenes rendert per `useStore(transform)` in JEDEM Pan-Frame ein
        SVG-Pattern neu; hier setzt die Store-Anmeldung oben drei Stile,
        und React sieht davon nichts. Schicht −1 wie zuvor (canvasLayers.scss).
      */}
      <div ref={gridRef} className="canvas-flow__grid" aria-hidden="true" />
      {/*
        `kind` fällt auf `'note'` zurück, wenn `placementMode` `null` ist —
        harmlos, weil `CanvasPlacementPreview` dann ohnehin `null` rendert
        (`previewPoint` ist nur gesetzt, während ein Werkzeug scharf ist).
      */}
      <CanvasPlacementPreview
        /*
          **Der Bereich zeigt sein Bild nur, wenn er GEZOGEN wird.**

          Er kann zweierlei, anders als Notiz und Memo: Ein Zug vom
          Werkzeug-Knopf setzt ihn in Standardgröße ab — dort gehört das Bild
          hin, es zeigt, was landet. Ein Klick auf den Knopf stellt dagegen das
          **Aufziehen** scharf (Fadenkreuz, `useAreaDrawing`), und dort bestimmt
          die Hand das Maß: Ein Bild in Standardgröße wäre da eine Lüge.

          Bis 2026-09-06 hatte er auf keinem der beiden Wege eines.
        */
        point={(placementMode === 'area' && !placementDrag) || textSquare ? null : previewPoint}
        zoom={getZoom() || 1}
        kind={placementMode ?? 'note'}
        fontSize={noteDefaults.text.fontSize}
        fontKey={noteDefaults.text.font}
        /*
          Dieselbe Quelle wie beim Absetzen: Das Bild zeigt die Farbe, in der
          der Zettel wirklich landet — nicht die Vorgabe.

          Der **Bereich** steht nicht in `noteDefaults` und braucht auch nichts
          von dort: Er merkt sich keine Farbe (v1), sein Ton kommt aus dem
          Design. Die Vorschau holt ihn sich selbst (`getAreaColor`).
        */
        colorKey={placementMode === null ? null : noteDefaults[placementMode].color}
      />
      <SelectionOverlay
        entries={selectedRects}
        frozenBox={swapBox}
        onResizeStart={startResize}
        onMoveStart={startMove}
        onSwapStart={startSwap}
        swapDraggedId={swapDraggedId}
        swapZones={swapZones}
        swapHit={swapHit}
        swapLanding={swapLanding}
        swapSeam={swapSeam}
        onSwapPointDoubleClick={handleSwapPointDoubleClick}
        onSwapPointEnter={markRingHover}
        onSwapPointLeave={handleSwapPointLeave}
        editingId={editingNoteId}
        swapPoints={swapPointEntries}
        notes={selectedNotes}
        onBulkColor={handleBulkColor}
        onBulkFont={handleBulkFont}
        onBulkToggleTitle={handleBulkToggleTitle}
        onBulkToggleAuthor={handleBulkToggleAuthor}
        onBulkDelete={handleBulkDelete}
        onBulkApply={onApplyApplicable}
        onBulkTape={handleBulkTape}
        onBulkFontSize={handleBulkFontSize}
        onBulkColorReset={handleBulkColorReset}
        onBulkColorPreview={handleBulkColorPreview}
        onBulkFontPreview={handleBulkFontPreview}
      />
      <HelperLines vertical={guides.vertical} horizontal={guides.horizontal} />
      <AdoptionMarks area={adoption.area} notes={adoption.notes} />
      {drawing && (
        <ViewportPortal>
          <div
            className="canvas-area-draw"
            data-testid="canvas-area-draw"
            style={{
              left: drawing.x, top: drawing.y, width: drawing.width, height: drawing.height,
              // Dieselbe gemerkte Farbe, die der aufgezogene Bereich gleich
              // trägt — sonst zeigte das Rechteck etwas anderes als sein
              // Ergebnis.
              '--area-surface': getAreaColor(noteDefaults.area.color).surface,
              '--area-border': getAreaColor(noteDefaults.area.color).border,
            } as React.CSSProperties}
          />
        </ViewportPortal>
      )}
      {textSquare && (
        <ViewportPortal>
          {/*
            Das Quadrat beim **Aufziehen** eines Texts — mit dem Platzhalter in
            der Größe, die es gerade festlegt: Man sieht beim Ziehen, wie groß
            der Text wird.
          */}
          <div
            className="canvas-text-draw"
            data-testid="canvas-text-draw"
            style={{
              left: textSquare.x,
              top: textSquare.y,
              width: textSquare.side,
              height: textSquare.side,
              '--text-size': `${canvasTextSizeFromSide(textSquare.side)}px`,
            } as React.CSSProperties}
          >
            <span className="canvas-text-draw__placeholder">{t('projectCanvas.text.placeholder')}</span>
          </div>
        </ViewportPortal>
      )}
      <MeasureLines measures={measures} />
      {spawnPreview && (
        <ViewportPortal>
          <CanvasSpawnPreview rect={spawnPreview.rect} kind={spawnPreview.kind} colorKey={spawnPreview.color} committed={spawnPreview.committed} />
        </ViewportPortal>
      )}
      {/*
        Die schwebende Bedienung (siehe `children` am Prop-Typ). Zuletzt, weil
        die DOM-Reihenfolge bei gleichem `z-index` entscheidet — hier steht
        ohnehin nichts auf derselben Stufe, aber die Lage soll nicht von einer
        späteren Umsortierung abhängen.
      */}
      {children}
    </ReactFlow>
    </CanvasElementMenuProvider>
    </CanvasTappedProvider>
  )
}
